import React, { useState, useRef, useEffect } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import ProductEntryForm, { ProductEntryFormHandle } from './ProductEntryForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from './ui/use-toast';
import { useIsMobile } from '../hooks/use-mobile';
import SalesInvoiceMobile from './SalesInvoiceMobile';
import { PrintOptionsModal } from './PrintOptionsModal';
import { useSalesInvoice } from '../context/SalesInvoiceContext';
import { createInvoiceApiCall, formatDateInIST, getPayBeforeDateFromInvoiceDaysSetting, sanitizeGstInput, GST_VALIDATION_MESSAGE } from '../lib/utils';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import SerialSelectionPopup from './SerialSelectionPopup';

import { drawInvoicePdf } from '../lib/printKohineer';
import { cn } from "@/lib/utils";
import { downloadPdf as downloadPdfUtil } from '@/lib/pdfDownload';
import { sharePdf as sharePdfUtil } from '@/lib/pdfDownload';
import { Capacitor } from '@capacitor/core';
import RepresentativeManager from './RepresentativeManager';
import { useBackNavigation } from '../hooks/use-back-navigation';
import { useSettings } from '../hooks/useSettings';
import { getBaseUrl, authenticatedFetch, fetchPDF } from '@/lib/api';
import { useUserPrivileges } from '@/context/UserPrivilegesContext';
import { PRIVILEGE } from '@/lib/privilegeConstants';


// TypeScript declaration for window.qz
// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare global {
  interface Window {
    qz: any;
  }
}

const fetchStates = async () => {
  const baseUrl = getBaseUrl();
  const token = localStorage.getItem('token') || '';
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  const response = await fetch(`${baseUrl}/api/States`, {
    headers: {
      'Authorization': authToken,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch states');
  return response.json();
};

const fetchLocations = async () => {
  const baseUrl = getBaseUrl();
  const token = localStorage.getItem('token') || '';
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  const response = await fetch(`${baseUrl}/api/Locations`, {
    headers: {
      'Authorization': authToken,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch locations');
  return response.json();
};

const fetchProductDetails = async (searchTerm: string) => {
  const baseUrl = getBaseUrl();
  const token = localStorage.getItem('token') || '';
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  // Replace with the actual endpoint if different
  const response = await fetch(`${baseUrl}/api/ProductDetails?searchstr=${encodeURIComponent(searchTerm)}`, {
    headers: {
      'Authorization': authToken,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch product details');
  return response.json();
};

const fetchCustomerByInputValue = async (inputValue: string) => {
  if (!inputValue) return null;
  const baseUrl = getBaseUrl();
  const token = localStorage.getItem('token') || '';
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  const response = await fetch(`${baseUrl}/api/CustomerPhoneContains?Phone=${encodeURIComponent(inputValue)}`, {
    headers: {
      'Authorization': authToken,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch customer');
  return response.json();
};

const fetchUnknownDetails = async () => {
  const baseUrl = getBaseUrl();
  const token = localStorage.getItem('token') || '';
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  const response = await fetch(`${baseUrl}/api/UnkonwDetails?InputValue=UNKNOWN`, {
    headers: {
      'Authorization': authToken,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch unknown customer details');
  return response.json();
};

const fetchCustomerPoints = async (customerCode: number) => {
  try {
    const baseUrl = getBaseUrl();
    const token = localStorage.getItem('token') || '';
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    const response = await fetch(`${baseUrl}/api/GetCustPoints?customerCode=${customerCode}`, {
      headers: {
        'Authorization': authToken,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch customer points');
    const data = await response.json();
    if (data.status === 0 && Array.isArray(data.data) && data.data.length > 0) {
      const pointsData = data.data[0];
      console.log('Customer points data from API:', pointsData);
      return pointsData.BalancePoints || 0;
    }
    return 0;
  } catch (err) {
    console.error('Error fetching customer points:', err);
    return 0;
  }
};

const fetchPointsConversionSetting = async (): Promise<number> => {
  try {
    const baseUrl = getBaseUrl();
    const token = localStorage.getItem('token') || '';
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    const response = await fetch(`${baseUrl}/api/Settings`, {
      headers: {
        'Authorization': authToken,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });
    if (!response.ok) return 100;
    const json = await response.json();
    const list: { Name?: string; Key?: string; Value?: string }[] = json?.data ?? [];
    const item = list.find((s) => (s.Name || s.Key) === 'Points Convertion');
    const raw = String(item?.Value ?? '').trim();
    const n = Number(raw);
    // "Points Convertion" = points per ₹1 (e.g. 100 points = ₹1)
    return Number.isFinite(n) && n > 0 ? n : 100;
  } catch {
    return 100;
  }
};

const fetchCompanies = async () => {
  const baseUrl = getBaseUrl();
  const token = localStorage.getItem('token') || '';
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  const response = await fetch(`${baseUrl}/api/Companies`, {
    headers: {
      'Authorization': authToken,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch companies');
  return response.json();
};

const fetchPartners = async () => {
  const baseUrl = getBaseUrl();
  const token = localStorage.getItem('token') || '';
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  const response = await fetch(`${baseUrl}/api/Partners`, {
    headers: {
      'Authorization': authToken,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch partners');
  return response.json();
};

const fetchRepresentatives = async () => {
  const baseUrl = getBaseUrl();
  const token = localStorage.getItem('token') || '';
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  const response = await fetch(`${baseUrl}/api/Representative`, {
    headers: {
      'Authorization': authToken,
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  });
  if (!response.ok) throw new Error('Failed to fetch representatives');
  const data = await response.json();
  const reps = (data?.data || data?.Data || []).map((item: any) => ({
    ...item,
    // Normalize to existing UI expectation
    Representative: item.RepName || item.Representative || '',
  }));
  return { ...data, data: reps } as any;
};

// Function to validate coupon
const validateCoupon = async (couponNo: string) => {
  try {
    const baseUrl = getBaseUrl();
    const token = localStorage.getItem('token') || '';
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    const response = await fetch(`${baseUrl}/api/ValidateCoupon?CouponNo=${encodeURIComponent(couponNo)}`, {
      headers: {
        'Authorization': authToken,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to validate coupon');
    return response.json();
  } catch (error) {
    console.error('Error validating coupon:', error);
    throw error;
  }
};

// Function to validate credit note
const validateCreditNote = async (invSeqNo: string, invDate: string, companyId: string) => {
  try {
    const baseUrl = getBaseUrl();
    const token = localStorage.getItem('token') || '';
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    const response = await fetch(`${baseUrl}/api/ValidateCreditNote?InvSeqNO=${encodeURIComponent(invSeqNo)}&InvDate=${encodeURIComponent(invDate)}&CompanyID=${encodeURIComponent(companyId)}`, {
      headers: {
        'Authorization': authToken,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to validate credit note');
    return response.json();
  } catch (error) {
    console.error('Error validating credit note:', error);
    throw error;
  }
};



// Robust function to convert numbers to words (handles decimals and large numbers)
function numberToWords(num) {
  if (num === 0) return 'zero';

  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const scales = ['', 'thousand', 'lakh', 'crore'];

  function convertLessThanOneThousand(n) {
    if (n === 0) return '';

    if (n < 10) return ones[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) {
      return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
    }
    if (n < 1000) {
      return ones[Math.floor(n / 100)] + ' hundred' + (n % 100 !== 0 ? ' and ' + convertLessThanOneThousand(n % 100) : '');
    }
  }

  function convertIndianNumber(n) {
    if (n === 0) return 'zero';

    const numStr = n.toString();
    const parts = [];

    // Handle crores (last 7 digits)
    if (numStr.length > 7) {
      const crorePart = parseInt(numStr.slice(0, -7));
      if (crorePart > 0) {
        parts.push(convertLessThanOneThousand(crorePart) + ' crore');
      }
    }

    // Handle lakhs (next 5 digits)
    if (numStr.length > 5) {
      const lakhPart = parseInt(numStr.slice(-7, -2));
      if (lakhPart > 0) {
        parts.push(convertLessThanOneThousand(lakhPart) + ' lakh');
      }
    }

    // Handle thousands and remaining
    const remainingPart = parseInt(numStr.slice(-5));
    if (remainingPart > 0) {
      if (remainingPart < 1000) {
        parts.push(convertLessThanOneThousand(remainingPart));
      } else {
        const thousandPart = Math.floor(remainingPart / 1000);
        const restPart = remainingPart % 1000;
        parts.push(convertLessThanOneThousand(thousandPart) + ' thousand');
        if (restPart > 0) {
          parts.push(convertLessThanOneThousand(restPart));
        }
      }
    }

    return parts.join(' ');
  }

  // Handle decimal numbers
  const numStr = num.toString();
  if (numStr.includes('.')) {
    const [wholePart, decimalPart] = numStr.split('.');
    const wholeWords = convertIndianNumber(parseInt(wholePart));
    const decimalWords = convertIndianNumber(parseInt(decimalPart));
    return wholeWords + ' rupees and ' + decimalWords + ' paise only';
  }

  return convertIndianNumber(Math.floor(num)) + ' rupees only';
}



const SalesInvoice = () => {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const LOCATION_ID = '10035'; // You may want to make this dynamic later
  const [showRepManager, setShowRepManager] = useState(false);

  // Use shared context for invoice state
  const {
    customer, setCustomer,
    products, setProducts,
    payments, setPayments,
    notes, setNotes,
    repName, setRepName,
    invoiceForm, setInvoiceForm,
    shipToForm, setShipToForm,
    shipToSame, setShipToSame,
    isUnknown, setIsUnknown,
    isEditMode, editingInvoiceId, onBackToReports, onUpdateSuccess,
    resetForNewInvoice,
    clearCustomerDetails,
  } = useSalesInvoice();
  const { hasPrivilege } = useUserPrivileges();
  const canEditPrice = hasPrivilege(PRIVILEGE.INVOICE_PRICE_CHANGE);
  const canEditDiscount = hasPrivilege(PRIVILEGE.EDIT_DISCOUNT);

  // Get settings for dynamic field visibility and labels
  const { salesInvoiceSettings, getSetting, primaryCompanyId, settings: settingsList } = useSettings();
  // 3MM print/preview: show serial using "Serial Print" template; must contain exact tag <SerialNo> (replaced with actual serial)
  const serialPrintTemplate = (getSetting('Serial Print') || '').trim();
  const showSerialInPrint = serialPrintTemplate.includes('<SerialNo>');

  // Resolve default payment method from "Invoice Default Payment Type" setting (Wallet -> Wallets for UI)
  const getDefaultPaymentMethod = () => {
    const raw = (getSetting('Invoice Default Payment Type') || 'Cash').trim();
    const mapped = raw === 'Wallet' ? 'Wallets' : raw;
    const allowed = ['Cash', 'Card', 'Wallets', 'Cheque', 'Coupon', 'Credit Note', 'Points'];
    return allowed.includes(mapped) ? mapped : 'Cash';
  };

  // Round payment amounts to 2 decimal places (currency)
  const roundPayment = (val: unknown): number => {
    const n = parseFloat(String(val ?? ''));
    return Number.isNaN(n) ? 0 : Math.round(n * 100) / 100;
  };

  // Fetch api/settings when Sales Invoice page is opened and update React Query cache; set Unknown checkbox from "Unknown User Ticketed"
  // In edit mode, do NOT overwrite isUnknown - it comes from API (FName/LName) via populateSalesInvoiceContext
  useEffect(() => {
    type SettingsRes = {
      Header: unknown[];
      EditableColumns: unknown[];
      ValueChangeColumns: unknown[];
      TwoFields: unknown[];
      status: number;
      message: string;
      data: { id: number; Name: string; Value: string }[];
      TotalRecords: number;
    };
    authenticatedFetch<SettingsRes>('/api/settings')
      .then((res) => {
        if (res?.data) {
          queryClient.setQueryData(['settings'], res.data);
          if (!isEditMode) {
            const list = res.data.data ?? [];
            const unknownSetting = list.find((s) => s.Name === 'Unknown User Ticketed');
            const unknownChecked = unknownSetting?.Value === '1';
            setIsUnknown(!!unknownChecked);
          }
        }
      })
      .catch((err) => {
        console.warn('Failed to fetch settings on Sales Invoice open:', err);
      });
  }, [queryClient, setIsUnknown, isEditMode]);

  // Serial field: only show when "Serial Text" setting has a value; use that value as the label
  const serialText = (getSetting('Serial Text') || '').trim();
  const showSerialField = serialText.length > 0;

  // Store raw input values for discount fields to allow natural typing
  const [rawInputValues, setRawInputValues] = useState<{ [key: string]: string }>({});

  // Custom back navigation handler for SalesInvoice
  const handleSalesInvoiceBack = () => {
    if (isEditMode && onBackToReports) {
      onBackToReports();
    } else {
      // Navigate back to dashboard/transactions
      window.dispatchEvent(new CustomEvent('setCurrentPage', { detail: 'transactions' }));
    }
  };

  // Use back navigation hook with custom handler
  useBackNavigation({
    onBack: handleSalesInvoiceBack,
    canGoBack: true,
    showExitConfirmation: false // Don't show exit confirmation on this page
  });

  // Function to fetch and log invoice details for debugging
  const fetchAndLogInvoiceDetails = async (invoiceId: string, shouldPopulateData = false) => {
    try {
      const baseUrl = getBaseUrl();
      const token = localStorage.getItem('token') || '';
      const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

      console.log('=== FETCHING INVOICE DETAILS FOR EDIT ===');
      console.log('Invoice ID:', invoiceId);
      console.log('API URL:', `${baseUrl}/api/InvoiceFullDetails?InvoiceID=${invoiceId}`);

      const response = await fetch(`${baseUrl}/api/InvoiceFullDetails?InvoiceID=${invoiceId}`, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        console.error('Failed to fetch invoice details:', response.status, response.statusText);
        return;
      }

      const result = await response.json();
      console.log('=== INVOICE DETAILS API RESPONSE ===');
      console.log('Full API Response:', JSON.stringify(result, null, 2));

      if (result.status === 0 && result.data && result.data.length > 0) {
        const firstRecord = result.data[0];

        // Populate ALL data if requested (which is true in edit mode)
        if (shouldPopulateData) {
          console.log('=== POPULATING ALL INVOICE DATA FROM API ===');

          // Detect Unknown customer from API (FName/LName)
          const isUnknownFromApi =
            (firstRecord.FName && String(firstRecord.FName).toUpperCase() === 'UNKNOWN') ||
            (firstRecord.LName && String(firstRecord.LName).toUpperCase() === 'UNKNOWN');
          setIsUnknown(!!isUnknownFromApi);

          // 1. Map Customer Data (API: FName, LName, TIN, ReferredDr, Address) - always use CustState, never override with company state
          // Customer Email: from api/InvoiceFullDetails "Customer Email" or "Email"
          const customerEmail = firstRecord['Customer Email'] ?? firstRecord.Email ?? result['Customer Email'] ?? '';

          // Location can come in multiple shapes depending on environment/API version:
          // - LocationID / LocationId: numeric id
          // - Location: string name (e.g. "Hyderabad") OR sometimes numeric id
          const apiLocationId =
            firstRecord.LocationID ??
            firstRecord.LocationId ??
            (typeof firstRecord.Location === 'number' ? firstRecord.Location : undefined);

          const apiLocationName =
            typeof firstRecord.Location === 'string' ? String(firstRecord.Location).trim() : '';

          const resolveLocationId = (prevLocationId: string): string => {
            if (apiLocationId != null && String(apiLocationId).trim() !== '') return String(apiLocationId);
            if (apiLocationName) {
              const match = locations?.find((l: any) =>
                String(l?.Name ?? '').trim().toLowerCase() === apiLocationName.toLowerCase()
              );
              if (match?.ID != null) return String(match.ID);
            }
            // Preserve previous location if we couldn't resolve, rather than wiping to ''
            return prevLocationId || '';
          };

          const customerData = {
            Phone: firstRecord.Mobile1 || '',
            Name: firstRecord.FName || '',
            Company: firstRecord.LName || '',
            GST: firstRecord.TIN || '',
            Locality: firstRecord.Locality || '',
            State: firstRecord.CustState || '',
            Email: (customerEmail && String(customerEmail).trim()) ? String(customerEmail).trim() : '',
            Address: firstRecord.Address || '',
            ReferredBy: firstRecord.ReferredDr || '',
            SelectCompany: firstRecord.CompanyID?.toString() || '',
            // In edit mode, resolve Location from id or name; preserve previous if not resolvable.
            // This supports environments where InvoiceFullDetails returns Location as a name (HTTP) vs id (HTTPS).
            Location: '', // filled via functional setCustomer below
            Partner: firstRecord.eCommPartner || 'SALES', // Default to SALES if not present
            InvoiceDate: firstRecord.CreatedOn ? new Date(firstRecord.CreatedOn).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            customerCode: firstRecord.CustomerCode?.toString() || '',
          };
          setCustomer((prev) => ({ ...customerData, Location: resolveLocationId(String(prev?.Location ?? '')) }));
          console.log('Customer data populated:', customerData);

          // 2. Map Products (reverse order so edit screen matches post order: first posted = first shown)
          // isIGST: 0 = intrastate (GST = CGST+SGST), 1 = interstate (GST = IGST only)
          const isIGST = firstRecord.isIGST === 1 || firstRecord.isIGST === true;
          const lineItemsInPostOrder = [...result.data].reverse();
          const mappedProducts = lineItemsInPostOrder.map((item: any, index: number) => {
            // Calculate GST percent - use CGST+SGST for intrastate, IGST only for interstate
            const sgstPer = parseFloat(item.sgstper) || 0;
            const cgstPer = parseFloat(item.cgstper) || 0;
            const igstPer = parseFloat(item.igstper) || 0;
            const totalTaxPer = isIGST ? igstPer : (cgstPer + sgstPer);

            // Extract numeric values safely
            const qty = parseFloat(item.Quantity || item.Qty || 1);
            // Use costprice from API (InvoiceFullDetails) - correct value; UnitCost may be taxable/unit price
            const unitCost = parseFloat(item.costprice ?? item.CostPrice ?? item.UnitCost ?? item.OriginalCost ?? 0);
            // Prefer line-level discount (ldiscount); GET response repeats header Discount on every row
            const discountAmount = parseFloat(
              (item.ldiscount !== undefined && item.ldiscount !== null) ? item.ldiscount : (item.Discount ?? 0)
            );
            const totalAmount = parseFloat(item.Total || item.TotalCost || 0);

            // OfferPrice: only from JSON (offerprice / OfferPrice). No default/derived value when not present.
            const rawOfferPrice = ('offerprice' in item) ? item.offerprice : (('OfferPrice' in item) ? item.OfferPrice : undefined);
            const parsed = (rawOfferPrice !== undefined && rawOfferPrice !== null && rawOfferPrice !== '') ? parseFloat(rawOfferPrice) : NaN;
            const offerPrice = Number.isNaN(parsed) ? undefined : Math.round(parsed);

            // MRP: only from api/InvoiceFullDetails JSON — no formula, no fallback
            const mrpFromApi = (item.MRP !== undefined && item.MRP !== null && item.MRP !== '')
              ? parseFloat(item.MRP)
              : 0;
            const rateAsMrp = Number.isNaN(mrpFromApi) ? 0 : mrpFromApi;

            // Discount %: use MRP from API so edit screen matches what was posted
            const discountPer = (rateAsMrp > 0 && qty > 0)
              ? ((discountAmount / (rateAsMrp * qty)) * 100)
              : (unitCost > 0 && qty > 0)
                ? ((discountAmount / (unitCost * qty)) * 100)
                : 0;

            // GST/Cess amounts from API (for payload / edit table)
            const sgstAmt = parseFloat(item.SGST ?? 0);
            const cgstAmt = parseFloat(item.CGST ?? 0);
            const igstAmt = parseFloat(item.IGST ?? 0);
            const cessAmt = parseFloat(item.Cess ?? 0);
            const gstAmountFromApi = isIGST ? igstAmt : (sgstAmt + cgstAmt);
            const cessAmountFromApi = cessAmt;
            // Same split as POST: SGST and CGST equal (floor half) when intrastate
            let sgstVal = 0, cgstVal = 0;
            if (!isIGST && gstAmountFromApi > 0) {
              const half = Math.floor((gstAmountFromApi / 2) * 100) / 100;
              sgstVal = half;
              cgstVal = half;
            } else if (isIGST) {
              sgstVal = 0;
              cgstVal = 0;
            }

            // Determine if product has serial number
            const hasSerial = Boolean(item.SerialNumber && item.SerialNumber.trim() !== '');

            return {
              id: item.ProductCode?.toString() || item.id?.toString() || index.toString(),
              ProdCode: item.ProductCode || 0,

              Title: item.Title || item['Product Name'] || item.ProductName || '',
              Barcode: item.BarCode || item.Barcode || '',
              Qty: qty,

              // MRP from api/InvoiceFullDetails JSON only (no formula)
              MRP: rateAsMrp,

              // User Request: UnitCost as Cost Price
              CostPrice: unitCost,

              OfferPrice: offerPrice,

              Discount: discountAmount,
              DiscountPer: discountPer.toFixed(2),

              GST: totalTaxPer,
              // CessPer is percentage; GET has cesspercent (e.g. 4.5) and Cess (amount) - use percent only
              CessPer: (item.cesspercent !== undefined && item.cesspercent !== null) ? item.cesspercent : (item.CessPer ?? 0),

              // Same as POST: GSTAmount and CessAmount for payload; SGST/CGST same value when intrastate
              GSTAmount: gstAmountFromApi.toFixed(2),
              CessAmount: cessAmountFromApi.toFixed(2),
              Cess: cessAmountFromApi,
              SGST: sgstVal,
              CGST: cgstVal,
              sgst: sgstVal,
              cgst: cgstVal,
              igst: isIGST ? igstAmt : 0,

              // User Request: Use API Total directly
              Total: totalAmount,

              hasSerial: hasSerial,
              SerialNumber: item.SerialNumber || '',
              SerialDetails: hasSerial ? [{ SerialNumber: item.SerialNumber, SNo: item.SerialNumber }] : [],

              HSN: item.HSNCode || '',
              Available: item.available || item.Available || 0,
              imgstr: item.imgstr || '',

              // Map to OriginalCost too for compatibility
              OriginalCost: unitCost,

              deliveredQty: item.deliveredQty ?? item['Delivered Qty'] ?? 0,
              DelQty: item.deliveredQty ?? item['Delivered Qty'] ?? qty,
              TaxableValue: item.TaxableValue || 0,
            };
          });
          setProducts(mappedProducts);
          console.log('Products populated:', mappedProducts.length);

          // 3. Map Invoice Form Data (Quote No → Tag1, KM → Tag2, Vehicle → Tag3; labels are dynamic from settings)
          const invoiceDateFromApi = firstRecord.CreatedOn ? new Date(firstRecord.CreatedOn).toISOString().split('T')[0] : '';
          setInvoiceForm(prev => ({
            ...prev,
            QuoteNo: firstRecord.Tag1 || '',
            Mode: firstRecord.Tag2 || '',
            VehicleNo: firstRecord.Tag3 || '',
            Delivered: true, // Making assumption based on deliveredQty presence
            AddInFIFO: prev.AddInFIFO, // Keep existing setting
            InvoiceDate: invoiceDateFromApi,
          }));
          // Invoice Date input is bound to form.InvoiceDate — set it from API CreatedOn so edit screen shows the date
          setForm(prev => ({ ...prev, InvoiceDate: invoiceDateFromApi }));

          setRepName(firstRecord.RepName || '');
          setNotes(firstRecord.Notes || '');

          // 4. Map Shipping Data
          // Always populate if data exists
          if (firstRecord.ShippingName || firstRecord.ShippingAddress) {
            setShipToForm({
              Name: firstRecord.ShippingName || '',
              Company: firstRecord.ShippingCompany || '',
              Phone: firstRecord.ShippingPhone || '',
              Email: firstRecord.ShippingEmail || '',
              GST: firstRecord.ShippingGST || '',
              Address: firstRecord.ShippingAddress || '',
              State: firstRecord.shippingstate || '',
            });
            // If shipping address exists and is different from customer address, uncheck "Ship to Same"
            // But for now, we'll leave shipToSame logic as is or maybe set to false if shipping data exists?
            // Let's rely on the user to toggle if they want to change it. 
            // Logic in useEffect will effectively keep it "same" if strictly equal, but here we just populate form.
          }

          // 5. Map Payments (Edit mode) using ONLY the Payments JSON array
          // Ignore header fields like PaidbyCash, PaidbyOthers, PaidbyPoints, walletamount, Couponamount, Chequeamount, etc.
          let cashAmount = 0;
          let cardAmount = 0;
          let pointsAmount = 0;
          let upiOrBankAmount = 0;
          let walletAmount = 0;
          let chequeAmount = 0;
          let couponAmount = 0;
          let creditNoteAmount = 0;
          let otherAmount = 0;
          let couponNoFromPayments = '';

          if (firstRecord.Payments) {
            try {
              const parsed = typeof firstRecord.Payments === 'string'
                ? JSON.parse(firstRecord.Payments)
                : firstRecord.Payments;

              if (Array.isArray(parsed)) {
                for (const p of parsed) {
                  const mode = String(p.PaymentMode || '').trim();
                  const amt = parseFloat(p.amount) || 0;
                  if (amt <= 0) continue;

                  switch (mode) {
                    case 'Cash':
                      cashAmount += amt;
                      break;
                    case 'Card':
                      cardAmount += amt;
                      break;
                    case 'Points':
                      pointsAmount += amt;
                      break;
                    case 'UPI':
                    case 'Bank':
                      upiOrBankAmount += amt;
                      break;
                    case 'Wallets':
                      walletAmount += amt;
                      break;
                    case 'Cheque':
                      chequeAmount += amt;
                      break;
                    case 'Coupon':
                      couponAmount += amt;
                      if (!couponNoFromPayments && p.Reference) {
                        couponNoFromPayments = String(p.Reference).trim();
                      }
                      break;
                    case 'Credit Note':
                      creditNoteAmount += amt;
                      break;
                    case 'Others':
                      otherAmount += amt;
                      break;
                    default:
                      // Unknown / unsupported mode: treat as "Others"
                      otherAmount += amt;
                      break;
                  }
                }
              }
            } catch (e) {
              console.error('Failed to parse Payments JSON from InvoiceFullDetails:', e);
            }
          }

          // Helper to safely convert to string (rounded to 2 decimals for payment amounts)
          const safeStr = (val: any) =>
            (val !== undefined && val !== null && !isNaN(parseFloat(val)))
              ? roundPayment(val).toFixed(2)
              : '';

          // Determine primary payment method from Payments array (first with amount > 0 in this priority order)
          const paymentPriority: { mode: string; label: string; amount: number }[] = [
            { mode: 'Cash', label: 'Cash', amount: cashAmount },
            { mode: 'Card', label: 'Card', amount: cardAmount },
            { mode: 'Points', label: 'Points', amount: pointsAmount },
            { mode: 'UPI', label: 'UPI', amount: upiOrBankAmount },
            { mode: 'Wallets', label: 'Wallets', amount: walletAmount },
            { mode: 'Cheque', label: 'Cheque', amount: chequeAmount },
            { mode: 'Coupon', label: 'Coupon', amount: couponAmount },
            { mode: 'Credit Note', label: 'Credit Note', amount: creditNoteAmount },
            { mode: 'Others', label: 'Others', amount: otherAmount },
          ];

          const usedFromArray = paymentPriority.find(p => p.amount > 0);
          const selectedMethod = usedFromArray ? usedFromArray.label : 'Cash';

          // Enable split payment if more than one payment mode has a positive amount
          const positiveModesCount = paymentPriority.filter(p => p.amount > 0).length;
          const splitPaymentFromApi = positiveModesCount > 1;

          setPayments(prev => ({
            ...prev,
            splitPaymentEnabled: splitPaymentFromApi,
            selectedPaymentMethod: selectedMethod,
            cashPayment: safeStr(cashAmount),
            cardPayment: safeStr(cardAmount),
            walletProvider: prev.walletProvider || 'PhonePe',
            walletAmount: safeStr(walletAmount),
            walletTxNo: firstRecord.PaidDetails || prev.walletTxNo || '',
            chequeAmount: safeStr(chequeAmount),
            chequeNo: prev.chequeNo || '',
            couponNo: couponNoFromPayments || prev.couponNo || '',
            couponAmount: safeStr(couponAmount),
            couponAmountLocked: prev.couponAmountLocked || false,
            creditNoteNo: prev.creditNoteNo || '',
            creditNoteAmount: safeStr(creditNoteAmount),
            pointsRedeem: safeStr(pointsAmount),
            eligiblePoints: prev.eligiblePoints || '',
            bankTransAmount: safeStr(upiOrBankAmount),
            otherAmount: safeStr(otherAmount),
            payBefore: prev.payBefore || '',
          }));

          console.log('=== DATA POPULATION COMPLETE ===');
        }
      }

      console.log('=== END INVOICE DETAILS DEBUG ===');
    } catch (error) {
      console.error('Error fetching invoice details:', error);
    }
  };

  // Handle navigation from SaleProductEntryForm - show success message when returning
  useEffect(() => {
    console.log('SalesInvoice: Checking sessionStorage for productAdded:', sessionStorage.getItem('productAdded'));

    // Check sessionStorage for return indicators
    const productAdded = sessionStorage.getItem('productAdded') === 'true';

    if (productAdded) {
      console.log('SalesInvoice: Product was added, showing success message');

      // Show success message for product added
      toast({
        title: 'Product Added Successfully',
        description: 'Product has been added to inventory. You can now search for it.',
        className: 'bg-green-600 text-white border-green-700'
      });

      // Clear the sessionStorage flags
      sessionStorage.removeItem('productAdded');
      sessionStorage.removeItem('returnSection');
    }
  }, []);

  // Additional effect to check sessionStorage on component mount with delay
  useEffect(() => {
    const checkSessionStorage = () => {
      console.log('SalesInvoice: Component mounted, checking sessionStorage with delay');
      const productAdded = sessionStorage.getItem('productAdded') === 'true';

      if (productAdded) {
        console.log('SalesInvoice: Product added detected on mount, showing success message');

        // Show success message for product added
        toast({
          title: 'Product Added Successfully',
          description: 'Product has been added to inventory. You can now search for it.',
          className: 'bg-green-600 text-white border-green-700'
        });

        // Clear the sessionStorage flags
        sessionStorage.removeItem('productAdded');
        sessionStorage.removeItem('returnSection');
      }
    };

    // Check immediately
    checkSessionStorage();

    // Also check after a short delay to ensure sessionStorage is available
    const timeoutId = setTimeout(checkSessionStorage, 100);

    return () => clearTimeout(timeoutId);
  }, []); // Empty dependency array - runs only on mount

  // Cleanup effect to clear cache when component unmounts
  useEffect(() => {
    console.log('SalesInvoice: Component mounted');
    return () => {
      console.log('SalesInvoice: Component unmounting, clearing cache');
      // Clear sales-invoice specific queries when component unmounts
      queryClient.removeQueries({ queryKey: ['sales-invoice-states'] });
      queryClient.removeQueries({ queryKey: ['sales-invoice-locations'] });
      queryClient.removeQueries({ queryKey: ['sales-invoice-companies'] });
      queryClient.removeQueries({ queryKey: ['sales-invoice-partners'] });
      queryClient.removeQueries({ queryKey: ['sales-invoice-representatives'] });
      queryClient.removeQueries({ queryKey: ['products-barcode'] });
      queryClient.removeQueries({ queryKey: ['products-name'] });
      queryClient.removeQueries({ queryKey: ['customer-phone'] });
    };
  }, [queryClient]);

  // Remove local useState for customer, products, payments, notes, repName

  // All hooks and state declarations remain here
  // Placeholder state for all fields
  const [form, setForm] = useState({
    ProductBarcode: '', Serial: '', ProductName: '',
    RepName: '', Location: '', SelectCompany: '', QuoteNo: '', Mode: '', VehicleNo: '',
    InvoiceDate: new Date().toISOString().slice(0, 10), PayBefore: (() => {
      const defaultDate = new Date();
      defaultDate.setDate(defaultDate.getDate() + 30); // Default to 30 days from now
      return defaultDate.toISOString().slice(0, 10);
    })(), EligiblePoints: 0, Redeem: 0, Discount: '',
    BalanceDue: '', SplitPayment: false, Total: '',
    ShippingState: '', ShippingAddress: '', ShippingName: '', ShippingGST: '', ShippingPhone: '', ShippingEmail: '',
    eCommOrderID: ''
  });

  const [barcodeSearchTerm, setBarcodeSearchTerm] = useState('');
  const [weightFromBarcode, setWeightFromBarcode] = useState<number | null>(null);
  const [productNameSearchTerm, setProductNameSearchTerm] = useState('');
  const [showBarcodeDropdown, setShowBarcodeDropdown] = useState(false);
  const [showProductNameDropdown, setShowProductNameDropdown] = useState(false);
  const [productDropdownActiveIndex, setProductDropdownActiveIndex] = useState(-1);
  const productNameDropdownRef = useRef<HTMLDivElement>(null);

  // Serial Number search states
  const [serialNumberSearchTerm, setSerialNumberSearchTerm] = useState('');
  const [debouncedSerialNumberSearch, setDebouncedSerialNumberSearch] = useState('');

  // Serial Selection Popup states
  const [showSerialSelectionPopup, setShowSerialSelectionPopup] = useState(false);
  const [serialPopupProduct, setSerialPopupProduct] = useState<any>(null);
  const [addedSerialNumbers, setAddedSerialNumbers] = useState<Set<string>>(new Set());

  // Rep Name dropdown states
  const [showRepNameDropdown, setShowRepNameDropdown] = useState(false);
  const [repNameSearchTerm, setRepNameSearchTerm] = useState('');

  const [selectedPartner, setSelectedPartner] = useState('');

  // Ship to Same functionality states
  const [showShipToPopup, setShowShipToPopup] = useState(false);

  // When opening for create mode (not edit), reset form so GST, QuoteNo, etc. are not pre-filled from a previous edit
  useEffect(() => {
    if (!isEditMode && !editingInvoiceId && resetForNewInvoice) {
      resetForNewInvoice();
    }
  }, []); // Run once on mount - eslint prefers empty deps for mount-only effect

  // Ensure locations and companies are loaded when component mounts
  useEffect(() => {
    // Only log once when component mounts
    console.log('SalesInvoice: Component mounted, checking data loading');
    console.log('SalesInvoice: Locations count:', locations?.length || 0);
    console.log('SalesInvoice: Companies count:', companies?.length || 0);
    console.log('SalesInvoice: Partners count:', partners?.length || 0);
    console.log('SalesInvoice: Is edit mode:', isEditMode);
    console.log('SalesInvoice: Editing invoice ID:', editingInvoiceId);

    // If in edit mode and data is not loaded, we need to wait for it
    if (isEditMode && editingInvoiceId) {
      console.log('SalesInvoice: In edit mode, waiting for data to load...');
    }
  }, []); // Empty dependency array - only run once on mount

  // Handle edit mode data loading
  useEffect(() => {
    if (isEditMode && editingInvoiceId) {
      // Only log once when entering edit mode
      console.log('SalesInvoice: Edit mode detected, preserving loaded data');
      console.log('SalesInvoice: Current customer data:', customer);
      console.log('SalesInvoice: Current products:', products);
      console.log('SalesInvoice: Locations loaded:', locations?.length || 0);
      console.log('SalesInvoice: Companies loaded:', companies?.length || 0);
      console.log('SalesInvoice: Partners loaded:', partners?.length || 0);

      // Fetch and log the invoice details API response for debugging
      console.log('SalesInvoice: Fetching invoice details for debugging...');
      fetchAndLogInvoiceDetails(editingInvoiceId, true); // Pass true to populate payments

      // Don't clear data in edit mode - it should already be populated by populateSalesInvoiceContext

      // Ensure partner is set for product search to work
      if (customer.Partner && !selectedPartner) {
        console.log('SalesInvoice: Setting selectedPartner to:', customer.Partner, 'in edit mode');
        setSelectedPartner(customer.Partner);
      }

      return;
    }
  }, [isEditMode, editingInvoiceId]); // Remove customer, products, locations, companies, partners from dependencies

  // Add state to persist shipping form values
  const [lastCustomerShipToForm, setLastCustomerShipToForm] = useState(null);

  // Handle "Ship to Same" functionality
  useEffect(() => {
    if (isEditMode && editingInvoiceId) {
      // In edit mode, NEVER change shipping data regardless of checkbox state
      // The API data should always be preserved and displayed
      console.log('Edit mode: Preserving API shipping data regardless of Ship to Same checkbox state');
      return;
    }

    // Only handle Ship to Same logic in create mode
    if (shipToSame) {
      // Save current shipping form values before switching to "Ship to Same"
      if (!lastCustomerShipToForm) {
        setLastCustomerShipToForm(shipToForm);
      }

      // Set shipping form to match customer details (only in create mode)
      setShipToForm({
        Name: customer.Name || '',
        Company: customer.Company || '',
        Phone: customer.Phone || '',
        Email: customer.Email || '',
        GST: customer.GST || '',
        Address: customer.Address || '',
        State: customer.State || '',
      });
    } else {
      // When "Ship to Same" is unchecked, populate with customer data as defaults
      // but preserve any existing custom shipping data if it exists
      const hasExistingShippingData = Boolean(shipToForm.Name || shipToForm.Company || shipToForm.Phone ||
        shipToForm.Email || shipToForm.GST || shipToForm.Address || shipToForm.State);

      if (!hasExistingShippingData) {
        // If no existing shipping data, populate with customer data as defaults
        setShipToForm({
          Name: customer.Name || '',
          Company: customer.Company || '',
          Phone: customer.Phone || '',
          Email: customer.Email || '',
          GST: customer.GST || '',
          Address: customer.Address || '',
          State: customer.State || '',
        });
      }
      // If there's existing shipping data, keep it unchanged
    }
  }, [shipToSame, lastCustomerShipToForm, setShipToForm, isEditMode, editingInvoiceId, customer]);

  // Update shipping form when customer data changes and "Ship to Same" is checked
  useEffect(() => {
    if (isEditMode && editingInvoiceId) {
      // In edit mode, NEVER update shipping form based on customer changes
      // The API data should always be preserved
      return;
    }

    if (shipToSame) {
      // Only update shipping form if "Ship to Same" is checked and we're not in edit mode
      setShipToForm({
        Name: customer.Name || '',
        Company: customer.Company || '',
        Phone: customer.Phone || '',
        Email: customer.Email || '',
        GST: customer.GST || '',
        Address: customer.Address || '',
        State: customer.State || '',
      });
    }
  }, [shipToSame, customer, setShipToForm, isEditMode, editingInvoiceId]);

  // Debounced search terms for better performance
  const [debouncedBarcodeSearch, setDebouncedBarcodeSearch] = useState('');
  const [debouncedProductNameSearch, setDebouncedProductNameSearch] = useState('');



  // Track when all dependencies are loaded
  const [isSearchReady, setIsSearchReady] = useState(false);

  // Debounce search terms to reduce API calls
  const parseWeightBarcode = (raw: string): { baseBarcode: string; weightKg: number } | null => {
    const trimmed = (raw || '').trim();
    if (!/^\d{11}$/.test(trimmed)) return null;
    const baseBarcode = trimmed.slice(0, 5);
    const weightPart = trimmed.slice(5);
    const grams = parseInt(weightPart, 10);
    if (!Number.isFinite(grams) || grams <= 0) return null;
    const weightKg = grams / 1000;
    return { baseBarcode, weightKg };
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const parsed = parseWeightBarcode(barcodeSearchTerm);
      setDebouncedBarcodeSearch(parsed?.baseBarcode ?? barcodeSearchTerm);
    }, 300); // 300ms delay
    return () => clearTimeout(timer);
  }, [barcodeSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedProductNameSearch(productNameSearchTerm);
    }, 300); // 300ms delay
    return () => clearTimeout(timer);
  }, [productNameSearchTerm]);



  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSerialNumberSearch(serialNumberSearchTerm);
    }, 300); // 300ms delay
    return () => clearTimeout(timer);
  }, [serialNumberSearchTerm]);

  // Barcode dropdown: fetch matching barcodes
  const { data: barcodeProductsResponse, isLoading: isBarcodeLoading } = useQuery({
    queryKey: ['products-barcode', debouncedBarcodeSearch, selectedPartner, customer.Location],
    queryFn: () => fetchProductsFiltered(debouncedBarcodeSearch),
    enabled: debouncedBarcodeSearch.length > 0 && !!selectedPartner && !!customer.Location && !!isSearchReady,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10, // Cache for 10 minutes (renamed from cacheTime in newer versions)
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
    retry: 1, // Reduce retry attempts for faster failure
  });
  const barcodeProducts = barcodeProductsResponse?.data || [];

  // Product name dropdown: fetch matching names
  const { data: productNameResponse, isLoading: isProductNameLoading } = useQuery({
    queryKey: ['products-name', debouncedProductNameSearch, selectedPartner, customer.Location],
    queryFn: () => fetchProductsFiltered(debouncedProductNameSearch),
    enabled: debouncedProductNameSearch.length > 0 && !!selectedPartner && !!customer.Location && !!isSearchReady, // Show results for any non-empty search
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10, // Cache for 10 minutes (renamed from cacheTime in newer versions)
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
    retry: 1, // Reduce retry attempts for faster failure
  });
  const productNameProducts = productNameResponse?.data || [];

  useEffect(() => {
    if (showProductNameDropdown && productNameProducts && productNameProducts.length > 0) {
      setProductDropdownActiveIndex(0);
    } else {
      setProductDropdownActiveIndex(-1);
    }
  }, [showProductNameDropdown, productNameProducts]);

  useEffect(() => {
    if (productDropdownActiveIndex < 0) return;
    const items = productNameDropdownRef.current?.querySelectorAll('.dropdown-item');
    const el = items && items[productDropdownActiveIndex] as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [productDropdownActiveIndex]);

  // Add state for product add message
  const [productAddMessage, setProductAddMessage] = useState<string | null>(null);
  const [productAddMessageType, setProductAddMessageType] = useState<'success' | 'error' | null>(null);

  // Add state for ProductEntryForm modal
  const [showProductEntryModal, setShowProductEntryModal] = useState(false);
  const productFormRef = useRef<ProductEntryFormHandle>(null);

  // Auto-hide the product add message after 3 seconds
  useEffect(() => {
    if (productAddMessage) {
      const timer = setTimeout(() => {
        setProductAddMessage(null);
        setProductAddMessageType(null);
      }, 3000); // 3 seconds
      return () => clearTimeout(timer);
    }
  }, [productAddMessage]);

  // Helper: match serial by trimmed, case-insensitive comparison (supports text like "one" matching "one " in SerialDetails)
  const serialMatches = (serialFromApi: string | undefined, searchTerm: string) =>
    (serialFromApi ?? '').trim().toLowerCase() === searchTerm.trim().toLowerCase();

  // Handle serial number search (when user searches by serial number)
  const handleSerialNumberSearch = async (serialNumber: string) => {
    if (!serialNumber.trim() || !selectedPartner || !isSearchReady) return;

    try {
      const latest = await fetchProductsFiltered(serialNumber);
      const searchTrimmed = serialNumber.trim();
      const found = latest.data?.find((p: any) => {
        // Check if any serial detail matches the searched serial number (text or number, trimmed & case-insensitive)
        if (p.SerialDetails && Array.isArray(p.SerialDetails)) {
          return p.SerialDetails.some((sd: any) =>
            serialMatches(sd.SerialNumber, searchTrimmed)
          );
        }
        return false;
      });

      if (found) {
        const hasSerial = found.hasSerial === 1 || found.HasSerialFlag === 1;
        if (hasSerial && found.SerialDetails && found.SerialDetails.length > 0) {
          // Find the matching serial detail (trimmed, case-insensitive)
          const matchingSerial = found.SerialDetails.find((sd: any) =>
            serialMatches(sd.SerialNumber, searchTrimmed)
          );

          const alreadyAdded = matchingSerial && [...addedSerialNumbers].some((added) => serialMatches(added, searchTrimmed));
          if (matchingSerial && !alreadyAdded) {
            // Add product with this serial number
            addProductToTableWithSerial(found, [matchingSerial]);
            setSerialNumberSearchTerm('');
            setProductAddMessage('Product added with serial number.');
            setProductAddMessageType('success');
            // Track added serial number (use actual value from API so removal works)
            setAddedSerialNumbers(prev => new Set(prev).add(matchingSerial.SerialNumber ?? ''));
          } else if (alreadyAdded) {
            setProductAddMessage('Serial number already added.');
            setProductAddMessageType('error');
          }
        }
        // If hasSerial = 0, do nothing (as per requirements)
      } else {
        // No product has this serial in SerialDetails
        setProductAddMessage("The serial number doesn't exist.");
        setProductAddMessageType('error');
      }
    } catch (error) {
      console.error('Error searching serial number:', error);
    }
  };

  // Add product to DataTable with serial numbers
  const addProductToTableWithSerial = (productDetails: any, serialDetails: any[]) => {
    if (!serialDetails || serialDetails.length === 0) {
      addProductToTable(productDetails);
      return;
    }

    // Add one product row per serial number
    serialDetails.forEach((serialDetail) => {
      const userMRP = parseFloat(productDetails.MRP) || 0;
      const qty = 1; // Each serial is quantity 1
      const gstPercent = parseFloat(productDetails.GST) || 0;
      const cessPercent = parseFloat(productDetails.CessPer) || 0;

      // Use serial rate if available, otherwise use product OfferPrice or MRP
      const serialRate = parseFloat(serialDetail.Rate) || 0;
      const offerPrice = serialRate > 0 ? serialRate : (productDetails.OfferPrice
        ? parseFloat(productDetails.OfferPrice)
        : userMRP);

      // Calculate Display DiscountPer = 100 - (OfferPrice * 100 / MRP)
      const displayDiscountPer = userMRP > 0 ? 100 - (offerPrice * 100 / userMRP) : 0;

      // Calculate Display discount = MRP - OfferPrice * Qty (per unit: MRP - OfferPrice, then * qty for total)
      const displayDiscount = (userMRP - offerPrice) * qty;

      // Calculate Taxable Value = OfferPrice * qty / (1 + (GstPercent + CessPercent) / 100)
      const taxableValue = (offerPrice * qty) / (1 + (gstPercent + cessPercent) / 100);

      // Calculate SGST+CGST or IGST = Taxable Value * GstPercent / 100
      const gstAmount = taxableValue * gstPercent / 100;

      // Calculate Cess Amount = Taxable Value * CessPercent / 100
      const cessAmount = taxableValue * cessPercent / 100;

      // Calculate Total = Taxable Value + GST Amount + Cess Amount
      const total = taxableValue + gstAmount + cessAmount;

      // Calculate Unit Price from MRP (for backward compatibility)
      const totalTax = gstPercent + cessPercent;
      const unitPrice = totalTax > 0 ? userMRP / (1 + totalTax / 100) : userMRP;
      const unitPriceStr = unitPrice.toFixed(2);
      // Always use product-level cost price from JSON; if 0 in JSON then show 0 (no fallback to serial or unit price)
      const productCostNum = Number(productDetails.costprice ?? productDetails.CostPrice);
      const costPriceToUse = !Number.isNaN(productCostNum) ? productCostNum.toFixed(2) : '0.00';

      const newProduct = {
        ...productDetails,
        ProdCode: productDetails.ProdCode || productDetails.code || '',
        Qty: qty,
        DelQty: qty,
        UnitPrice: unitPriceStr,
        OriginalUnitPrice: unitPriceStr,
        DiscountPer: displayDiscountPer.toFixed(2),
        Discount: displayDiscount.toFixed(2),
        OfferPrice: offerPrice.toFixed(2),
        TaxableValue: taxableValue.toFixed(2),
        GSTAmount: gstAmount.toFixed(2),
        CessAmount: cessAmount.toFixed(2),
        Total: total.toFixed(2),
        CostPrice: costPriceToUse,
        OriginalCostPrice: costPriceToUse,
        SerialNumber: serialDetail.SerialNumber, // Add serial number
        SerialDetails: [serialDetail], // Store serial detail
        hasSerial: true,
      };

      setProducts(prev => {
        if (invoiceForm.AddInFIFO) {
          // FIFO: add to bottom
          return [...prev, newProduct];
        } else {
          // LIFO: add to top
          return [newProduct, ...prev];
        }
      });
    });
  };

  // Handle serial selection from popup
  const handleSerialSelection = (selectedSerials: any[]) => {
    if (!serialPopupProduct || selectedSerials.length === 0) return;

    // Track added serial numbers
    const newSerialNumbers = new Set(addedSerialNumbers);
    selectedSerials.forEach(serial => {
      newSerialNumbers.add(serial.SerialNumber);
    });
    setAddedSerialNumbers(newSerialNumbers);

    // Add products with selected serials
    addProductToTableWithSerial(serialPopupProduct, selectedSerials);

    setProductAddMessage(`${selectedSerials.length} product(s) added with serial numbers.`);
    setProductAddMessageType('success');

    // Close popup
    setShowSerialSelectionPopup(false);
    setSerialPopupProduct(null);
  };

  // Add product to DataTable. When Duplicate Invoice Line Items enabled, always add as new row; otherwise merge by barcode.
  const addProductToTable = (productDetails: any) => {
    const duplicateLineItems = salesInvoiceSettings?.duplicateInvoiceLineItems ?? false;
    setProducts(prev => {
      const idx = duplicateLineItems ? -1 : prev.findIndex(p => p.Barcode === productDetails.Barcode);
      const addQty = Number(productDetails.Qty) || 1;
      if (idx !== -1) {
        // Product exists, update quantity
        const existing = prev[idx];
        const existingQty = Number(existing.Qty) || 1;
        const newQty = existingQty + addQty;
        const existingDelQty = Number(existing.DelQty ?? existing.Qty ?? 1);
        const newDelQty = existingDelQty + addQty;
        const updated = {
          ...existing,
          Qty: newQty,
          DelQty: newDelQty,
        };
        const newArr = [...prev];
        newArr[idx] = updated;
        return newArr;
      }
      // New product, set Qty and DelQty to the specified value
      const userMRP = parseFloat(productDetails.MRP) || 0;
      const qty = addQty;
      const gstPercent = parseFloat(productDetails.GST) || 0;
      const cessPercent = parseFloat(productDetails.CessPer) || 0;

      // Use OfferPrice from API if available, otherwise use MRP
      const offerPrice = productDetails.OfferPrice
        ? parseFloat(productDetails.OfferPrice)
        : userMRP;

      // Calculate Display DiscountPer = 100 - (OfferPrice * 100 / MRP)
      const displayDiscountPer = userMRP > 0 ? 100 - (offerPrice * 100 / userMRP) : 0;

      // Calculate Display discount = MRP - OfferPrice * Qty (per unit: MRP - OfferPrice, then * qty for total)
      const displayDiscount = (userMRP - offerPrice) * qty;

      // Calculate Taxable Value = OfferPrice * qty / (1 + (GstPercent + CessPercent) / 100)
      const taxableValue = (offerPrice * qty) / (1 + (gstPercent + cessPercent) / 100);

      // Calculate SGST+CGST or IGST = Taxable Value * GstPercent / 100
      const gstAmount = taxableValue * gstPercent / 100;

      // Calculate Cess Amount = Taxable Value * CessPercent / 100
      const cessAmount = taxableValue * cessPercent / 100;

      // Calculate Total = Taxable Value + GST Amount + Cess Amount
      const total = taxableValue + gstAmount + cessAmount;

      // Calculate Unit Price from MRP (for backward compatibility)
      const totalTax = gstPercent + cessPercent;
      const unitPrice = totalTax > 0 ? userMRP / (1 + totalTax / 100) : userMRP;
      const unitPriceStr = unitPrice.toFixed(2);
      // Use cost price from API when provided (including 0); only fall back to unit price when not provided
      const apiCostPrice = productDetails.costprice ?? productDetails.CostPrice;
      const costPriceFromApi = apiCostPrice !== undefined && apiCostPrice !== null ? Number(apiCostPrice) : NaN;
      const costPriceToUse = !Number.isNaN(costPriceFromApi) ? costPriceFromApi.toFixed(2) : unitPriceStr;

      const newProduct = {
        ...productDetails,
        ProdCode: productDetails.ProdCode || productDetails.code || '',
        Qty: addQty,
        DelQty: addQty,
        UnitPrice: unitPriceStr,
        OriginalUnitPrice: unitPriceStr,
        DiscountPer: displayDiscountPer.toFixed(2),
        Discount: displayDiscount.toFixed(2),
        OfferPrice: offerPrice.toFixed(2),
        TaxableValue: taxableValue.toFixed(2),
        GSTAmount: gstAmount.toFixed(2),
        CessAmount: cessAmount.toFixed(2),
        Total: total.toFixed(2),
        CostPrice: costPriceToUse,
        OriginalCostPrice: costPriceToUse,
      };
      console.log('Adding product with new calculations:', {
        productDetails,
        MRP: userMRP,
        OfferPrice: offerPrice,
        Qty: qty,
        DisplayDiscountPer: displayDiscountPer,
        DisplayDiscount: displayDiscount,
        TaxableValue: taxableValue,
        GSTAmount: gstAmount,
        CessAmount: cessAmount,
        Total: total,
        newProduct
      });
      if (invoiceForm.AddInFIFO) {
        // FIFO: add to bottom
        return [...prev, newProduct];
      } else {
        // LIFO: add to top
        return [newProduct, ...prev];
      }
    });
  };

  // State for Pop-up for Qty
  const [showQtyModal, setShowQtyModal] = useState(false);
  const [qtyModalProduct, setQtyModalProduct] = useState<any>(null);
  const [qtyModalQty, setQtyModalQty] = useState('1');
  // Temporary on-screen override for qty popup (null = use settings)
  const [qtyPopupOverride, setQtyPopupOverride] = useState<boolean | null>(null);
  const showQtyPopup = qtyPopupOverride ?? (salesInvoiceSettings?.invoiceQtyPopupRequired ?? false);

  // State for Cost Price validation popup
  const [showCostPriceValidation, setShowCostPriceValidation] = useState(false);
  const [costPriceValidationData, setCostPriceValidationData] = useState<{
    index: number;
    enteredValue: string;
    originalValue: string;
  } | null>(null);

  // State for Unit Price validation popup
  const [showUnitPriceValidation, setShowUnitPriceValidation] = useState(false);
  const [unitPriceValidationData, setUnitPriceValidationData] = useState<{
    index: number;
    enteredValue: string;
    originalValue: string;
  } | null>(null);

  // Handler for Cost Price validation - Confirm
  const handleCostPriceConfirm = () => {
    if (costPriceValidationData) {
      const { index, enteredValue } = costPriceValidationData;

      // Update the product with the entered cost price
      setProducts(prev => prev.map((prod, i) =>
        i === index ? { ...prod, CostPrice: enteredValue } : prod
      ));

      // Clear raw input value
      setRawInputValues(prev => {
        const newVals = { ...prev };
        delete newVals[`CostPrice-${index}`];
        return newVals;
      });

      // Move to next field
      const fieldOrder = ['MRP', 'UnitPrice', 'Qty', 'DiscountPer', 'Discount', 'CostPrice', 'DelQty', 'OfferPrice'];
      const currentFieldIndex = fieldOrder.indexOf('CostPrice');

      let nextField: string | null = null;
      let nextRowIndex = index;

      if (currentFieldIndex < fieldOrder.length - 1) {
        nextField = fieldOrder[currentFieldIndex + 1];
      } else if (index < products.length - 1) {
        nextField = fieldOrder[0];
        nextRowIndex = index + 1;
      }

      if (nextField) {
        setTimeout(() => {
          const nextInput = document.querySelector(
            `input[data-field="${nextField}"][data-index="${nextRowIndex}"]`
          ) as HTMLInputElement;
          if (nextInput) {
            nextInput.focus();
            nextInput.select();
          }
        }, 50);
      }
    }

    setShowCostPriceValidation(false);
    setCostPriceValidationData(null);
  };

  // Handler for Cost Price validation - Cancel
  const handleCostPriceCancel = () => {
    if (costPriceValidationData) {
      const { index, originalValue } = costPriceValidationData;

      // Revert to original cost price
      setProducts(prev => prev.map((prod, i) =>
        i === index ? { ...prod, CostPrice: originalValue } : prod
      ));

      // Clear raw input value
      setRawInputValues(prev => {
        const newVals = { ...prev };
        delete newVals[`CostPrice-${index}`];
        return newVals;
      });
    }

    setShowCostPriceValidation(false);
    setCostPriceValidationData(null);
  };

  // Handler for Unit Price validation - Confirm
  const handleUnitPriceConfirm = () => {
    if (unitPriceValidationData) {
      const { index, enteredValue } = unitPriceValidationData;

      // Apply the entered value and recalculate
      handleProductFieldChange(index, 'UnitPrice', enteredValue);

      // Clear raw input value
      setRawInputValues(prev => {
        const newVals = { ...prev };
        delete newVals[`UnitPrice-${index}`];
        return newVals;
      });
    }

    setShowUnitPriceValidation(false);
    setUnitPriceValidationData(null);
  };

  // Handler for Unit Price validation - Cancel
  const handleUnitPriceCancel = () => {
    if (unitPriceValidationData) {
      const { index, originalValue } = unitPriceValidationData;

      // Revert to original unit price
      setProducts(prev => prev.map((prod, i) =>
        i === index ? { ...prod, UnitPrice: originalValue } : prod
      ));

      // Clear raw input value
      setRawInputValues(prev => {
        const newVals = { ...prev };
        delete newVals[`UnitPrice-${index}`];
        return newVals;
      });
    }

    setShowUnitPriceValidation(false);
    setUnitPriceValidationData(null);
  };

  // Initialize OriginalCostPrice for products that don't have it (e.g., from edit mode)
  useEffect(() => {
    setProducts(prev => prev.map(prod => {
      const updates: any = {};
      if (!prod.OriginalCostPrice && prod.CostPrice) {
        updates.OriginalCostPrice = prod.CostPrice;
      }
      if (!prod.OriginalUnitPrice && prod.UnitPrice) {
        updates.OriginalUnitPrice = prod.UnitPrice;
      }
      if (Object.keys(updates).length > 0) {
        return { ...prod, ...updates };
      }
      return prod;
    }));
  }, []); // Run once on mount

  const isWeightBasedUqc = (uqc: any): boolean => {
    if (!uqc) return false;
    const norm = String(uqc).toLowerCase().replace(/\./g, '').trim();
    const weightUqcs = [
      'kg',
      'kgs',
      'kgm',
      'kilogram',
      'kilograms',
      'kilo',
      'kilos',
      'g',
      'gm',
      'gms',
      'gram',
      'grams',
    ];
    return weightUqcs.includes(norm);
  };

  // Update handleBarcodeProductSelect to fetch latest product data for selected location and partner
  const handleBarcodeProductSelect = async (product: any) => {
    setBarcodeSearchTerm('');
    setShowBarcodeDropdown(false);
    if (!product.Barcode) return;
    console.log('[handleBarcodeProductSelect] Selected product:', product, 'partnerId:', selectedPartner, 'locationId:', customer.Location);
    const latest = await fetchProductsFiltered(product.Barcode);
    const found = latest.data?.find((p: any) => p.Barcode === product.Barcode);
    if (found) {
      // -------- 11 DIGIT WEIGHT BARCODE PARSER --------
      let parsedWeightFromBarcode: number | null = null;

      const scannedBarcode = found.Barcode;

      console.log('[Barcode Debug] Raw scanned barcode:', scannedBarcode);
      console.log('[Barcode Debug] Barcode length:', scannedBarcode?.length);

      if (scannedBarcode && scannedBarcode.length === 11) {
        console.log('[Barcode Debug] Detected 11 digit barcode');

        const productCodePart = scannedBarcode.slice(0, 5);
        const weightPart = scannedBarcode.slice(5);

        console.log('[Barcode Debug] Product code part:', productCodePart);
        console.log('[Barcode Debug] Weight part:', weightPart);

        if (found.UQCName?.toLowerCase() === 'kgs') {
          const grams = parseInt(weightPart, 10);

          console.log('[Barcode Debug] Parsed grams:', grams);

          if (!isNaN(grams)) {
            parsedWeightFromBarcode = grams / 1000;

            console.log(
              '[Barcode Debug] Converted quantity (kg):',
              parsedWeightFromBarcode
            );
          } else {
            console.log('[Barcode Debug] Failed to parse grams');
          }
        } else {
          console.log(
            '[Barcode Debug] Product UQC is not KGS, skipping weight barcode logic'
          );
        }
      }

      // Check if product is available based on isTraceable logic
      const available = Number(found.Available) || 0;
      const isTraceable = found.isTraceable || false;

      // If available is 0 and isTraceable is true, don't add the product
      if (available <= 0 && isTraceable) {
        return; // Don't add the product, but don't show error message
      }

      // If isTraceable is false, allow adding even if available is 0
      // If available > 0, allow adding regardless of isTraceable
      if (available > 0 || !isTraceable) {
        // Check if product has serial numbers
        const hasSerial = found.hasSerial === 1 || found.HasSerialFlag === 1;
        if (hasSerial && found.SerialDetails && found.SerialDetails.length > 0) {
          // Open serial selection popup
          setSerialPopupProduct(found);
          setShowSerialSelectionPopup(true);
        } else {
          // No serial numbers, proceed with normal flow
          const canUseWeight =
            parsedWeightFromBarcode !== null ||
            (weightFromBarcode && isWeightBasedUqc(found.UQCName));

          console.log('[Barcode Debug] Can use weight:', canUseWeight);

          if (canUseWeight) {
            const qty =
              parsedWeightFromBarcode ?? (weightFromBarcode as number);

            console.log('[Barcode Debug] Final quantity used:', qty);
            console.log('[Barcode Debug] Adding product with qty:', qty);

            const productWithQty = { ...found, Qty: qty, DelQty: qty };
            addProductToTable(productWithQty);
            setProductAddMessage(`Product added with ${qty} kg.`);
            setProductAddMessageType('success');
            setWeightFromBarcode(null);
          } else if (showQtyPopup) {
            setQtyModalProduct(found);
            setQtyModalQty('1');
            setQtyModalMRP(found.MRP?.toString() || '');
            const gstPercent = parseFloat(found.GST) || 0;
            const cessPercent = parseFloat(found.CessPer) || 0;
            const mrp = parseFloat(found.MRP) || 0;
            const totalTax = gstPercent + cessPercent;
            const unitPrice = totalTax > 0 ? mrp / (1 + totalTax / 100) : mrp;
            setQtyModalUnitPrice(unitPrice.toFixed(2));
            setShowQtyModal(true);
          } else {
            addProductToTable(found);
            setProductAddMessage('Product added.');
            setProductAddMessageType('success');
          }
        }
      }
    }
  };

  // Update handleNameProductSelect to fetch latest product data for selected location and partner
  const handleNameProductSelect = async (product: any) => {
    setProductNameSearchTerm('');
    setShowProductNameDropdown(false);
    if (!product.Barcode) return;
    console.log('[handleNameProductSelect] Selected product:', product, 'partnerId:', selectedPartner, 'locationId:', customer.Location);
    const latest = await fetchProductsFiltered(product.Title);
    const found = latest.data?.find((p: any) => p.Barcode === product.Barcode);
    if (found) {
      // Check if product is available based on isTraceable logic
      const available = Number(found.Available) || 0;
      const isTraceable = found.isTraceable || false;

      // If available is 0 or negative and isTraceable is true, don't add the product
      if (available <= 0 && isTraceable) {
        return; // Don't add the product, but don't show error message
      }

      // If isTraceable is false, allow adding even if available is 0
      // If available > 0, allow adding regardless of isTraceable
      if (available > 0 || !isTraceable) {
        // Check if product has serial numbers
        const hasSerial = found.hasSerial === 1 || found.HasSerialFlag === 1;
        if (hasSerial && found.SerialDetails && found.SerialDetails.length > 0) {
          // Open serial selection popup
          setSerialPopupProduct(found);
          setShowSerialSelectionPopup(true);
        } else {
          // No serial numbers, proceed with normal flow
          if (showQtyPopup) {
            setQtyModalProduct(found);
            setQtyModalQty('1');
            setQtyModalMRP(found.MRP?.toString() || '');
            const gstPercent = parseFloat(found.GST) || 0;
            const cessPercent = parseFloat(found.CessPer) || 0;
            const mrp = parseFloat(found.MRP) || 0;
            const totalTax = gstPercent + cessPercent;
            const unitPrice = totalTax > 0 ? mrp / (1 + totalTax / 100) : mrp;
            setQtyModalUnitPrice(unitPrice.toFixed(2));
            setShowQtyModal(true);
          } else {
            addProductToTable(found);
            setProductAddMessage('Product added.');
            setProductAddMessageType('success');
          }
        }
      }
    }
  };

  // Remove product from table
  const handleRemoveProduct = (index: number) => {
    const removed = products[index];
    const serialToRemove = removed?.SerialNumber
      || removed?.SerialDetails?.[0]?.SerialNumber
      || null;
    if (serialToRemove) {
      setAddedSerialNumbers(prev => {
        const next = new Set(prev);
        next.delete(serialToRemove);
        return next;
      });
    }
    setProducts(prev => prev.filter((_, i) => i !== index));
  };



  // Calculate invoice total
  const invoiceTotal = products.reduce((sum, product) => {
    const mrp = parseFloat(product.MRP) || 0;
    const qty = parseFloat(product.Qty) || 0;
    const discount = parseFloat(product.Discount) || 0;
    // Per-unit offer price
    const offerPerUnit = qty > 0 ? (mrp - (discount / qty)) : mrp;
    // Total for all units
    const total = offerPerUnit * qty;
    return sum + total;
  }, 0);

  const paymentModes = [
    'Cash', 'Card', 'Wallets', 'Cheque', 'Coupon', 'Credit Note', 'Points'
  ];
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedPayment = paymentModes[selectedIndex];

  // Fetch states using react-query
  const { data: statesResponse, isLoading: isStatesLoading, error: statesError } = useQuery({
    queryKey: ['sales-invoice-states'],
    queryFn: fetchStates,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
  const states = statesResponse?.data || [];

  const { data: locationsResponse, isLoading: isLocationsLoading, error: locationsError } = useQuery({
    queryKey: ['sales-invoice-locations'],
    queryFn: fetchLocations,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
  const locations = Array.isArray(locationsResponse) ? locationsResponse : (locationsResponse?.data || []);

  const { data: companiesResponse, isLoading: isCompaniesLoading, error: companiesError } = useQuery({
    queryKey: ['sales-invoice-companies'],
    queryFn: fetchCompanies,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
  const companies = companiesResponse?.data || [];

  const { data: partnersResponse, isLoading: isPartnersLoading, error: partnersError } = useQuery({
    queryKey: ['sales-invoice-partners'],
    queryFn: fetchPartners,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
  const partners = partnersResponse?.data || [];

  const { data: representativesResponse, isLoading: isRepresentativesLoading, error: representativesError } = useQuery({
    queryKey: ['sales-invoice-representatives'],
    queryFn: fetchRepresentatives,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
  const representatives = representativesResponse?.data || [];

  // Set default partner when partners are loaded
  useEffect(() => {
    if (partners.length > 0 && !selectedPartner) {
      const defaultPartnerId = partners[0].Id.toString();
      setSelectedPartner(defaultPartnerId);
      // Also set customer.Partner to ensure eCommPartner is sent correctly
      setCustomer(prev => ({ ...prev, Partner: defaultPartnerId }));
    }
  }, [partners]);

  // Set search ready when all dependencies are loaded
  useEffect(() => {
    if (selectedPartner && locations.length > 0) {
      setIsSearchReady(true);
    }
  }, [selectedPartner, locations.length]);

  // Preload search results when partner and locations are ready
  useEffect(() => {
    if (selectedPartner && locations.length > 0) {
      // Preload with empty search to warm up the connection
      const preloadSearch = async () => {
        try {
          await fetchProductsFiltered('');
        } catch (error) {
          console.log('Preload failed, will retry on first search');
        }
      };
      preloadSearch();
    }
  }, [selectedPartner, locations.length]);

  // When partner changes, clear products and reload (but not in edit mode)
  useEffect(() => {
    if (!isEditMode) {
      setProducts([]);
    }
  }, [selectedPartner, isEditMode]);

  // Helper to get selected location ID
  const getSelectedLocationId = () => {
    return customer.Location || '';
  };

  // Helper to get state from selected company
  const getStateFromCompany = (companyId: string) => {
    if (!companies || companies.length === 0) return '';
    // Convert companyId to string for comparison, as dropdown values are strings
    const company = companies.find((c: any) => String(c.Id) === String(companyId));

    if (!company || !company.State) return '';

    // Try to find a matching state in the states dropdown
    if (states && states.length > 0) {
      const matchingState = states.find((s: any) =>
        s.State.toLowerCase() === company.State.toLowerCase()
      );
      if (matchingState) {
        return matchingState.State; // Return the exact state from dropdown
      }
    }

    // If no exact match found, return the company state as fallback
    return company.State;
  };

  // Updated fetchProductsFiltered to use selectedPartner as partnerID
  const fetchProductsFiltered = async (search: string) => {
    const locationId = getSelectedLocationId();
    const partnerId = selectedPartner;
    if (!locationId || !partnerId) {
      return { data: [] };
    }
    const baseUrl = getBaseUrl();
    const token = localStorage.getItem('token') || '';
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    console.log('[Barcode Search] fetchProductsFiltered called', {
      url: `${baseUrl}/api/AllProducts`,
      partnerId,
      locationId,
      rawSearch: search,
      debouncedBarcodeSearch,
      weightFromBarcode,
    });
    const response = await fetch(
      `${baseUrl}/api/AllProducts?partnerID=${partnerId}&LocationID=${locationId}&FilterValue=${encodeURIComponent(search)}`,
      {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
      }
    );
    if (!response.ok) throw new Error('Failed to fetch products');
    const data = await response.json();
    // Normalize data keys to match table usage
    if (Array.isArray(data.data)) {
      data.data = data.data.map((item: any) => ({
        ...item,
        Title: item.title,
        Barcode: item.barcode,
        Category: item.Category,
        HSN: item.HSN,
        MRP: item.rate,
        CostPrice: item.costprice,
        UQCName: item.UQCName,
        BrandName: item.BrandName,
        Available: item.available,
        OfferPrice: item.OfferPrice,
        CessPer: item.CessPer,
        GST: item.gstper, // <-- Map gstper to GST
        isTraceable: item.isTraceable, // Add isTraceable field
        hasSerial: item.hasSerial || item.HasSerialFlag || 0, // Preserve hasSerial
        HasSerialFlag: item.HasSerialFlag || item.hasSerial || 0, // Preserve HasSerialFlag
        SerialDetails: item.SerialDetails || [], // Preserve SerialDetails
      }));
    }
    return data;
  };

  const payBack = (() => {
    const payment = parseFloat(payments.cashPayment) || 0;
    const received = parseFloat(payments.cashReceived) || 0;
    return received > payment ? (received - payment).toFixed(2) : '0';
  })();

  const scrollCarousel = (dir: 'left' | 'right') => {
    setSelectedIndex((prev) => {
      if (dir === 'left') return prev > 0 ? prev - 1 : paymentModes.length - 1;
      return prev < paymentModes.length - 1 ? prev + 1 : 0;
    });
  };

  const [phoneQuery, setPhoneQuery] = useState('');
  const [debouncedPhoneQuery, setDebouncedPhoneQuery] = useState('');
  const [showPhoneSuggestions, setShowPhoneSuggestions] = useState(false);
  const [phoneDropdownActiveIndex, setPhoneDropdownActiveIndex] = useState(-1);
  const phoneDropdownRef = useRef<HTMLDivElement>(null);
  const [customerTotalDue, setCustomerTotalDue] = useState<number | null>(null);
  const customerLookupInputValue = isUnknown ? 'UNKNOWN' : debouncedPhoneQuery;
  const { data: customerPhoneResponse, isFetching: isCustomerPhoneFetching } = useQuery({
    queryKey: ['customer-phone-contains', customerLookupInputValue, isUnknown],
    queryFn: () => (isUnknown ? fetchUnknownDetails() : fetchCustomerByInputValue(customerLookupInputValue)),
    enabled: isUnknown || debouncedPhoneQuery.length >= 1,
    staleTime: 0,
  });

  const phoneSuggestions = Array.isArray(customerPhoneResponse?.data) ? customerPhoneResponse.data : [];

  useEffect(() => {
    if (!isUnknown) {
      setCustomerTotalDue(null);
    }
  }, [isUnknown]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedPhoneQuery(phoneQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [phoneQuery]);

  useEffect(() => {
    if (showPhoneSuggestions && phoneSuggestions.length > 0) {
      setPhoneDropdownActiveIndex(0);
    } else {
      setPhoneDropdownActiveIndex(-1);
    }
  }, [showPhoneSuggestions, phoneSuggestions]);

  useEffect(() => {
    if (phoneDropdownActiveIndex < 0) return;
    const items = phoneDropdownRef.current?.querySelectorAll('.phone-dropdown-item');
    const el = items && items[phoneDropdownActiveIndex] as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [phoneDropdownActiveIndex]);

  const applyCustomerFromPhoneResult = (c: any) => {
    setCustomer(f => ({
      ...f,
      Phone: c?.Mobile || '',
      Name: c?.CompanyName || '',
      Company: c?.ContactName || '',
      Email: c?.EmailId || '',
      Address: c?.Address || '',
      GST: c?.GSTNo || '',
      State: c?.State || '',
      Locality: c?.Locality || '',
      customerCode: c?.Code || f.customerCode,
    }));

    const dueRaw = c?.CustomerDue ?? c?.TotalDue;
    const dueNum = dueRaw === null || dueRaw === undefined || Number.isNaN(Number(dueRaw)) ? null : Number(dueRaw);
    setCustomerTotalDue(dueNum);
  };

  useEffect(() => {
    if (!customerPhoneResponse?.data || customerPhoneResponse.data.length < 1) return;

    // For Unknown checkbox, fetch + apply UNKNOWN customer record
    if (isUnknown) {
      applyCustomerFromPhoneResult(customerPhoneResponse.data[0]);
      return;
    }

    // For phone lookup, if full 10 digits and exactly one exact match, auto-apply
    if (phoneQuery.length === 10 && customerPhoneResponse.data.length === 1) {
      const c = customerPhoneResponse.data[0];
      if ((c?.Mobile || '') === phoneQuery) {
        applyCustomerFromPhoneResult(c);
      }
    }
  }, [customerPhoneResponse, phoneQuery, isUnknown]);

  // Add state for showMoreCustomerFields and showPaymentOptions at the top of the component
  const [showMoreCustomerFields, setShowMoreCustomerFields] = useState(false);
  const [showMoreProductFields, setShowMoreProductFields] = useState(false);
  const [customerGstError, setCustomerGstError] = useState(false);
  const [shipToGstError, setShipToGstError] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [hasOpenedPaymentModal, setHasOpenedPaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');

  // When Payment dialog opens, fetch latest settings: set Pay Before from "Invoice No of Day for Due", Split Payment from "Split Payment"
  useEffect(() => {
    if (!showPaymentModal) return;
    type SettingsRes = { status: number; message: string; data: { id: number; Name: string; Value: string }[] };
    authenticatedFetch<SettingsRes>('/api/settings')
      .then((res) => {
        queryClient.invalidateQueries({ queryKey: ['settings'] });
        const list = res?.data?.data ?? [];

        // Pay Before from "Invoice No of Day for Due" (e.g. "10:D" = 10 days) - used for sales invoice due payment calendar
        const invoiceDaysSetting = list.find((s) => s.Name === 'Invoice No of Day for Due');
        const value = invoiceDaysSetting?.Value?.trim();
        if (value) {
          const payBeforeDate = getPayBeforeDateFromInvoiceDaysSetting(value);
          setForm((f) => ({ ...f, PayBefore: payBeforeDate }));
        }

        // Split Payment checkbox: default from "Split Payment" setting ("1" = checked, "0" = unchecked); user can still toggle manually
        const splitPaymentSetting = list.find((s) => s.Name === 'Split Payment');
        const splitPaymentChecked = splitPaymentSetting?.Value?.trim() === '1';
        setPayments((p) => ({ ...p, splitPaymentEnabled: splitPaymentChecked }));

      })
      .catch((err) => {
        console.warn('Failed to fetch settings when opening Payment dialog:', err);
      });
  }, [showPaymentModal, queryClient]);

  // In edit mode, treat payment as already opened so Update / Update and Print stay enabled
  useEffect(() => {
    if (isEditMode) setHasOpenedPaymentModal(true);
  }, [isEditMode]);

  const [pointsError, setPointsError] = useState('');
  const [couponError, setCouponError] = useState('');
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [couponValidationData, setCouponValidationData] = useState(null);
  const [creditNoteError, setCreditNoteError] = useState('');
  const [isValidatingCreditNote, setIsValidatingCreditNote] = useState(false);

  useEffect(() => {
    if (companies.length > 0 && !customer.SelectCompany && !isEditMode) {
      const defaultCompany = primaryCompanyId
        ? companies.find(c => String(c.Id) === primaryCompanyId || String(c.CompanyID) === primaryCompanyId)
        : null;
      const companyToUse = defaultCompany || companies[0];
      setCustomer(f => ({
        ...f,
        SelectCompany: companyToUse.Id,
        State: companyToUse.State || ''
      }));
    }
  }, [companies, isEditMode, primaryCompanyId]);

  // Function to format due amount display based on value
  const [headerRedeemPoints, setHeaderRedeemPoints] = useState<number | null>(null);
  const [headerRedeemPointsLoading, setHeaderRedeemPointsLoading] = useState(false);

  useEffect(() => {
    const codeRaw = (customer as any)?.customerCode;
    const code = Number(codeRaw);
    if (!codeRaw || !Number.isFinite(code) || code <= 0) {
      setHeaderRedeemPoints(null);
      return;
    }
    let alive = true;
    setHeaderRedeemPointsLoading(true);
    fetchCustomerPoints(code)
      .then((pts) => { if (alive) setHeaderRedeemPoints(Number(pts) || 0); })
      .catch(() => { if (alive) setHeaderRedeemPoints(null); })
      .finally(() => { if (alive) setHeaderRedeemPointsLoading(false); });
    return () => { alive = false; };
  }, [customer.customerCode]);

  const formatDueAmount = (totalDue: number | null) => {
    if (totalDue === null || totalDue === undefined) {
      return { text: 'Due: Not Available', clickable: false };
    }

    const absValue = Math.abs(totalDue);
    const formattedAmount = `Rs. ${absValue.toFixed(2)}`;

    if (totalDue < 0) {
      return { text: `We need to pay: ${formattedAmount}`, clickable: true };
    } else if (totalDue === 0) {
      return { text: `Due: ${formattedAmount}`, clickable: true };
    } else {
      return { text: `Due: ${formattedAmount}`, clickable: true };
    }
  };

  // Function to handle navigation to Customer Ledger
  const handleDueAmountClick = () => {
    if (customerTotalDue !== null && customer.Name) {
      // Navigate to Customer Ledger page with customer name pre-populated
      navigate('/reports/customer-ledger', {
        state: {
          customerName: customer.Name,
          searchTerm: customer.Name
        }
      });
    }
  };

  // On companies list load, sync State with selected company or set both if not set (only in create mode)
  useEffect(() => {
    if (companies.length > 0 && !isEditMode) {
      if (customer.SelectCompany) {
        const selectedCompany = companies.find(c => String(c.Id) === String(customer.SelectCompany));
        if (selectedCompany && selectedCompany.State) {
          setCustomer(f => ({ ...f, State: selectedCompany.State }));
        }
      } else {
        const defaultCompany = primaryCompanyId
          ? companies.find(c => String(c.Id) === primaryCompanyId || String(c.CompanyID) === primaryCompanyId)
          : null;
        const companyToUse = defaultCompany || companies[0];
        setCustomer(f => ({ ...f, SelectCompany: companyToUse.Id, State: companyToUse.State || '' }));
      }
    }
  }, [companies, isEditMode, primaryCompanyId]);

  // On company change, always update State to match selected company (only in create mode)
  useEffect(() => {
    if (customer.SelectCompany && companies.length > 0 && !isEditMode) {
      const company = companies.find(c => String(c.Id) === String(customer.SelectCompany));
      if (company && company.State && customer.State !== company.State) {
        setCustomer(f => ({ ...f, State: company.State }));
      }
    }
  }, [customer.SelectCompany, companies, isEditMode]);

  // Set default company when companies are loaded and SelectCompany is not set
  useEffect(() => {
    if (companies.length > 0 && !form.SelectCompany) {
      const defaultCompany = primaryCompanyId
        ? companies.find(c => String(c.Id) === primaryCompanyId || String(c.CompanyID) === primaryCompanyId)
        : null;
      const companyToUse = defaultCompany || companies[0];
      setForm(f => ({ ...f, SelectCompany: companyToUse.Id }));
    }
  }, [companies, primaryCompanyId]);

  // Add this handler above the table rendering in the component
  const handleDelQtyChange = (index: number, value: string) => {
    // Handle empty value - default to 1
    const processedValue = (value === '' || value === null || value === undefined) ? '1' : value;
    setProducts(prev =>
      prev.map((prod, i) =>
        i === index ? { ...prod, DelQty: Number(processedValue) } : prod
      )
    );
  };

  // Handler for Tab/Enter key navigation in product table
  const handleFieldKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number, currentField: string) => {
    if (e.key === 'Tab' || e.key === 'Enter') {
      e.preventDefault(); // Prevent default Tab/Enter behavior

      // Get the raw value that was typed
      const rawKey = `${currentField}-${index}`;
      const value = rawInputValues[rawKey] !== undefined ? rawInputValues[rawKey] : e.currentTarget.value;

      // Special handling for UnitPrice validation
      if (currentField === 'UnitPrice') {
        const currentProduct = products[index];
        const originalUnitPrice = parseFloat(currentProduct.OriginalUnitPrice || '0') || 0;
        const enteredUnitPrice = parseFloat(value) || 0;

        if (enteredUnitPrice > 0 && originalUnitPrice > 0 && enteredUnitPrice < originalUnitPrice) {
          // Show validation popup
          setUnitPriceValidationData({
            index: index,
            enteredValue: value,
            originalValue: currentProduct.OriginalUnitPrice || '0.00'
          });
          setShowUnitPriceValidation(true);
          return; // Don't proceed with navigation until user confirms/cancels
        }
      }

      // Special handling for CostPrice validation
      if (currentField === 'CostPrice') {
        const currentProduct = products[index];
        const originalCostPrice = parseFloat(currentProduct.OriginalCostPrice || '0') || 0;
        const enteredCostPrice = parseFloat(value) || 0;

        console.log('Cost Price Tab/Enter Validation:', {
          originalCostPrice,
          enteredCostPrice,
          product: currentProduct,
          willShowPopup: enteredCostPrice > 0 && originalCostPrice > 0 && enteredCostPrice < originalCostPrice
        });

        if (enteredCostPrice > 0 && originalCostPrice > 0 && enteredCostPrice < originalCostPrice) {
          // Show validation popup
          setCostPriceValidationData({
            index: index,
            enteredValue: value,
            originalValue: currentProduct.OriginalCostPrice || '0.00'
          });
          setShowCostPriceValidation(true);
          return; // Don't proceed with navigation until user confirms/cancels
        }
      }

      // Privilege guards: do not apply price/discount on Tab/Enter without the required privilege
      const priceFields = ['MRP', 'UnitPrice', 'OfferPrice'];
      const discountFields = ['DiscountPer', 'Discount'];
      if (priceFields.includes(currentField) && !canEditPrice) return;
      if (discountFields.includes(currentField) && !canEditDiscount) return;

      // Trigger recalculation with the current value
      if (currentField === 'DelQty') {
        handleDelQtyChange(index, value);
      } else if (currentField === 'CostPrice') {
        // Update CostPrice directly - handle empty value
        const processedValue = (value === '' || value === null || value === undefined) ? '0.00' : value;
        setProducts(prev => prev.map((prod, i) =>
          i === index ? { ...prod, CostPrice: processedValue } : prod
        ));
      } else {
        handleProductFieldChange(index, currentField, value);
      }

      // Clear the raw input value after recalculation
      setRawInputValues(prev => {
        const newVals = { ...prev };
        delete newVals[rawKey];
        return newVals;
      });

      // Define the field order for navigation
      const fieldOrder = ['MRP', 'UnitPrice', 'Qty', 'DiscountPer', 'Discount', 'CostPrice', 'DelQty', 'OfferPrice'];
      const currentFieldIndex = fieldOrder.indexOf(currentField);

      // Determine the next field
      let nextField: string | null = null;
      let nextRowIndex = index;

      if (currentFieldIndex < fieldOrder.length - 1) {
        // Move to next field in the same row
        nextField = fieldOrder[currentFieldIndex + 1];
      } else if (index < products.length - 1) {
        // Move to first field of next row
        nextField = fieldOrder[0];
        nextRowIndex = index + 1;
      }

      // Move focus to the next field after a short delay to ensure recalculation completes
      if (nextField) {
        setTimeout(() => {
          const nextInput = document.querySelector(
            `input[data-field="${nextField}"][data-index="${nextRowIndex}"]`
          ) as HTMLInputElement;
          if (nextInput) {
            nextInput.focus();
            nextInput.select();
          }
        }, 50); // Small delay to ensure state updates complete
      }
    }
  };

  // Helper function to recalculate all derived fields (GST, Taxable Value, Cess, Total)
  // This ensures consistency across all field changes
  const recalculateProductFields = (product: any, changedField?: string, changedValue?: string) => {
    const updated = { ...product };

    // Update the changed field if provided
    if (changedField && changedValue !== undefined) {
      updated[changedField] = changedValue;
      if (changedField === 'Qty') {
        // Update DelQty to match the new quantity
        updated.DelQty = parseFloat(changedValue) || 1;
      }
    }

    // Get current values for calculation
    const mrp = parseFloat(updated.MRP) || 0;
    const qty = parseFloat(updated.Qty) || 1;
    const gstPercent = parseFloat(updated.GST) || 0;
    const cessPercent = parseFloat(updated.CessPer) || 0;

    // Recalculate based on what changed
    if (changedField === 'MRP') {
      // When MRP is changed and Offer price is not changed => Calculate discount and Discount%
      // Keep OfferPrice unchanged, recalculate discount and discount%
      const offerPrice = parseFloat(updated.OfferPrice) || 0;
      const displayDiscountPer = mrp > 0 ? 100 - (offerPrice * 100 / mrp) : 0;
      const displayDiscount = (mrp - offerPrice) * qty;
      updated.DiscountPer = displayDiscountPer.toFixed(2);
      updated.Discount = displayDiscount.toFixed(2);

      console.log(`MRP changed: Keeping OfferPrice unchanged at ${offerPrice.toFixed(2)}, new discount%: ${displayDiscountPer.toFixed(2)}`);
    } else if (changedField === 'OfferPrice') {
      // When OfferPrice is changed, recalculate discount and Discount%
      const offerPrice = parseFloat(changedValue || '0') || 0;
      const displayDiscountPer = mrp > 0 ? 100 - (offerPrice * 100 / mrp) : 0;
      const displayDiscount = (mrp - offerPrice) * qty;
      updated.DiscountPer = displayDiscountPer.toFixed(2);
      updated.Discount = displayDiscount.toFixed(2);
    } else if (changedField === 'DiscountPer') {
      // When DiscountPer is changed, calculate Offer Price and Discount
      const discountPer = parseFloat(changedValue || '0') || 0;
      const offerPrice = mrp * (1 - discountPer / 100);
      updated.OfferPrice = offerPrice.toFixed(2);
      const displayDiscount = (mrp - offerPrice) * qty;
      updated.Discount = displayDiscount.toFixed(2);
    } else if (changedField === 'Discount') {
      // When Discount is changed, calculate OfferPrice and DiscountPer
      const discount = parseFloat(changedValue || '0') || 0;
      const offerPrice = qty > 0 ? mrp - (discount / qty) : mrp;
      updated.OfferPrice = offerPrice.toFixed(2);
      const displayDiscountPer = mrp > 0 ? 100 - (offerPrice * 100 / mrp) : 0;
      updated.DiscountPer = displayDiscountPer.toFixed(2);
    } else if (changedField === 'TaxableValue') {
      // When Taxable Value is changed, recalculate OfferPrice, then discount and Discount%
      const taxableValue = parseFloat(changedValue || '0') || 0;
      const offerPrice = (taxableValue * (1 + (gstPercent + cessPercent) / 100)) / qty;
      updated.OfferPrice = offerPrice.toFixed(2);
      const displayDiscountPer = mrp > 0 ? 100 - (offerPrice * 100 / mrp) : 0;
      const displayDiscount = (mrp - offerPrice) * qty;
      updated.DiscountPer = displayDiscountPer.toFixed(2);
      updated.Discount = displayDiscount.toFixed(2);
    } else if (changedField === 'Qty') {
      // When Quantity is changed, KEEP OfferPrice unchanged, recalculate discount
      const offerPrice = parseFloat(updated.OfferPrice) || 0;
      const displayDiscount = (mrp - offerPrice) * qty;
      updated.Discount = displayDiscount.toFixed(2);
      const displayDiscountPer = mrp > 0 ? 100 - (offerPrice * 100 / mrp) : 0;
      updated.DiscountPer = displayDiscountPer.toFixed(2);
    }

    // ALWAYS recalculate Taxable Value, GST Amount, Cess Amount, and Total
    // This ensures these fields are ALWAYS up to date regardless of which field changed
    const offerPrice = parseFloat(updated.OfferPrice) || 0;

    // Calculate Taxable Value (unless it was just edited directly)
    let taxableValue;
    if (changedField === 'TaxableValue') {
      taxableValue = parseFloat(changedValue || '0') || 0;
    } else {
      // Formula: Taxable Value = OfferPrice * Qty / (1 + (GST% + Cess%) / 100)
      taxableValue = (offerPrice * qty) / (1 + (gstPercent + cessPercent) / 100);
    }

    // Calculate GST Amount = Taxable Value * GST% / 100
    const gstAmount = taxableValue * gstPercent / 100;

    // Calculate Cess Amount = Taxable Value * CessPercent / 100
    const cessAmount = taxableValue * cessPercent / 100;

    // Calculate Total = Taxable Value + GST Amount + Cess Amount
    const total = taxableValue + gstAmount + cessAmount;

    // Update all calculated fields - these ALWAYS get recalculated
    updated.TaxableValue = String(taxableValue.toFixed(2));
    updated.GSTAmount = String(gstAmount.toFixed(2));
    updated.CessAmount = String(cessAmount.toFixed(2));
    updated.Total = String(total.toFixed(2));

    // Update Unit Price for backward compatibility
    const totalTax = gstPercent + cessPercent;
    const unitPrice = totalTax > 0 ? mrp / (1 + totalTax / 100) : mrp;
    updated.UnitPrice = unitPrice.toFixed(2);

    // Update legacy SGST/CGST/Cess fields: split GST amount so SGST and CGST are equal (matches POST and Cess rounding)
    const gstAmountNum = parseFloat(String(gstAmount)) || 0;
    const half = Math.floor((gstAmountNum / 2) * 100) / 100;
    updated.SGST = half;
    updated.CGST = half;
    updated.Cess = cessAmount;

    // Debug log to track recalculations
    console.log(`✓ Recalculation triggered by '${changedField || 'initialization'}':`, {
      MRP: mrp,
      Qty: qty,
      OfferPrice: offerPrice,
      'GST%': gstPercent,
      'Cess%': cessPercent,
      '→ TaxableValue': updated.TaxableValue,
      '→ GSTAmount': updated.GSTAmount,
      '→ CessAmount': updated.CessAmount,
      '→ Total': updated.Total
    });

    return updated;
  };

  // Handler for editable product fields
  const handleProductFieldChange = (index: number, field: string, value: string) => {
    // Privilege guards: do not apply price/discount edits without the required privilege
    const priceFields = ['MRP', 'UnitPrice', 'OfferPrice'];
    const discountFields = ['DiscountPer', 'Discount'];
    if (priceFields.includes(field) && !canEditPrice) return;
    if (discountFields.includes(field) && !canEditDiscount) return;

    // Handle empty values - set to default
    let processedValue = value;
    if (value === '' || value === null || value === undefined) {
      if (field === 'Qty' || field === 'DelQty') {
        processedValue = '1'; // Default quantity to 1
      } else {
        processedValue = '0.00'; // Default other numeric fields to 0.00
      }
    }

    // Special handling for OfferPrice validation
    if (field === 'OfferPrice') {
      const currentProduct = products[index];
      const offerPrice = parseFloat(processedValue) || 0;
      const mrp = parseFloat(currentProduct.MRP) || 0;

      // Validate: Check if Offer Price exceeds MRP
      if (offerPrice > mrp) {
        // Show error toast
        toast({
          title: 'Invalid Offer Price',
          description: `Offer Price (${offerPrice.toFixed(2)}) cannot exceed MRP (${mrp.toFixed(2)})`,
          variant: 'destructive',
        });
        // Force re-render by setting products with a fresh copy to reset the input
        setProducts([...products]);
        return;
      }
    }

    // Special handling for Qty validation
    if (field === 'Qty') {
      const currentProduct = products[index];
      const qty = parseFloat(processedValue) || 1;
      const isTraceable = currentProduct.isTraceable || false;
      const available = Number(currentProduct.Available) || 0;

      // Check for quantity exceeded validation for traceable products
      if (isTraceable && qty > available) {
        // Check if quantity popup is required
        if (showQtyPopup) {
          // Show error toast for quantity exceeded
          toast({
            title: 'Quantity Exceeded',
            description: `You can't sell more than the available quantity: ${available}`,
            variant: 'destructive',
          });
          // Force re-render by setting products with a fresh copy to reset the input
          setProducts([...products]);
          return;
        } else {
          // Auto-correct quantity to available amount without popup
          processedValue = available.toString();
        }
      }
    }

    // Use the helper function to recalculate all fields
    setProducts(prev => prev.map((prod, i) => {
      if (i !== index) return prod;

      // Handle Extra1/Extra2 fields separately (no recalculation needed)
      if (field === 'Extra1' || field === 'Extra2') {
        return { ...prod, [field]: processedValue };
      }

      // Use the recalculation helper for all other fields
      return recalculateProductFields(prod, field, processedValue);
    }));
  };

  // State for location change confirmation
  const [showLocationConfirm, setShowLocationConfirm] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<string | null>(null);
  const [prevLocation, setPrevLocation] = useState(customer.Location);

  // State for company change confirmation
  const [showCompanyConfirm, setShowCompanyConfirm] = useState(false);
  const [pendingCompany, setPendingCompany] = useState<string | null>(null);
  const [prevCompany, setPrevCompany] = useState(customer.SelectCompany);

  // Set default Invoice Date to today if not already set
  useEffect(() => {
    if (!form.InvoiceDate) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      setForm(f => ({ ...f, InvoiceDate: `${yyyy}-${mm}-${dd}` }));
    }
  }, []);

  // Populate due date from context in edit mode (invoice date comes from API CreatedOn in fetchAndLogInvoiceDetails)
  useEffect(() => {
    if (isEditMode) {
      const updates: any = {};

      // Set due date from context (PaidOn from API)
      if (payments.payBefore) {
        updates.PayBefore = payments.payBefore;
      }

      // Do not set InvoiceDate here in edit mode — it is set from API CreatedOn when InvoiceFullDetails loads

      if (Object.keys(updates).length > 0) {
        setForm(f => ({ ...f, ...updates }));
      }
    }
  }, [isEditMode, payments.payBefore]);

  // Determine if selected company is Non-GST (GST !== 1)
  const selectedCompany = companies && companies.find((c: any) => String(c.Id) === String(customer.SelectCompany));
  const isNonGSTCompany = selectedCompany ? selectedCompany.GST !== 1 : false;

  // Add dynamic summary calculations before the return statement
  const summary = products.reduce((acc, prod) => {
    const qty = parseFloat(prod.Qty) || 0;
    const discount = parseFloat(prod.Discount) || 0;
    const total = parseFloat(prod.Total) || 0;
    const rate = parseFloat(prod.MRP) || 0;

    // Calculate GST and CESS amounts consistently with table display
    // Use OfferPrice (not MRP) as that's what the table uses
    const offerPrice = parseFloat(prod.OfferPrice) || 0;
    const gstPercent = parseFloat(prod.GST) || 0;
    const cessPercent = parseFloat(prod.CessPer) || 0;

    // Correct calculation as per specification:
    // Taxable Value = OfferPrice * qty / (1 + (GstPercent + CessPercent) / 100)
    const taxableValue = (offerPrice * qty) / (1 + (gstPercent + cessPercent) / 100);

    // GST Amount = Taxable Value * GstPercent / 100
    const gstAmount = taxableValue * gstPercent / 100;

    // Cess Amount = Taxable Value * CessPercent / 100
    const cessAmount = taxableValue * cessPercent / 100;

    // Total = Taxable Value + GST Amount + Cess Amount

    acc.gst += gstAmount;
    acc.cess += cessAmount;
    acc.qty += qty;
    acc.discount += discount;
    acc.subtotal += total; // show actual line total (after discounts & taxes)
    acc.items += 1;
    return acc;
  }, { gst: 0, cess: 0, qty: 0, discount: 0, subtotal: 0, items: 0 });
  const summaryGstDisplay = isNonGSTCompany ? 0 : summary.gst;
  const summaryCessDisplay = isNonGSTCompany ? 0 : summary.cess;

  const [prevPartner, setPrevPartner] = useState('');
  const [showPartnerConfirm, setShowPartnerConfirm] = useState(false);
  const [pendingPartner, setPendingPartner] = useState<string | null>(null);

  // Treat pointsRedeem as rupees on the payment screen (1 point = ₹1)
  const pointsRedeemRupees = parseFloat(String(payments.pointsRedeem)) || 0;
  const eligiblePoints = parseFloat(String(payments.eligiblePoints)) || 0;
  const roundedEligiblePoints = Math.round(eligiblePoints * 100) / 100;
  const remainingPointsValue = Math.max(0, roundedEligiblePoints - pointsRedeemRupees);
  const formattedEligiblePoints = roundedEligiblePoints.toFixed(2);
  const formattedRemainingPoints = remainingPointsValue.toFixed(2);

  // Validate points redemption
  const isPointsValid = eligiblePoints >= 0 && pointsRedeemRupees <= eligiblePoints;

  // Calculate valid coupon amount (exclude if exceeds maximum allowed)
  const getValidCouponAmount = () => {
    // In edit mode, if there's a coupon amount, include it even without validation data
    if (isEditMode && editingInvoiceId && payments.couponAmount) {
      return parseFloat(payments.couponAmount) || 0;
    }

    // For create mode, require validation data
    if (!couponValidationData || !payments.couponAmount) return 0;
    const enteredAmount = parseFloat(payments.couponAmount) || 0;
    const maxAmount = parseFloat(couponValidationData.Amount) || 0;
    return enteredAmount <= maxAmount ? enteredAmount : 0;
  };

  // Calculate total payment from all payment modes (rounded)
  // totalPayment = cash + card + upi(wallet) + bank + points + coupon + cheque + credit note + other
  const totalPaid =
    roundPayment(payments.cashPayment) +
    roundPayment(payments.cardPayment) +
    roundPayment(payments.walletAmount) +
    roundPayment(payments.bankTransAmount) +
    roundPayment(payments.chequeAmount) +
    roundPayment(getValidCouponAmount()) +
    roundPayment(payments.creditNoteAmount) +
    roundPayment(pointsRedeemRupees) +
    roundPayment(payments.otherAmount);
  // Use rounded total (same as displayed in payment popup) so balance is 0 when Total and Paid match
  const roundedInvoiceTotal = Math.round(invoiceTotal);
  const balanceDue = Math.round((roundedInvoiceTotal - totalPaid) * 100) / 100;

  // // When the payment popup opens, set splitPaymentEnabled to true (but allow user to uncheck)
  // useEffect(() => {
  //   if (showPaymentModal) {
  //     setSplitPaymentEnabled(true);

  //     // In edit mode, detect which payment method was used and set accordingly
  //     if (isEditMode && editingInvoiceId) {
  //       const paymentMethods = [
  //         { key: 'cashPayment', value: 'Cash' },
  //         { key: 'cardPayment', value: 'Card' },
  //         { key: 'walletAmount', value: 'Wallets' },
  //         { key: 'chequeAmount', value: 'Cheque' },
  //         { key: 'couponAmount', value: 'Coupon' },
  //         { key: 'creditNoteAmount', value: 'Credit Note' },
  //         { key: 'pointsRedeem', value: 'Points' }
  //       ];

  //       // Find the first payment method with a value > 0
  //       const usedPaymentMethod = paymentMethods.find(method => 
  //         parseFloat(payments[method.key]) > 0
  //       );

  //       if (usedPaymentMethod) {
  //         setSelectedPaymentMethod(usedPaymentMethod.value);
  //       } else {
  //         setSelectedPaymentMethod('Cash'); // Default if no payment found
  //       }
  //     } else {
  //       setSelectedPaymentMethod('Cash'); // Default for new invoices
  //     }

  //     setForm(f => ({ ...f, Redeem: 0 }));
  //   }
  // }, [showPaymentModal, isEditMode, editingInvoiceId, payments]);
  // When the payment popup opens, set splitPaymentEnabled to false (but allow user to check)
  useEffect(() => {
    if (showPaymentModal) {
      setPayments(p => ({ ...p, splitPaymentEnabled: false }));

      if (isEditMode && editingInvoiceId) {
        console.log('=== PAYMENT MODAL OPENED IN EDIT MODE ===');
        console.log('Invoice ID:', editingInvoiceId);
        console.log('Selected payment method:', selectedPaymentMethod);

        // Ensure payment values are properly formatted as numbers (only if needed)
        const needsFormatting = Object.values(payments).some(value =>
          typeof value === 'string' && value !== '' && isNaN(parseFloat(value))
        );

        if (needsFormatting) {
          console.log('Formatting payment values...');
          const formattedPayments = {
            cashPayment: payments.cashPayment ? roundPayment(payments.cashPayment).toFixed(2) : '',
            cardPayment: payments.cardPayment ? roundPayment(payments.cardPayment).toFixed(2) : '',
            walletAmount: payments.walletAmount ? roundPayment(payments.walletAmount).toFixed(2) : '',
            chequeAmount: payments.chequeAmount ? roundPayment(payments.chequeAmount).toFixed(2) : '',
            couponAmount: payments.couponAmount ? roundPayment(payments.couponAmount).toFixed(2) : '',
            creditNoteAmount: payments.creditNoteAmount ? roundPayment(payments.creditNoteAmount).toFixed(2) : '',
            pointsRedeem: payments.pointsRedeem ? roundPayment(payments.pointsRedeem).toFixed(2) : '',
          };

          // Update payments with properly formatted values
          setPayments(prev => ({
            ...prev,
            ...formattedPayments
          }));
        }

        const paymentMethods = [
          { key: 'cashPayment', value: 'Cash' },
          { key: 'cardPayment', value: 'Card' }, // Map cardPayment to Card
          { key: 'walletAmount', value: 'Wallets' },
          { key: 'chequeAmount', value: 'Cheque' },
          { key: 'couponAmount', value: 'Coupon' },
          { key: 'creditNoteAmount', value: 'Credit Note' },
          { key: 'pointsRedeem', value: 'Points' }
        ];

        // Find the first payment method with a value > 0
        const usedPaymentMethod = paymentMethods.find(
          method => parseFloat(payments[method.key]) > 0
        );

        if (usedPaymentMethod) {
          setSelectedPaymentMethod(usedPaymentMethod.value);
          console.log('Selected payment method:', usedPaymentMethod.value);
        } else {
          setSelectedPaymentMethod(getDefaultPaymentMethod());
          console.log('No payment method found, defaulting to setting');
        }

        console.log('=== END PAYMENT MODAL DEBUG ===');
      } else {
        setSelectedPaymentMethod(getDefaultPaymentMethod()); // Default for new invoices from settings
      }

      setForm(f => ({ ...f, Redeem: 0 }));
    }
    // Removed 'payments' from dependency array to prevent re-trigger on typing
  }, [showPaymentModal, isEditMode, editingInvoiceId]);




  // Add after other useState declarations, near the top of SalesInvoice
  const [discountPercent, setDiscountPercent] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewedProducts, setPreviewedProducts] = useState<any[]>([]);

  const combinedTotal = products.reduce((sum, p) => {
    const offer = parseFloat(p.OfferPrice) || 0;
    const qty = parseFloat(p.Qty) || 0;
    return sum + (offer * qty);
  }, 0);

  const handlePercentChange = (val: string) => {
    setDiscountPercent(val);
    const percent = parseFloat(val) || 0;
    const combinedTotal = products.reduce((sum, p) => {
      const offer = parseFloat(p.OfferPrice) || 0;
      const qty = parseFloat(p.Qty) || 0;
      return sum + (offer * qty);
    }, 0);
    const amount = (combinedTotal * percent) / 100;
    setDiscountAmount(amount ? amount.toFixed(2) : '');
  };

  const handleAmountChange = (val: string) => {
    setDiscountAmount(val);
    const amount = parseFloat(val) || 0;
    const combinedTotal = products.reduce((sum, p) => {
      const offer = parseFloat(p.OfferPrice) || 0;
      const qty = parseFloat(p.Qty) || 0;
      return sum + (offer * qty);
    }, 0);
    const percent = combinedTotal > 0 ? (amount / combinedTotal) * 100 : 0;
    setDiscountPercent(percent ? percent.toFixed(2) : '');
  };

  const previewDiscount = (percent: number, amount: number) => {
    setIsPreviewing(true);
    if (products.length === 0) {
      setPreviewedProducts([]);
      return;
    }
    let previewed = products.map(p => {
      const offer = parseFloat(p.OfferPrice) || 0;
      const qty = parseFloat(p.Qty) || 0;
      const originalTotal = offer * qty;
      let share = 0;
      if (amount > 0) {
        const combinedTotal = products.reduce((sum, p) => {
          const offer = parseFloat(p.OfferPrice) || 0;
          const qty = parseFloat(p.Qty) || 0;
          return sum + (offer * qty);
        }, 0);
        share = combinedTotal > 0 ? (originalTotal / combinedTotal) * amount : 0;
      } else if (percent > 0) {
        share = (originalTotal * percent) / 100;
      }
      const previewOffer = offer - (share / (qty || 1));
      return {
        ...p,
        previewDiscount: share,
        previewOfferPrice: previewOffer,
        previewTotal: previewOffer * qty,
      };
    });
    setPreviewedProducts(previewed);
  };

  const handleAdjust = () => {
    if (isDiscountActive) return;
    const percent = parseFloat(discountPercent) || 0;
    const amount = parseFloat(discountAmount) || 0;
    if (products.length === 0 || (!percent && !amount)) return;
    // When user entered a %, prefer percent (add percentage points). Otherwise the % field
    // auto-fills the amount from current subtotal, which would add less than the intended % of MRP.
    const usePercent = percent > 0;
    // Backup current state so Cancel can restore
    setOriginalProducts(products.map(p => ({ ...p })));
    setProducts(prev => prev.map((p) => {
      const mrp = parseFloat(p.MRP) || 0;
      const qty = parseFloat(p.Qty) || 1;
      const itemTotal = mrp * qty;
      // Current effective discount from this row (from OfferPrice/MRP or stored DiscountPer)
      const offerPrice = parseFloat(p.OfferPrice) || mrp;
      const existingDiscountPer = mrp > 0 ? Math.max(0, 100 - (offerPrice * 100 / mrp)) : (parseFloat(p.DiscountPer) || 0);
      const existingDiscount = parseFloat(p.Discount) || (mrp > 0 ? (mrp - offerPrice) * qty : 0);
      let newDiscountPer = existingDiscountPer;
      let newDiscount = existingDiscount;
      if (usePercent) {
        // Add percentage points to existing % (e.g. 10% + 10% = 20% of MRP)
        newDiscountPer = Math.min(100, existingDiscountPer + percent);
        newDiscount = (newDiscountPer / 100) * itemTotal;
      } else if (amount > 0) {
        // Add flat amount: distribute proportionally by MRP*Qty share of total
        const combinedTotal = prev.reduce((sum, x) => sum + (parseFloat(x.MRP) || 0) * (parseFloat(x.Qty) || 0), 0);
        const share = combinedTotal > 0 ? (itemTotal / combinedTotal) * amount : 0;
        newDiscount = existingDiscount + share;
        newDiscountPer = itemTotal > 0 ? (newDiscount / itemTotal) * 100 : 0;
      }
      const newOffer = itemTotal > 0 ? mrp - (newDiscount / qty) : mrp;
      const updatedProduct = {
        ...p,
        Discount: newDiscount.toFixed(2),
        DiscountPer: newDiscountPer.toFixed(2),
        OfferPrice: Math.max(0, newOffer).toFixed(2),
      };
      // Recalculate derived fields (TaxableValue, GSTAmount, CessAmount, Total) to match table edits
      return recalculateProductFields(updatedProduct);
    }));
    setIsDiscountActive(true);
  };

  const handleCancel = () => {
    setProducts(originalProducts.map(p => ({ ...p })));
    setDiscountPercent('');
    setDiscountAmount('');
    setIsDiscountActive(false);
  };

  const handlePaymentConfirm = () => {
    // Validate points before confirming payment
    if (selectedPaymentMethod === 'Points') {
      const pointsValue = parseFloat(payments.pointsRedeem) || 0; // points
      const eligiblePoints = parseFloat(payments.eligiblePoints) || 0; // points

      if (eligiblePoints <= 0) {
        setPointsError('No eligible points available');
        return;
      }

      if (pointsValue > eligiblePoints) {
        setPointsError('Points to redeem cannot exceed eligible points');
        return;
      }
    }

    // Clear any points error
    setPointsError('');

    const total = roundedInvoiceTotal;
    const paid = totalPaid;
    const balance = balanceDue;

    // Show green toast with payment details (uses rounded total to match popup)
    toast({
      title: "Payment Confirmed",
      description: `Total: ₹${total.toFixed(2)} | Paid: ₹${paid.toFixed(2)} | Balance: ₹${balance.toFixed(2)}`,
      variant: "default",
      className: "bg-green-500 text-white border-green-600",
    });

    // Close the payment modal
    setShowPaymentModal(false);
  };

  // When products are first loaded or changed:
  const originalProductsRef = useRef<any[]>([]);

  useEffect(() => {
    // Only set backup if it's empty (first load or after product list changes)
    if (originalProductsRef.current.length === 0 && products.length > 0) {
      originalProductsRef.current = products.map(p => ({ ...p }));
    }
  }, [products]);

  const [originalProducts, setOriginalProducts] = useState<any[]>([]);

  useEffect(() => {
    // Only set backup if products are added and backup is empty
    if (products.length > 0 && originalProducts.length === 0) {
      setOriginalProducts(products.map(p => ({ ...p })));
    }
    // If products are cleared, also clear the backup
    if (products.length === 0 && originalProducts.length > 0) {
      setOriginalProducts([]);
    }
  }, [products]);

  // Reset adjust fields when all items are removed so they don't show stale %/Discount
  useEffect(() => {
    if (products.length === 0) {
      setDiscountPercent('');
      setDiscountAmount('');
      setIsDiscountActive(false);
    }
  }, [products.length]);

  const [originalProductsMap, setOriginalProductsMap] = useState({});

  useEffect(() => {
    if (Object.keys(originalProductsMap).length === 0) {
      const map = {};
      products.forEach(p => {
        map[p.Barcode || p.id || p.Title] = { ...p }; // Use a unique key
      });
      setOriginalProductsMap(map);
    }
  }, [products]);

  const [isNew, setIsNew] = useState(false);

  // isUnknown is now managed by SalesInvoiceContext

  // Add effect to auto-fill Name and Company when Unknown is checked
  useEffect(() => {
    if (isUnknown) {
      setCustomer(prev => ({
        ...prev,
        Name: 'UNKNOWN',
        Company: 'UNKNOWN',
      }));
    }
  }, [isUnknown, setCustomer]);

  const [isDiscountActive, setIsDiscountActive] = useState(false);

  const displayProducts = isPreviewing && previewedProducts.length > 0 ? previewedProducts : products;
  const displayTotal = isPreviewing && previewedProducts.length > 0
    ? previewedProducts.reduce((sum, p) => sum + (parseFloat(p.previewTotal) || 0), 0)
    : invoiceTotal;

  // Add state for notes popup and notes text
  const [showNotesPopup, setShowNotesPopup] = useState(false);
  const [notesText, setNotesText] = useState('');

  // Initialize notesText with existing notes when popup opens
  useEffect(() => {
    if (showNotesPopup) {
      setNotesText(notes || '');
    }
  }, [showNotesPopup, notes]);

  const navigate = useNavigate();

  // Add after other useState declarations, near the top of SalesInvoice
  const [showStateChangeConfirm, setShowStateChangeConfirm] = useState(false);
  const [pendingState, setPendingState] = useState<string | null>(null);
  const [prevCustomerState, setPrevCustomerState] = useState(customer.State);

  // Update prevCustomerState whenever customer.State changes (but not due to popup)
  useEffect(() => {
    setPrevCustomerState(customer.State);
  }, [customer.State]);

  // Add state for qty modal error
  const [qtyModalError, setQtyModalError] = useState('');

  // Add this before the return statement in the component
  const companyState = getStateFromCompany(customer.SelectCompany);
  const isIGST = customer.State && companyState && customer.State !== companyState;
  const [qtyModalMRP, setQtyModalMRP] = useState('');
  const [qtyModalUnitPrice, setQtyModalUnitPrice] = useState('');

  // Split Payment checkbox state is now managed in the context

  // Function to fetch customer points when Points payment method is selected
  const fetchPointsWhenSelected = async () => {
    if (customer.customerCode) {
      try {
        const balancePoints = await fetchCustomerPoints(customer.customerCode);
        console.log('Fetched customer points when Points selected:', balancePoints);

        // Check if we're in edit mode and already have a valid points value
        const currentPointsRedeem = parseFloat(payments.pointsRedeem) || 0;
        const isEditMode = editingInvoiceId !== null;

        console.log('=== POINTS DEBUG ===');
        console.log('isEditMode:', isEditMode);
        console.log('editingInvoiceId:', editingInvoiceId);
        console.log('currentPointsRedeem:', currentPointsRedeem);
        console.log('balancePoints:', balancePoints);
        console.log('currentPointsRedeem > 0:', currentPointsRedeem > 0);
        console.log('currentPointsRedeem <= balancePoints:', currentPointsRedeem <= balancePoints);
        console.log('========================');

        // In edit mode, preserve existing points value if it's valid
        if (isEditMode && currentPointsRedeem > 0) {
          console.log('In edit mode, preserving existing points value:', currentPointsRedeem);
          setPayments(p => ({
            ...p,
            eligiblePoints: balancePoints.toString(),
            // Keep the existing pointsRedeem value
            pointsRedeem: p.pointsRedeem
          }));
          return;
        }

        // Handle points logic based on split payment and invoice total (₹)
        const invoiceTotalValue = invoiceTotal;
        let pointsRedeemValue = '0';

        if (balancePoints <= 0) {
          // No eligible points, set both to 0 and make read-only
          pointsRedeemValue = '0';
        } else if (!payments.splitPaymentEnabled) {
          // Split payment is unchecked
          if (balancePoints >= invoiceTotalValue) {
            // Eligible points are enough, allow Points payment type
            pointsRedeemValue = invoiceTotalValue.toString();
          } else {
            // Eligible points are not enough, make field read-only
            pointsRedeemValue = '0';
          }
        } else {
          // Split payment is checked, allow manual entry
          pointsRedeemValue = '0';
        }

        // Update payments state with both eligible points and points redeem in one call
        setPayments(p => ({
          ...p,
          eligiblePoints: balancePoints.toString(),
          pointsRedeem: pointsRedeemValue
        }));

        console.log('Updated payments state with eligiblePoints:', balancePoints.toString(), 'pointsRedeem:', pointsRedeemValue);
      } catch (error) {
        console.error('Error fetching customer points:', error);
      }
    } else {
      // No customer code, set both to 0
      setPayments(p => ({
        ...p,
        eligiblePoints: '0',
        pointsRedeem: '0'
      }));
    }
  };

  // Handle split payment toggle and update points logic
  useEffect(() => {
    const eligiblePoints = parseFloat(payments.eligiblePoints) || 0;
    const invoiceTotalValue = invoiceTotal;

    // Only update points logic if we're not in edit mode or if points haven't been loaded from API yet
    const currentPointsRedeem = parseFloat(payments.pointsRedeem) || 0;
    const isEditMode = editingInvoiceId !== null;

    // Skip this logic entirely if we're in edit mode and already have a valid points value
    if (isEditMode && currentPointsRedeem > 0) {
      console.log('In edit mode with valid points value, skipping auto-update logic');
      return;
    }

    if (selectedPaymentMethod === 'Points') {
      if (eligiblePoints <= 0) {
        // No eligible points, set to 0 and make read-only
        setPayments(p => ({
          ...p,
          pointsRedeem: '0'
        }));
      } else if (!payments.splitPaymentEnabled) {
        // Split payment is unchecked and Points is selected
        if (eligiblePoints >= invoiceTotalValue) {
          // Eligible points are enough, auto-fill with invoice total
          setPayments(p => ({
            ...p,
            pointsRedeem: invoiceTotalValue.toString()
          }));
        } else {
          // Eligible points are not enough, set to 0 and make read-only
          setPayments(p => ({
            ...p,
            pointsRedeem: '0'
          }));
        }
      } else {
        // Split payment is enabled: preserve whatever is already loaded/entered
        // Do not override pointsRedeem here
      }
    }
  }, [payments.splitPaymentEnabled, selectedPaymentMethod, payments.eligiblePoints, invoiceTotal, editingInvoiceId]);

  // Debug: Monitor payments state changes
  useEffect(() => {
    console.log('Payments state changed:', {
      eligiblePoints: payments.eligiblePoints,
      pointsRedeem: payments.pointsRedeem,
      selectedPaymentMethod: selectedPaymentMethod
    });
  }, [payments.eligiblePoints, payments.pointsRedeem, selectedPaymentMethod]);

  // Fetch eligible points when component loads in edit mode with Points payment method
  useEffect(() => {
    if (isEditMode && editingInvoiceId && selectedPaymentMethod === 'Points' && customer.customerCode) {
      console.log('Edit mode: Fetching eligible points for Points payment method');
      fetchPointsWhenSelected();
    }
  }, [isEditMode, editingInvoiceId, selectedPaymentMethod, customer.customerCode]);

  // Clear coupon validation data when payment method changes
  // Modified to preserve coupon validation data when switching payment methods
  useEffect(() => {
    if (selectedPaymentMethod !== 'Coupon') {
      setCouponError('');
      // Don't clear couponValidationData to preserve validation state
      // setCouponValidationData(null); // Commented out to preserve validation
    }
  }, [selectedPaymentMethod]);

  // Add state for product table search
  const [productTableSearch, setProductTableSearch] = useState('');

  // Filter products for table display
  const filteredProducts = products.filter(prod => {
    if (!productTableSearch.trim()) return true;
    const search = productTableSearch.toLowerCase();
    return (
      (prod.Title && prod.Title.toLowerCase().includes(search)) ||
      (prod.Barcode && prod.Barcode.toLowerCase().includes(search)) ||
      (prod.HSN && prod.HSN.toLowerCase().includes(search)) ||
      (prod.MRP && String(prod.MRP).toLowerCase().includes(search)) ||
      (prod.OfferPrice && String(prod.OfferPrice).toLowerCase().includes(search)) ||
      (prod.Category && prod.Category.toLowerCase().includes(search)) ||
      (prod.BrandName && prod.BrandName.toLowerCase().includes(search))
    );
  });

  // Filter representatives for dropdown
  const filteredRepresentatives = representatives.filter(rep => {
    if (!repNameSearchTerm.trim()) return true;
    const search = repNameSearchTerm.toLowerCase();
    return (
      (rep.Representative && rep.Representative.toLowerCase().includes(search))
    );
  });

  // Add useEffect to auto-select State in Ship To popup based on selected company (only in create mode)
  useEffect(() => {
    if (showShipToPopup && customer.SelectCompany && companies.length > 0 && !isEditMode) {
      const company = companies.find(c => String(c.Id) === String(customer.SelectCompany));
      if (company && company.State && (!shipToForm.State || shipToForm.State !== company.State)) {
        setShipToForm(f => ({ ...f, State: company.State }));
      }
    }
    // eslint-disable-next-line
  }, [showShipToPopup, customer.SelectCompany, companies, isEditMode]);

  // Handle click outside to close Rep Name dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.rep-name-dropdown')) {
        setShowRepNameDropdown(false);
      }
    };

    if (showRepNameDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showRepNameDropdown]);

  // Sync repNameSearchTerm with repName when repName is reset or initialized
  useEffect(() => {
    if (repName === '') {
      setRepNameSearchTerm('');
    } else if (repNameSearchTerm === '' && repName !== '') {
      // Initialize search term with current rep name value (for edit mode)
      setRepNameSearchTerm(repName);
    }
  }, [repName, repNameSearchTerm]);

  // Add useEffect to set Name/Company to 'UNKNOWN' in Ship To popup if isUnknown is checked
  useEffect(() => {
    if (showShipToPopup && isUnknown) {
      setShipToForm(prev => ({ ...prev, Name: 'UNKNOWN', Company: 'UNKNOWN' }));
    }
    // eslint-disable-next-line
  }, [showShipToPopup, isUnknown, setShipToForm]);

  // When opening the Ship To popup, initialize shipToForm with current customer values
  useEffect(() => {
    if (showShipToPopup) {
      if (isEditMode && editingInvoiceId) {
        // In edit mode, preserve existing shipping data from API
        console.log('Edit mode: Preserving existing shipping data when Ship to Same is checked');
        console.log('Current shipToForm:', shipToForm);
        // Don't overwrite the existing shipToForm data that was loaded from API
        // The data should already be populated by populateSalesInvoiceContext
      } else {
        // In create mode, always initialize with customer values
        setShipToForm({
          Name: customer.Name || '',
          Company: customer.Company || '',
          Phone: customer.Phone || '',
          Email: customer.Email || '',
          GST: customer.GST || '',
          Address: customer.Address || '',
          State: customer.State || '', // Use customer state instead of company state
        });
      }
    }
    // eslint-disable-next-line
  }, [showShipToPopup, isEditMode, editingInvoiceId]);

  // Clear payment values when creating new invoice (not in edit mode)
  useEffect(() => {
    // Only clear in create mode when component first mounts or when exiting edit mode
    if (!isEditMode && !editingInvoiceId) {
      setPayments(prev => ({
        ...prev,
        cashPayment: '',
        cardPayment: '',
        walletAmount: '',
        chequeAmount: '',
        bankTransAmount: '',
        // Keep coupon and credit note at 0, not empty
        couponAmount: '0',
        creditNoteAmount: '0',
        pointsRedeem: '',
      }));
    }
  }, [isEditMode, editingInvoiceId]);

  // Add this useEffect after payment-related state declarations
  useEffect(() => {
    // Only run this logic in create mode, not in edit mode
    if (!payments.splitPaymentEnabled && showPaymentModal && !isEditMode) {
      // Auto-fill the selected payment method with invoiceTotal (only in create mode)
      setPayments(prev => {
        const totalStr = Math.round(invoiceTotal).toString();
        return {
          ...prev,
          cashPayment: selectedPaymentMethod === 'Cash' ? totalStr : '',
          walletAmount: selectedPaymentMethod === 'Wallets' ? totalStr : '',
          chequeAmount: selectedPaymentMethod === 'Cheque' ? totalStr : '',
          // Keep coupon and credit note amounts independent of split toggle
          couponAmount: prev.couponAmount || '0',
          creditNoteAmount: prev.creditNoteAmount || '0',
          pointsRedeem: selectedPaymentMethod === 'Points' ? '0' : '',
          eligiblePoints: prev.eligiblePoints, // Always preserve eligible points
          cardPayment: selectedPaymentMethod === 'Card' ? totalStr : '', // Map Card to cardPayment for API compatibility
        };
      });
    }
  }, [payments.splitPaymentEnabled, selectedPaymentMethod, invoiceTotal, showPaymentModal, isEditMode]);

  // Helper to get current company state (use customer.SelectCompany to match billing/company for isIGST)
  const getCurrentCompanyState = () => {
    const companyId = customer.SelectCompany || form.SelectCompany;
    if (companyId && companies.length > 0) {
      const company = companies.find(c => String(c.Id) === String(companyId));
      return company?.State || '';
    }
    return '';
  };

  // Function to handle points validation when split payment is unchecked
  const handlePointsValidation = (value: string) => {
    if (value && parseFloat(value) > 0 && eligiblePoints > 0) {
      const enteredPoints = parseFloat(value) || 0;
      if (enteredPoints > eligiblePoints) {
        setPointsError('Points can\'t be more than eligible points value');
        setPayments(p => ({ ...p, pointsRedeem: '0' }));
        return true; // Validation failed
      }
    }
    return false; // Validation passed
  };

  // Function to handle coupon validation
  const handleCouponValidation = async () => {
    if (!payments.couponNo || payments.couponNo.trim() === '') {
      setCouponError('Please enter a coupon code');
      return;
    }

    setIsValidatingCoupon(true);
    setCouponError('');
    setCouponValidationData(null);

    try {
      const response = await validateCoupon(payments.couponNo.trim());

      if (response.status === 0 && response.data && response.data.length > 0) {
        const couponData = response.data[0];
        setCouponValidationData(couponData);

        // Validation rules
        const billValue = parseFloat(couponData.BillValue) || 0;
        const apiAmount = parseFloat(couponData.Amount) || 0;
        const validFrom = couponData.ValidFrom ? new Date(couponData.ValidFrom) : null;
        const validTo = couponData.ValidTo ? new Date(couponData.ValidTo) : null;

        // Compare dates only (ignore time): coupon valid for the full ValidFrom/ValidTo calendar days
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (validFrom) {
          const validFromStart = new Date(validFrom);
          validFromStart.setHours(0, 0, 0, 0);
          if (today < validFromStart) {
            setCouponError('Coupon is not yet valid.');
            setPayments(p => ({ ...p, couponAmount: '0', couponAmountLocked: false }));
            return;
          }
        }
        if (validTo) {
          const endOfValidToDay = new Date(validTo);
          endOfValidToDay.setHours(23, 59, 59, 999);
          if (today > endOfValidToDay) {
            setCouponError('Coupon has expired.');
            setPayments(p => ({ ...p, couponAmount: '0', couponAmountLocked: false }));
            return;
          }
        }

        // Check minimum bill value against total invoice amount
        if (invoiceTotal < billValue) {
          setCouponError(`Minimum bill value should be ${billValue.toFixed(2)}.`);
          setPayments(p => ({ ...p, couponAmount: '0', couponAmountLocked: false }));
          return;
        }

        // Success: set amount from API and lock field
        setPayments(p => ({ ...p, couponAmount: apiAmount.toFixed(2), couponAmountLocked: true }));
        toast({
          title: 'Success',
          description: 'Coupon applied successfully.',
          className: 'bg-green-600 text-white border-green-700'
        });
      } else {
        setCouponError('Invalid coupon code');
        setPayments(p => ({ ...p, couponAmount: '0', couponAmountLocked: false }));
      }
    } catch (error) {
      console.error('Error validating coupon:', error);
      setCouponError('Failed to validate coupon. Please try again.');
      setPayments(p => ({ ...p, couponAmount: '0', couponAmountLocked: false }));
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  // Function to validate coupon amount when user enters it
  const validateCouponAmount = (amount: string) => {
    if (!couponValidationData) {
      setCouponError('Please validate the coupon first');
      return false;
    }

    const enteredAmount = parseFloat(amount) || 0;
    const maxAmount = parseFloat(couponValidationData.Amount) || 0;

    if (enteredAmount > maxAmount) {
      setCouponError(`You can use up to ₹${maxAmount.toFixed(2)} only.`);
      return false;
    }

    setCouponError('');
    return true;
  };

  // Function to handle credit note validation
  const handleCreditNoteValidation = async () => {
    if (!payments.creditNoteNo || payments.creditNoteNo.trim() === '') {
      setCreditNoteError('Please enter a credit note number');
      return;
    }

    setIsValidatingCreditNote(true);
    setCreditNoteError('');

    try {
      const invSeqNo = payments.creditNoteNo.trim();
      const invDate = new Date().toISOString().slice(0, 10);
      const companyId = String(customer.SelectCompany || '');

      const response = await validateCreditNote(invSeqNo, invDate, companyId);

      if (response.status === 0 && response.data && response.data.length > 0) {
        const creditData = response.data[0];
        const invoicedAmount = parseFloat(creditData.InvoicedAmount) || 0;
        setPayments(p => ({ ...p, creditNoteAmount: invoicedAmount.toFixed(2) }));
        // Check if credit note amount exceeds invoice total
        const invTotal = products.reduce((sum, p) => {
          const mrp = parseFloat(p.MRP) || 0;
          const qty = parseFloat(p.Qty) || 0;
          const discount = parseFloat(p.Discount) || 0;
          const offerPerUnit = qty > 0 ? mrp - (discount / qty) : mrp;
          return sum + offerPerUnit * qty;
        }, 0);
        if (invoicedAmount > invTotal) {
          setCreditNoteError(
            `Credit Note Amount Exceeds Invoice Total\nThe selected credit note amount (${invoicedAmount.toFixed(2)}) is greater than the invoice total (${invTotal.toFixed(2)}).\nPlease add more products or remove the credit note to continue.`
          );
          toast({
            title: 'Warning',
            description: 'Credit note amount exceeds invoice total. Add more products or remove the credit note.',
            variant: 'destructive'
          });
        } else {
          setCreditNoteError('');
          toast({
            title: 'Success',
            description: 'Credit Note applied successfully.',
            className: 'bg-green-600 text-white border-green-700'
          });
        }
      } else {
        setCreditNoteError('Invalid Credit Note');
        setPayments(p => ({ ...p, creditNoteAmount: '0' }));
      }
    } catch (error) {
      console.error('Error validating credit note:', error);
      setCreditNoteError('Failed to validate credit note. Please try again.');
      setPayments(p => ({ ...p, creditNoteAmount: '0' }));
    } finally {
      setIsValidatingCreditNote(false);
    }
  };

  // Show/clear credit note exceeds error when products or credit note amount changes
  useEffect(() => {
    const cnAmt = parseFloat(String(payments.creditNoteAmount)) || 0;
    if (cnAmt <= 0) return;
    if (cnAmt > invoiceTotal) {
      setCreditNoteError(
        `Credit Note Amount Exceeds Invoice Total\nThe selected credit note amount (${cnAmt.toFixed(2)}) is greater than the invoice total (${invoiceTotal.toFixed(2)}).\nPlease add more products or remove the credit note to continue.`
      );
    } else {
      setCreditNoteError(prev => {
        if (prev && prev.includes('Credit Note Amount Exceeds Invoice Total')) return '';
        return prev;
      });
    }
  }, [invoiceTotal, payments.creditNoteAmount]);

  // If company changes while popup is open, update State field (only in create mode)
  useEffect(() => {
    if (showShipToPopup && !isEditMode) {
      setShipToForm(f => ({ ...f, State: customer.State || '' }));
    }
    // eslint-disable-next-line
  }, [customer.SelectCompany, companies, showShipToPopup, isEditMode]);

  // On locations list load, set customer.Location if not set
  useEffect(() => {
    if (locations.length > 0 && !customer.Location) {
      setCustomer(f => ({ ...f, Location: locations[0].ID }));
    }
  }, [locations]);

  // On companies list load, set customer.SelectCompany and State if not set (only in create mode; in edit mode preserve CustState from API)
  useEffect(() => {
    if (companies.length > 0 && !isEditMode) {
      if (customer.SelectCompany) {
        const selectedCompany = companies.find(c => String(c.Id) === String(customer.SelectCompany));
        if (selectedCompany && selectedCompany.State) {
          setCustomer(f => ({ ...f, State: selectedCompany.State }));
        }
      } else {
        const defaultCompany = primaryCompanyId
          ? companies.find(c => String(c.Id) === primaryCompanyId || String(c.CompanyID) === primaryCompanyId)
          : null;
        const companyToUse = defaultCompany || companies[0];
        setCustomer(f => ({ ...f, SelectCompany: companyToUse.Id, State: companyToUse.State || '' }));
      }
    }
  }, [companies, primaryCompanyId, isEditMode]);

  // Add handler for Create button
  const handleCreateInvoice = async () => {
    try {
      if (balanceDue < 0) {
        toast({
          title: 'Invalid Balance',
          description: 'Cannot create invoice with negative balance. Please adjust payments.',
          variant: 'destructive'
        });
        return;
      }
      // Prevent creation if phone is empty or not exactly 10 digits (and not unknown)
      if (!isUnknown && (!customer.Phone || customer.Phone.trim() === '' || customer.Phone.replace(/\D/g, '').length !== 10)) {
        toast({
          description: 'Enter correct phone number',
          variant: 'destructive'
        });
        return;
      }
      // Add validation before building invLineItems
      const invalidProducts = products.filter(p => !p.ProdCode || isNaN(Number(p.ProdCode)));
      if (invalidProducts.length > 0) {
        toast({
          title: 'Validation Error',
          description: 'One or more products are missing a valid numeric ProdCode. Please check your product list.',
          variant: 'destructive'
        });
        return;
      }

      // Validate credit note amount does not exceed invoice total
      const creditNoteAmt = parseFloat(String(payments.creditNoteAmount)) || 0;
      if (creditNoteAmt > invoiceTotal) {
        toast({
          title: 'Credit Note Amount Exceeds Invoice Total',
          description: `The selected credit note amount (₹${creditNoteAmt.toFixed(2)}) is greater than the invoice total (₹${invoiceTotal.toFixed(2)}). Please add more products or remove the credit note to continue.`,
          variant: 'destructive'
        });
        return;
      }

      // Calculate valid coupon amount for API call
      const validCouponAmount = getValidCouponAmount();
      const paymentsForApi = {
        ...payments,
        couponAmount: validCouponAmount.toString()
      };

      // Use the same createInvoiceApiCall function as SalesInvoiceMobile
      await createInvoiceApiCall({
        customer,
        products,
        payments: paymentsForApi,
        notes,
        repName,
        shipToForm,
        printInvoice: false, // Create only, not print
        companyState: getCurrentCompanyState(),
        isUnknown,
        invoiceDate: form.InvoiceDate || customer.InvoiceDate || new Date().toISOString().slice(0, 10),
        payBefore: form.PayBefore,
        partners,
        companies, // Pass companies array
        invoiceForm: {
          QuoteNo: invoiceForm.QuoteNo || '',
          Mode: invoiceForm.Mode || '',
          VehicleNo: invoiceForm.VehicleNo || '',
          Delivered: invoiceForm.Delivered || false,
          AddInFIFO: invoiceForm.AddInFIFO || false
        },
        editingInvoiceId,
        shipToSame,
        onSuccess: async (result) => {
          const successMessage = isEditMode ? 'Invoice updated successfully!' : result.message;
          toast({ title: 'Success', description: successMessage, className: 'bg-green-600 text-white border-green-700' });

          if (isEditMode && onUpdateSuccess) {
            // In edit mode, call the success callback to return to invoice reports
            onUpdateSuccess(successMessage);
          } else {
            // In create mode, reset form completely for new invoice (clear all fields)
            setHasOpenedPaymentModal(false);
            const currentCompany = customer.SelectCompany;
            const currentLocation = customer.Location;
            const currentPartner = customer.Partner;

            // Get the company's default state instead of keeping old customer's state
            const selectedCompany = companies.find(c => String(c.Id) === String(currentCompany));
            const companyState = selectedCompany ? selectedCompany.State || '' : '';
            const keepUnknown = salesInvoiceSettings?.unknownUserTicketed ?? false;

            // Clear ALL customer details for fresh entry (when Unknown is kept, set Name/Company to UNKNOWN so they show and stay read-only)
            setCustomer({
              Name: keepUnknown ? 'UNKNOWN' : '',
              Company: keepUnknown ? 'UNKNOWN' : '',
              Phone: '',
              Email: '',
              GST: '',
              Address: '',
              State: companyState, // Reset to company's state, not old customer's state
              Locality: '', // Clear locality field
              ReferredBy: '',
              SelectCompany: currentCompany, // Keep company settings
              Location: currentLocation, // Keep location
              Partner: currentPartner, // Keep partner
              eCommOrderID: '', // Clear eComm field
            });
            setProducts([]);
            setPayments({
              splitPaymentEnabled: false,
              selectedPaymentMethod: getDefaultPaymentMethod(),
              cashPayment: '',
              walletProvider: 'PhonePe',
              walletAmount: '',
              walletTxNo: '',
              chequeAmount: '',
              chequeNo: '',
              couponNo: '',
              couponAmount: '0',
              couponAmountLocked: false,
              creditNoteNo: '',
              creditNoteAmount: '0',
              pointsRedeem: '',
              bankTransAmount: '',
              otherAmount: '',
              payBefore: (() => {
                const defaultDate = new Date();
                defaultDate.setDate(defaultDate.getDate() + 30); // Default to 30 days from now
                return defaultDate.toISOString().slice(0, 10);
              })(),
            });
            setNotes('');
            setRepName('');
            setForm({
              ProductBarcode: '', Serial: '', ProductName: '',
              RepName: '', Location: '', SelectCompany: '', QuoteNo: '', Mode: '', VehicleNo: '',
              InvoiceDate: new Date().toISOString().slice(0, 10), PayBefore: (() => {
                const defaultDate = new Date();
                defaultDate.setDate(defaultDate.getDate() + 30); // Default to 30 days from now
                return defaultDate.toISOString().slice(0, 10);
              })(), EligiblePoints: 0, Redeem: 0, Discount: '',
              BalanceDue: '', SplitPayment: false, Total: '',
              ShippingState: '', ShippingAddress: '', ShippingName: '', ShippingGST: '', ShippingPhone: '', ShippingEmail: '',
              eCommOrderID: ''
            });
            setInvoiceForm({
              QuoteNo: '',
              Mode: '',
              VehicleNo: '',
              Delivered: true,
              AddInFIFO: true
            });
            setIsUnknown(keepUnknown);

            // Reset shipping form (when Unknown is kept, set Name/Company to UNKNOWN)
            setShipToForm({ Name: keepUnknown ? 'UNKNOWN' : '', Company: keepUnknown ? 'UNKNOWN' : '', Phone: '', Email: '', GST: '', Address: '', State: '' });
            setShipToSame(true);
            setCustomerTotalDue(null);

            // Invalidate product search cache for fresh suggestions in next invoice
            queryClient.invalidateQueries({ queryKey: ['products-search'] });
            queryClient.removeQueries({ queryKey: ['products-search'] });
          }
        },
        onError: (error) => {
          const errorMessage = isEditMode ? 'Failed to update invoice' : 'Failed to create invoice';
          toast({ title: 'Error', description: error?.message || errorMessage, variant: 'destructive' });
        }
      });
    } catch (error) {
      toast({ title: 'API Error', description: error?.message || 'Failed to create invoice', variant: 'destructive' });
    }
  };

  // Helper function to reset rep name and search term together
  const resetRepNameAndSearch = () => {
    setRepName('');
    setRepNameSearchTerm('');
  };


  const [pdfDataUrl, setPdfDataUrl] = useState<string | null>(null);

  // Add state for print options
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  const [pendingInvoiceId, setPendingInvoiceId] = useState(null);
  // Web-specific print options (for QZ Tray)
  const [printMainOption, setPrintMainOption] = useState('Kohinoor'); // 'Kohinoor' or 'Multiple Page Modem'
  const [kohinoorSubOption, setKohinoorSubOption] = useState('2mm'); // '2mm' or '3mm'
  const enableDesktopDirectPrint = false;
  const [defaultPrinterDriver, setDefaultPrinterDriver] = useState('');
  const [selectedPrinterDriver, setSelectedPrinterDriver] = useState('');
  const [isPrinterDriverLoading, setIsPrinterDriverLoading] = useState(false);
  const [printerDriverError, setPrinterDriverError] = useState<string | null>(null);
  const [isSendingToPrinter, setIsSendingToPrinter] = useState(false);

  // Handler for PrintOptionsModal onSelect
  const handlePrintOptionSelect = async (options: { format: 'kohineer', size?: '2inch' | '3inch', deviceAddress?: string }) => {
    console.log('[SalesInvoice] Print option selected:', options);

    const resolvedInvoiceId = pendingInvoiceId ?? editingInvoiceId;
    if (!resolvedInvoiceId) {
      toast({
        title: 'Print Error',
        description: 'No invoice ID available for printing',
        variant: 'destructive'
      });
      return;
    }

    try {
      const baseUrl = getBaseUrl();
      const token = localStorage.getItem('token') || '';
      const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      const getRes = await axios.get(`${baseUrl}/api/InvoiceFullDetails?InvoiceID=${resolvedInvoiceId}`, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
      });

      if (getRes.data.status === 0 && getRes.data.data) {
        const invData = { ...getRes.data.data[0], data: getRes.data.data };

        let paperWidth = 58; // default 2mm
        if (options.size === '3inch') {
          paperWidth = 80;
        }

        const doc = new jsPDF({ unit: 'mm', format: [paperWidth, 200] });
        await drawInvoicePdf(doc, invData, paperWidth, 0, resolvedInvoiceId as any, { showSerialInPrint, serialPrintTemplate });

        // For now, just download the PDF since this is desktop version
        const pdfDataUri = doc.output('datauristring');
        const fileName = `Invoice_${invData.InvSeqNo || resolvedInvoiceId}`;

        await downloadPdfUtil(
          pdfDataUri,
          fileName,
          () => {
            toast({
              title: 'Print Success',
              description: 'PDF generated successfully',
              className: 'bg-green-600 text-white border-green-700'
            });
          },
          (error) => {
            toast({
              title: 'Print Error',
              description: error,
              variant: 'destructive'
            });
          }
        );
      } else {
        toast({
          title: 'Print Error',
          description: getRes.data.message || 'Failed to fetch invoice details',
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Print error:', err);
      toast({
        title: 'Print Error',
        description: 'Failed to print invoice',
        variant: 'destructive'
      });
    }

    setShowPrintOptions(false);
  };

  // Print invoice based on Invoice Print Template V2 setting (0=2inch, 1=3inch, 2=A4)
  const printInvoiceByTemplate = async (invoiceId: string | number, templateValue: string) => {
    const baseUrl = getBaseUrl();
    const token = localStorage.getItem('token') || '';
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

    try {
      let pdfUrl: string;

      if (templateValue === '2') {
        // A4: use GetInvoicePDF API with multiple page modern
        const apiEndpoint = `/api/GetInvoicePDF?InvoiceID=${invoiceId}&format=${encodeURIComponent('multiple page modern')}`;
        const pdfBlob = await fetchPDF(apiEndpoint);
        pdfUrl = URL.createObjectURL(pdfBlob);
      } else {
        // 0 = 2inch (58mm), 1 = 3inch (80mm): use drawInvoicePdf (3-inch uses white headers + border so preview and print match)
        const paperWidth = templateValue === '1' ? 80 : 58;
        const getRes = await axios.get(`${baseUrl}/api/InvoiceFullDetails?InvoiceID=${invoiceId}`, {
          headers: { 'Authorization': authToken, 'Content-Type': 'application/json' },
        });
        if (getRes.data.status !== 0 || !getRes.data.data) {
          throw new Error(getRes.data?.message || 'Failed to fetch invoice details');
        }
        const invData = { ...getRes.data.data[0], data: getRes.data.data };
        const doc = new jsPDF({ unit: 'mm', format: [paperWidth, 200] });
        await drawInvoicePdf(doc, invData, paperWidth, 0, String(invoiceId), { showSerialInPrint, serialPrintTemplate });
        const dataUri = doc.output('datauristring');
        const byteCharacters = atob(dataUri.split(',')[1]);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
        const pdfBlob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
        pdfUrl = URL.createObjectURL(pdfBlob);
      }

      const printWindow = window.open(pdfUrl, '_blank', 'width=900,height=1100,scrollbars=yes,resizable=yes');
      if (!printWindow) {
        toast({ title: 'Print Error', description: 'Please allow popups to print invoices.', variant: 'destructive' });
        URL.revokeObjectURL(pdfUrl);
        return;
      }
      printWindow.addEventListener('load', () => {
        printWindow.focus();
        printWindow.print();
        const checkClosed = setInterval(() => {
          if (printWindow.closed) {
            clearInterval(checkClosed);
            setTimeout(() => URL.revokeObjectURL(pdfUrl), 500);
          }
        }, 500);
      });
      toast({ title: 'Print', description: 'Print dialog opened', className: 'bg-green-600 text-white border-green-700' });
    } catch (err) {
      console.error('Print error:', err);
      toast({ title: 'Print Error', description: err instanceof Error ? err.message : 'Failed to print invoice', variant: 'destructive' });
    }
  };

  // Modified handleCreateAndPrint
  const handleCreateAndPrint = async () => {
    try {
      if (balanceDue < 0) {
        toast({
          title: 'Invalid Balance',
          description: 'Cannot create invoice with negative balance. Please adjust payments.',
          variant: 'destructive'
        });
        return;
      }
      // Prevent creation if phone is empty or not exactly 10 digits (and not unknown)
      if (!isUnknown && (!customer.Phone || customer.Phone.trim() === '' || customer.Phone.replace(/\D/g, '').length !== 10)) {
        toast({
          title: 'Validation Error',
          description: 'Phone number must be exactly 10 digits.',
          variant: 'destructive'
        });
        return;
      }
      // Add validation before building invLineItems
      const invalidProducts = products.filter(p => !p.ProdCode || isNaN(Number(p.ProdCode)));
      if (invalidProducts.length > 0) {
        toast({
          title: 'Validation Error',
          description: 'One or more products are missing a valid numeric ProdCode. Please check your product list.',
          variant: 'destructive'
        });
        return;
      }

      // Validate credit note amount does not exceed invoice total
      const creditNoteAmt = parseFloat(String(payments.creditNoteAmount)) || 0;
      if (creditNoteAmt > invoiceTotal) {
        toast({
          title: 'Credit Note Amount Exceeds Invoice Total',
          description: `The selected credit note amount (₹${creditNoteAmt.toFixed(2)}) is greater than the invoice total (₹${invoiceTotal.toFixed(2)}). Please add more products or remove the credit note to continue.`,
          variant: 'destructive'
        });
        return;
      }

      // Calculate valid coupon amount for API call
      const validCouponAmount = getValidCouponAmount();
      const paymentsForApi = {
        ...payments,
        couponAmount: validCouponAmount.toString()
      };

      // Use the same createInvoiceApiCall function as SalesInvoiceMobile
      await createInvoiceApiCall({
        customer,
        products,
        payments: paymentsForApi,
        notes,
        repName,
        shipToForm,
        printInvoice: true, // Create and print
        companyState: getCurrentCompanyState(),
        isUnknown,
        invoiceDate: form.InvoiceDate || customer.InvoiceDate || new Date().toISOString().slice(0, 10),
        payBefore: form.PayBefore,
        partners,
        companies, // Pass companies array
        invoiceForm: {
          QuoteNo: invoiceForm.QuoteNo || '',
          Mode: invoiceForm.Mode || '',
          VehicleNo: invoiceForm.VehicleNo || '',
          Delivered: invoiceForm.Delivered || false,
          AddInFIFO: invoiceForm.AddInFIFO || false
        },
        editingInvoiceId,
        shipToSame,
        onSuccess: async (result) => {
          // Extract InvoiceID from API response
          let invoiceId = null;
          if (result?.data?.[0]?.Column2) {
            invoiceId = result.data[0].Column2;
          } else if (result?.data?.data?.[0]?.Column2) {
            invoiceId = result.data.data[0].Column2;
          } else if (result?.Column2) {
            invoiceId = result.Column2;
          }

          toast({ title: 'Success', description: result.message, className: 'bg-green-600 text-white border-green-700' });
          console.log('=== INVOICE CREATED SUCCESSFULLY ===');
          console.log('Invoice ID:', invoiceId);
          setPendingInvoiceId(invoiceId);

          // Print directly using Invoice Print Template setting (0=2inch, 1=3inch, 2=A4)
          const templateValue = salesInvoiceSettings.invoicePrintTemplateV2 || '0';
          if (invoiceId) {
            printInvoiceByTemplate(invoiceId, templateValue);
          }

          // Only clear fields in create mode, not in edit mode
          if (!isEditMode) {
            // Reset form completely for new invoice (clear all fields)
            const currentCompany = customer.SelectCompany;
            const currentLocation = customer.Location;
            const currentPartner = customer.Partner;

            // Get the company's default state instead of keeping old customer's state
            const selectedCompany = companies.find(c => String(c.Id) === String(currentCompany));
            const companyState = selectedCompany ? selectedCompany.State || '' : '';
            const keepUnknownPrint = salesInvoiceSettings?.unknownUserTicketed ?? false;

            // Clear ALL customer details for fresh entry (when Unknown is kept, set Name/Company to UNKNOWN)
            setCustomer({
              Name: keepUnknownPrint ? 'UNKNOWN' : '',
              Company: keepUnknownPrint ? 'UNKNOWN' : '',
              Phone: '',
              Email: '',
              GST: '',
              Address: '',
              State: companyState,
              Locality: '',
              ReferredBy: '',
              SelectCompany: currentCompany,
              Location: currentLocation,
              Partner: currentPartner,
              eCommOrderID: '',
            });
            setProducts([]);
            setPayments({
              splitPaymentEnabled: false,
              selectedPaymentMethod: getDefaultPaymentMethod(),
              cashPayment: '',
              walletProvider: 'PhonePe',
              walletAmount: '',
              walletTxNo: '',
              chequeAmount: '',
              chequeNo: '',
              couponNo: '',
              couponAmount: '0',
              couponAmountLocked: false,
              creditNoteNo: '',
              creditNoteAmount: '0',
              pointsRedeem: '',
              bankTransAmount: '',
              otherAmount: '',
              payBefore: (() => {
                const defaultDate = new Date();
                defaultDate.setDate(defaultDate.getDate() + 30);
                return defaultDate.toISOString().slice(0, 10);
              })(),
            });
            setNotes('');
            setRepName('');
            setForm({
              ProductBarcode: '', Serial: '', ProductName: '',
              RepName: '', Location: '', SelectCompany: '', QuoteNo: '', Mode: '', VehicleNo: '',
              InvoiceDate: new Date().toISOString().slice(0, 10), PayBefore: (() => {
                const defaultDate = new Date();
                defaultDate.setDate(defaultDate.getDate() + 30);
                return defaultDate.toISOString().slice(0, 10);
              })(), EligiblePoints: 0, Redeem: 0, Discount: '',
              BalanceDue: '', SplitPayment: false, Total: '',
              ShippingState: '', ShippingAddress: '', ShippingName: '', ShippingGST: '', ShippingPhone: '', ShippingEmail: '',
              eCommOrderID: ''
            });
            setInvoiceForm({
              QuoteNo: '',
              Mode: '',
              VehicleNo: '',
              Delivered: true,
              AddInFIFO: true
            });
            setIsUnknown(keepUnknownPrint);

            // Reset shipping form (when Unknown is kept, set Name/Company to UNKNOWN)
            setShipToForm({ Name: keepUnknownPrint ? 'UNKNOWN' : '', Company: keepUnknownPrint ? 'UNKNOWN' : '', Phone: '', Email: '', GST: '', Address: '', State: '' });
            setShipToSame(true);
            setCustomerTotalDue(null);

            // Invalidate product search cache for fresh suggestions in next invoice
            queryClient.invalidateQueries({ queryKey: ['products-search'] });
            queryClient.removeQueries({ queryKey: ['products-search'] });
          }
          // Note: In edit mode, navigation will be handled after successful print completion
        },
        onError: (error) => {
          toast({ title: 'Invoice Error', description: error?.message || 'Failed to create invoice', variant: 'destructive', className: 'bg-red-600 text-white border-red-700' });
        }
      });
    } catch (error) {
      toast({ title: 'API Error', description: error?.message || 'Failed to create/print invoice', variant: 'destructive', className: 'bg-red-600 text-white border-red-700' });
    }
  };



  const downloadPdf = async () => {
    console.log('=== DOWNLOAD PDF TRIGGERED ===');

    const resolvedInvoiceId = pendingInvoiceId ?? editingInvoiceId;
    console.log('resolvedInvoiceId:', resolvedInvoiceId);

    if (!resolvedInvoiceId) {
      console.error('No invoice ID available');
      toast({
        title: 'Download Error',
        description: 'No invoice ID available for download',
        variant: 'destructive'
      });
      return;
    }

    try {
      console.log('Fetching invoice data for ID:', resolvedInvoiceId);
      // Fetch invoice data
      const baseUrl = getBaseUrl();
      const token = localStorage.getItem('token') || '';
      const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      const getRes = await axios.get(`${baseUrl}/api/InvoiceFullDetails?InvoiceID=${resolvedInvoiceId}`, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json',
        },
      });

      // Log full API response in JSON format (like Postman)
      console.log('=== FULL API RESPONSE (JSON) ===');
      console.log(JSON.stringify(getRes.data, null, 2));
      console.log('=== END API RESPONSE ===');
      console.log('API Response:', getRes.data);

      if (getRes.data.status === 0 && getRes.data.data) {
        // Header might be at top level of response or in first data item
        const invData = {
          ...getRes.data.data[0],
          data: getRes.data.data,
          Header: getRes.data.Header || getRes.data.data[0]?.Header // Get Header from top level or first item
        };
        console.log('Invoice data prepared:', invData);
        console.log('Header value:', invData.Header);

        let paperWidth = 58; // default 2mm
        if (printMainOption === 'Kohinoor') {
          paperWidth = kohinoorSubOption === '3mm' ? 80 : 58;
        }
        console.log('Paper width:', paperWidth);

        const doc = new jsPDF({ unit: 'mm', format: [paperWidth, 200] });
        console.log('PDF document created');

        await drawInvoicePdf(doc, invData, paperWidth, 0, resolvedInvoiceId as any, { showSerialInPrint, serialPrintTemplate });
        console.log('PDF content drawn');

        // Use the unified download utility
        const pdfDataUri = doc.output('datauristring');
        const fileName = `Invoice_${invData.InvSeqNo || resolvedInvoiceId}`;

        await downloadPdfUtil(
          pdfDataUri,
          fileName,
          () => {
            toast({
              title: 'Download Success',
              description: Capacitor.isNativePlatform()
                ? "PDF saved to device and share dialog opened"
                : "Invoice PDF downloaded successfully",
              className: 'bg-green-600 text-white border-green-700'
            });

            setShowPrintOptions(false);

            // In edit mode, navigate back to reports after download
            if (isEditMode) {
              try {
                if (onBackToReports) {
                  onBackToReports();
                } else {
                  navigate('/reports/invoice-report');
                }
              } catch { }
            } else {
              // In create mode, reset form completely for new invoice (clear all fields)
              const currentCompany = customer.SelectCompany;
              const currentLocation = customer.Location;
              const currentPartner = customer.Partner;

              // Get the company's default state instead of keeping old customer's state
              const selectedCompany = companies.find(c => String(c.Id) === String(currentCompany));
              const companyState = selectedCompany ? selectedCompany.State || '' : '';
              const keepUnknownDl = salesInvoiceSettings?.unknownUserTicketed ?? false;

              // Clear ALL customer details for fresh entry (when Unknown is kept, set Name/Company to UNKNOWN)
              setCustomer({
                Name: keepUnknownDl ? 'UNKNOWN' : '',
                Company: keepUnknownDl ? 'UNKNOWN' : '',
                Phone: '',
                Email: '',
                GST: '',
                Address: '',
                State: companyState, // Reset to company's state, not old customer's state
                Locality: '', // Clear locality field
                ReferredBy: '',
                SelectCompany: currentCompany, // Keep company settings
                Location: currentLocation, // Keep location
                Partner: currentPartner, // Keep partner
                eCommOrderID: '', // Clear eComm field
              });
              setProducts([]);
              setPayments({
                splitPaymentEnabled: false,
                selectedPaymentMethod: getDefaultPaymentMethod(),
                cashPayment: '',
                walletProvider: 'PhonePe',
                walletAmount: '',
                walletTxNo: '',
                chequeAmount: '',
                chequeNo: '',
                couponNo: '',
                couponAmount: '0',
                couponAmountLocked: false,
                creditNoteNo: '',
                creditNoteAmount: '0',
                pointsRedeem: '',
                bankTransAmount: '',
                otherAmount: '',
                payBefore: (() => {
                  const defaultDate = new Date();
                  defaultDate.setDate(defaultDate.getDate() + 30); // Default to 30 days from now
                  return defaultDate.toISOString().slice(0, 10);
                })(),
              });
              setNotes('');
              setRepName('');
              setForm({
                ProductBarcode: '', Serial: '', ProductName: '',
                RepName: '', Location: '', SelectCompany: '', QuoteNo: '', Mode: '', VehicleNo: '',
              InvoiceDate: new Date().toISOString().slice(0, 10), PayBefore: (() => {
                  const defaultDate = new Date();
                  defaultDate.setDate(defaultDate.getDate() + 30); // Default to 30 days from now
                  return defaultDate.toISOString().slice(0, 10);
                })(), EligiblePoints: 0, Redeem: 0, Discount: '',
                BalanceDue: '', SplitPayment: false, Total: '',
                ShippingState: '', ShippingAddress: '', ShippingName: '', ShippingGST: '', ShippingPhone: '', ShippingEmail: '',
                eCommOrderID: ''
              });
              setInvoiceForm({
                QuoteNo: '',
                Mode: '',
                VehicleNo: '',
                Delivered: true,
                AddInFIFO: true
              });
              setIsUnknown(keepUnknownDl);

              // Reset shipping form (when Unknown is kept, set Name/Company to UNKNOWN)
              setShipToForm({ Name: keepUnknownDl ? 'UNKNOWN' : '', Company: keepUnknownDl ? 'UNKNOWN' : '', Phone: '', Email: '', GST: '', Address: '', State: '' });
              setShipToSame(true);
              setCustomerTotalDue(null);

              // Invalidate product search cache for fresh suggestions in next invoice
              queryClient.invalidateQueries({ queryKey: ['products-search'] });
              queryClient.removeQueries({ queryKey: ['products-search'] });
            }
          },
          (error) => {
            toast({
              title: 'Download Error',
              description: error,
              variant: 'destructive'
            });
          }
        );

      } else {
        console.error('API returned error:', getRes.data);
        toast({
          title: 'Download Error',
          description: getRes.data.message || 'Failed to fetch invoice details',
          variant: 'destructive'
        });
      }
    } catch (err) {
      console.error('Download error:', err);
      toast({
        title: 'Download Error',
        description: 'Failed to download PDF',
        variant: 'destructive'
      });
    }
  };

  const pdfIframeRef = useRef(null);

  // Optionally, clean up Blob URLs when modal is closed or new PDF is generated
  useEffect(() => {
    return () => {
      if (pdfDataUrl) {
        URL.revokeObjectURL(pdfDataUrl);
      }
    };
  }, [pdfDataUrl]);

  // Helper to draw a single straight horizontal line
  function drawStraightLine(doc, paperWidth, y, thickness = 0.4) {
    doc.setLineWidth(thickness);
    doc.line(0, y, paperWidth, y);
    doc.setLineWidth(0.2); // Reset to default if needed
  }

  const generateInvoicePdfBase64 = async (invoiceId: string, paperWidth: number) => {
    const baseUrl = getBaseUrl();
    const token = localStorage.getItem('token') || '';
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    const getRes = await axios.get(`${baseUrl}/api/InvoiceFullDetails?InvoiceID=${invoiceId}`, {
      headers: {
        'Authorization': authToken,
        'Content-Type': 'application/json',
      },
    });

    if (!getRes.data || getRes.data.status !== 0 || !getRes.data.data) {
      throw new Error(getRes.data?.message || 'Failed to fetch invoice details');
    }

    const invData = { ...getRes.data.data[0], data: getRes.data.data };
    const format = printMainOption === 'Kohinoor'
      ? [paperWidth, 200]
      : 'a4';

    const doc = new jsPDF({ unit: 'mm', format });
    await drawInvoicePdf(doc, invData, paperWidth, 0, invoiceId as any, { showSerialInPrint, serialPrintTemplate });
    const dataUri = doc.output('datauristring');
    const base64 = dataUri.split(',')[1];
    return { base64, invoiceData: invData };
  };

  const ensureQzConnection = async () => {
    if (typeof window === 'undefined' || !window.qz) {
      throw new Error('QZ Tray is not available. Please install it from https://qz.io/download/');
    }
    if (!window.qz.websocket.isActive()) {
      await window.qz.websocket.connect();
    }
  };

  const resolvePaperWidth = () => {
    if (printMainOption !== 'Kohinoor') {
      return 80;
    }
    return kohinoorSubOption === '3mm' ? 80 : 58;
  };

  // QZ Tray integration for silent POS printing
  // Requirements:
  // 1. QZ Tray must be installed and running on the client machine: https://qz.io/download/
  // 2. QZ Tray JS must be loaded globally (e.g., via CDN in public/index.html):
  //    <script src="https://cdn.jsdelivr.net/npm/qz-tray@2.1.0/qz-tray.js"></script>
  // 3. The POS-80 3mm printer should be set as the default printer in the OS.
  //
  // The printPdf function below will send the PDF directly to the default printer using QZ Tray, with no print dialog.

  // QZ Tray printPdf integration
  const printPdf = async () => {
    const resolvedInvoiceId = pendingInvoiceId ?? editingInvoiceId;

    if (Capacitor.isNativePlatform()) {
      await handlePrintOptionSelect({ format: 'kohineer', size: kohinoorSubOption === '3mm' ? '3inch' : '2inch' });
      return;
    }

    if (!resolvedInvoiceId) {
      toast({
        title: 'Print Error',
        description: 'No invoice ID available for printing',
        variant: 'destructive'
      });
      return;
    }

    if (!enableDesktopDirectPrint) {
      try {
        const paperWidth = printMainOption === 'Kohinoor' ? (kohinoorSubOption === '3mm' ? 80 : 58) : 80;
        const { base64 } = await generateInvoicePdfBase64(resolvedInvoiceId, paperWidth);

        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
        const pdfBlob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(pdfBlob);

        const printWindow = window.open(pdfUrl, '_blank', 'width=900,height=1100,scrollbars=yes,resizable=yes');
        if (!printWindow) {
          toast({
            title: 'Print Error',
            description: 'Please allow popups to print invoices. Check your browser popup blocker settings.',
            variant: 'destructive',
          });
          URL.revokeObjectURL(pdfUrl);
          return;
        }

        printWindow.focus();
        const checkClosed = setInterval(() => {
          if (printWindow.closed) {
            clearInterval(checkClosed);
            setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
          }
        }, 500);
        try {
          printWindow.addEventListener('load', () => {
            try {
              printWindow.focus();
              printWindow.print();
            } catch (e) {
              console.error('Auto-print may have been blocked by the browser:', e);
            }
          });
          printWindow.addEventListener('beforeunload', () => {
            setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
          });
        } catch { }

        const sizeLabel = paperWidth === 80 ? '3-inch (80mm)' : paperWidth === 58 ? '2-inch (58mm)' : `${paperWidth}mm`;
        toast({
          title: 'PDF Opened',
          description: `Page size: ${sizeLabel}. Use your browser's print dialog to print.`,
          className: 'bg-blue-600 text-white border-blue-700',
        });
        setShowPrintOptions(false);
      } catch (err: any) {
        console.error('Print error:', err);
        toast({
          title: 'Print Error',
          description: err?.message || 'Failed to open system print dialog',
          variant: 'destructive',
        });
      }
      return;
    }

    const printerName = (selectedPrinterDriver || defaultPrinterDriver).trim();
    if (!printerName) {
      toast({
        title: 'Printer Required',
        description: 'Select or enter the printer driver name before printing.',
        variant: 'destructive',
      });
      return;
    }

    setIsSendingToPrinter(true);
    try {
      await ensureQzConnection();
      const paperWidth = resolvePaperWidth();
      const { base64 } = await generateInvoicePdfBase64(resolvedInvoiceId, paperWidth);
      const config = window.qz.configs.create(printerName);
      await window.qz.print(config, [{ type: 'pdf', format: 'base64', data: base64 }]);

      toast({
        title: 'Print Job Sent',
        description: `Invoice sent to ${printerName}.`,
        className: 'bg-green-600 text-white border-green-700',
      });
      setShowPrintOptions(false);
    } catch (err: any) {
      console.error('Print error:', err);
      const message = err?.message || 'Failed to send the invoice to the printer.';
      toast({
        title: 'Print Error',
        description: message,
        variant: 'destructive',
      });
      if (!window.qz) {
        setPrinterDriverError('Install QZ Tray from https://qz.io/download/ and keep it running to print directly.');
      }
    } finally {
      setIsSendingToPrinter(false);
    }
  };

  // Monitor products loading in edit mode - only run once when products are loaded
  useEffect(() => {
    if (isEditMode && editingInvoiceId && products.length > 0) {
      console.log('SalesInvoice: Products loaded successfully in edit mode');
      console.log('SalesInvoice: Products count:', products.length);
      console.log('SalesInvoice: First product:', products[0]);
    }
  }, [isEditMode, editingInvoiceId, products.length]); // Only depend on products.length, not the entire products array

  // Monitor payments state changes in edit mode - only log once when entering edit mode
  useEffect(() => {
    if (isEditMode && editingInvoiceId) {
      console.log('SalesInvoice: Entered edit mode for invoice:', editingInvoiceId);
      console.log('SalesInvoice: Initial payments state:', payments);
      console.log('SalesInvoice: Cash payment value:', payments.cashPayment);
      console.log('SalesInvoice: Card amount value:', payments.cardPayment);
      console.log('SalesInvoice: Cash parseFloat result:', parseFloat(payments.cashPayment));
      console.log('SalesInvoice: Card parseFloat result:', parseFloat(payments.cardPayment));
      console.log('SalesInvoice: Initial shipToForm state:', shipToForm);
    }
  }, [isEditMode, editingInvoiceId]); // Removed 'payments' from dependencies to prevent continuous logging

  // Monitor shipToForm changes in edit mode for debugging
  useEffect(() => {
    if (isEditMode && editingInvoiceId) {
      console.log('SalesInvoice: shipToForm changed in edit mode:', shipToForm);
    }
  }, [isEditMode, editingInvoiceId, shipToForm]);

  // Monitor print modal state changes
  useEffect(() => {
    console.log('SalesInvoice: showPrintOptions changed to:', showPrintOptions);
    console.log('SalesInvoice: pendingInvoiceId changed to:', pendingInvoiceId);
    if (showPrintOptions) {
      console.log('SalesInvoice: Modal should be visible now');
    }
  }, [showPrintOptions, pendingInvoiceId]);

  useEffect(() => {
    if (!enableDesktopDirectPrint) {
      return;
    }

    if (Capacitor.isNativePlatform()) {
      return;
    }

    if (!showPrintOptions) {
      setPrinterDriverError(null);
      setIsPrinterDriverLoading(false);
      return;
    }

    if (typeof window === 'undefined' || !window.qz) {
      setPrinterDriverError('QZ Tray is not detected. Please install QZ Tray and keep it running to print directly.');
      setDefaultPrinterDriver('');
      setSelectedPrinterDriver('');
      return;
    }

    let isCancelled = false;

    const fetchDefaultPrinter = async () => {
      setIsPrinterDriverLoading(true);
      setPrinterDriverError(null);
      try {
        if (!window.qz.websocket.isActive()) {
          await window.qz.websocket.connect();
        }
        const defaultPrinter = await window.qz.printers.getDefault();
        if (isCancelled) return;
        setDefaultPrinterDriver(defaultPrinter || '');
        setSelectedPrinterDriver(defaultPrinter || '');
      } catch (error: any) {
        if (isCancelled) return;
        const message = error?.message || 'Unable to fetch the default printer driver.';
        setPrinterDriverError(message);
      } finally {
        if (!isCancelled) {
          setIsPrinterDriverLoading(false);
        }
      }
    };

    fetchDefaultPrinter();

    return () => {
      isCancelled = true;
    };
  }, [showPrintOptions, enableDesktopDirectPrint]);

  // Remove the continuous monitoring useEffect that was causing repeated logging
  // useEffect(() => {
  //   console.log('SalesInvoice: Products context changed:', products);
  //   console.log('SalesInvoice: Products length:', products?.length || 0);
  //   if (products && products.length > 0) {
  //     console.log('SalesInvoice: Products available in context:', products);
  //   }
  // }, [products]);

  const webPrintDisabled = enableDesktopDirectPrint ? (!selectedPrinterDriver.trim() || isSendingToPrinter) : false;
  const webPrintButtonClass = webPrintDisabled
    ? 'px-4 py-2 rounded text-white bg-green-400 cursor-not-allowed opacity-70'
    : 'px-4 py-2 rounded text-white bg-green-600 hover:bg-green-700';
  const webPrintButtonLabel = enableDesktopDirectPrint && isSendingToPrinter ? 'Printing...' : 'Print';

  return (
    <>
      {isMobile ? (
        <SalesInvoiceMobile />
      ) : (
        <div className="flex flex-col min-h-screen bg-gray-50">
          <main className="flex-1 overflow-y-auto">
            {/* Sales Invoice Title - compact for desktop */}
            <div className="px-2 sm:px-3 md:px-3 lg:px-2 xl:px-2.5 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-0.5 xl:py-1 2xl:py-1 bg-gray-50 flex items-center justify-between">
              <button
                className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 md:px-3 lg:px-2 xl:px-2.5 2xl:px-3 py-1 sm:py-1.5 md:py-1.5 lg:py-0.5 xl:py-1 2xl:py-1 rounded-md border border-gray-300 text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm font-semibold text-gray-700 bg-white hover:bg-gray-100 transition"
                onClick={handleSalesInvoiceBack}
              >
                <span className="text-base sm:text-lg md:text-lg lg:text-base xl:text-base 2xl:text-lg">←</span>
                Back
              </button>
              <div className="font-bold text-lg sm:text-2xl md:text-2xl lg:text-lg xl:text-xl 2xl:text-2xl text-blue-900">Sales Invoice</div>
              <div className="w-12 sm:w-16 md:w-16 lg:w-12 xl:w-14 2xl:w-16" />
            </div>
            {/* Customer Details */}
            <div className="p-2 sm:p-3 md:p-3 lg:p-2 xl:p-2.5 2xl:p-3 pt-0 border-b bg-gray-50">
              <div className="flex items-center justify-between gap-3 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-1.5">
                <div className="font-semibold text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm text-blue-900">Customer Details</div>
                <div className="flex items-center gap-3">
                  <div className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-blue-600 shrink-0">
                    Redeem points available:{' '}
                    <span className="font-semibold text-blue-600">
                      {headerRedeemPointsLoading ? '...' : ((headerRedeemPoints ?? 0).toFixed(2))}
                    </span>
                  </div>
                  {customerTotalDue !== null && (
                    <button
                      type="button"
                      onClick={handleDueAmountClick}
                      className="inline-flex items-center gap-1 text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-400 rounded transition-colors shrink-0"
                      title="Click to view customer ledger details"
                    >
                      <span>{formatDueAmount(customerTotalDue).text}</span>
                      <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 lg:w-3 lg:h-3 xl:w-3.5 xl:h-3.5 2xl:w-4 2xl:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-6 gap-2 sm:gap-4 md:gap-4 lg:gap-1.5 xl:gap-2 2xl:gap-2 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-1.5">
                <div>
                  <label className="block text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-2">Phone</label>
                  <div className="relative">
                    <input
                      className={`w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm ${isUnknown ? 'bg-gray-100' : 'bg-white'} placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition`}
                      value={customer.Phone}
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setCustomer(f => ({ ...f, Phone: val }));
                        setPhoneQuery(val);
                        setShowPhoneSuggestions(!isUnknown && val.length >= 1);
                        if (!val) {
                          setCustomerTotalDue(null);
                        }
                      }}
                      onFocus={() => setShowPhoneSuggestions(!isUnknown && customer.Phone.length >= 1)}
                      onBlur={() => setTimeout(() => setShowPhoneSuggestions(false), 150)}
                      onKeyDown={e => {
                        const len = phoneSuggestions?.length || 0;
                        if (!showPhoneSuggestions || len === 0) return;
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          setPhoneDropdownActiveIndex(prev => {
                            const next = prev + 1;
                            return next >= len ? 0 : next;
                          });
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setPhoneDropdownActiveIndex(prev => {
                            const next = prev - 1;
                            return next < 0 ? len - 1 : next;
                          });
                        } else if (e.key === 'Enter') {
                          e.preventDefault();
                          const idx = phoneDropdownActiveIndex >= 0 ? phoneDropdownActiveIndex : 0;
                          const c = phoneSuggestions[idx];
                          if (c) {
                            applyCustomerFromPhoneResult(c);
                            setPhoneQuery(c?.Mobile || '');
                            setShowPhoneSuggestions(false);
                          }
                        }
                      }}
                      placeholder="Enter phone number"
                      type="text"
                      maxLength={10}
                      readOnly={isUnknown}
                    />

                    {showPhoneSuggestions && !isUnknown && phoneQuery.length >= 1 && (
                      <div ref={phoneDropdownRef} className="absolute left-0 top-full mt-1 z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {isCustomerPhoneFetching ? (
                          <div className="p-2 text-xs text-gray-500">Loading...</div>
                        ) : phoneSuggestions.length > 0 ? (
                          phoneSuggestions.map((c: any, index: number) => (
                            <div
                              key={`cust-phone-${c?.Code ?? c?.Mobile ?? index}`}
                              className={`phone-dropdown-item p-2 text-xs border-b last:border-b-0 transition cursor-pointer ${index === phoneDropdownActiveIndex ? 'bg-blue-100' : 'hover:bg-blue-100'}`}
                              onClick={() => {
                                applyCustomerFromPhoneResult(c);
                                setPhoneQuery(c?.Mobile || '');
                                setShowPhoneSuggestions(false);
                              }}
                            >
                              <div className="font-medium">{c?.Mobile || ''}</div>
                              {(c?.CompanyName || c?.ContactName) && (
                                <div className="text-gray-600">{c?.CompanyName || c?.ContactName || ''}</div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="p-2 text-xs text-gray-500">No customers found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-2">Name</label>
                  <input
                    className="w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                    value={customer.Name}
                    onChange={e => setCustomer(f => ({ ...f, Name: e.target.value }))}
                    placeholder="Enter name"
                    type="text"
                    readOnly={isUnknown}
                  />
                </div>
                <div>
                  <label className="block text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-2">Company</label>
                  <input
                    className="w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                    value={customer.Company}
                    onChange={e => setCustomer(f => ({ ...f, Company: e.target.value }))}
                    placeholder="Enter company"
                    type="text"
                    readOnly={isUnknown}
                  />
                </div>
                <div>
                  <label className="block text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-2">GST #</label>
                  <input
                    className={cn('w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition', isUnknown ? 'bg-gray-100 border-gray-300' : 'bg-white', customerGstError ? 'border-red-500' : 'border-gray-300')}
                    value={customer.GST}
                    onChange={e => {
                      const { value, hadInvalidInput } = sanitizeGstInput(e.target.value);
                      setCustomer(f => ({ ...f, GST: value }));
                      setCustomerGstError(hadInvalidInput);
                    }}
                    onBlur={() => setCustomerGstError(false)}
                    placeholder="Enter GST number"
                    type="text"
                    maxLength={15}
                    readOnly={isUnknown}
                  />
                  {customerGstError && <p className="text-red-500 text-[10px] sm:text-xs mt-0.5">{GST_VALIDATION_MESSAGE}</p>}
                </div>
                <div>
                  <label className="block text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-2">Locality</label>
                  <input
                    className={`w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm ${isUnknown ? 'bg-gray-100' : 'bg-white'} placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition`}
                    value={customer.Locality}
                    onChange={e => setCustomer(f => ({ ...f, Locality: e.target.value }))}
                    placeholder="Enter locality"
                    type="text"
                    readOnly={isUnknown}
                  />
                </div>
                <div>
                  <label className="block text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-2">State</label>
                  <select
                    className={`w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm ${isUnknown ? 'bg-gray-100' : 'bg-white'} focus:outline-none focus:ring-2 focus:ring-cyan-400 appearance-none transition`}
                    value={customer.State}
                    onChange={e => {
                      const newState = e.target.value;
                      if (products.length > 0 && newState !== customer.State) {
                        setPendingState(newState);
                        setShowStateChangeConfirm(true);
                      } else {
                        setCustomer(f => ({ ...f, State: newState }));
                      }
                    }}
                    disabled={isUnknown}
                  >
                    <option value="">{isStatesLoading ? 'Loading...' : 'Select'}</option>
                    {statesError && <option value="">Error loading states</option>}
                    {states && states.map((state: any) => (
                      <option key={state.Code} value={state.State}>{state.State}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-1.5 flex flex-row items-center justify-between">
                <div>
                  <button type="button" className="text-blue-600 underline text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs" onClick={() => setShowMoreCustomerFields(v => !v)}>{showMoreCustomerFields ? 'Hide' : 'More'} ▼</button>
                </div>
                <div className="flex flex-row items-center gap-2 sm:gap-3 md:gap-3 lg:gap-2 xl:gap-2.5 2xl:gap-3">
                  {/* Ship to Same - Conditionally shown based on settings */}
                  {salesInvoiceSettings.shipAddressNeeded && (
                    <label className="flex items-center text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs">
                      <input
                        type="checkbox"
                        className="mr-1"
                        checked={shipToSame}
                        onChange={e => {
                          const isChecked = e.target.checked;
                          setShipToSame(isChecked);
                          if (!isChecked) {
                            // When unchecking, show popup with preserved values
                            setShowShipToPopup(true);
                          }
                        }}
                      />
                      Ship to (Same)
                    </label>
                  )}
                  <label className="flex items-center text-xs">
                    <input
                      type="checkbox"
                      className="mr-1"
                      checked={isUnknown}
                      onChange={e => {
                        setIsUnknown(e.target.checked);
                        if (e.target.checked) {
                          setCustomerTotalDue(null);
                          // When Unknown is checked: in edit mode preserve CustState; in create mode use company state
                          const stateToKeep = isEditMode ? customer.State : getStateFromCompany(customer.SelectCompany);
                          setCustomer(f => ({
                            ...f,
                            Phone: '',
                            Name: 'UNKNOWN',
                            Company: 'UNKNOWN',
                            Locality: '',
                            State: stateToKeep,
                            Address: '',
                            GST: '',
                            Email: '',
                            ReferredBy: ''
                          }));
                        } else {
                          setCustomerTotalDue(null);
                          // When Unknown is unchecked: preserve customer State (CustState) in edit mode; in create mode use company state
                          const stateToKeep = isEditMode ? customer.State : getStateFromCompany(customer.SelectCompany);
                          setCustomer(f => ({
                            ...f,
                            Name: '',
                            Company: '',
                            State: stateToKeep
                          }));
                        }
                      }}
                    />
                    Unknown
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-3 sm:px-4 md:px-4 lg:px-3 xl:px-3.5 2xl:px-4 rounded-md font-medium border-gray-300 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs"
                    onClick={() => {
                      clearCustomerDetails();
                      setCustomerTotalDue(null);
                      setPhoneQuery('');
                      setShowPhoneSuggestions(false);
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    className="h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-3 sm:px-4 md:px-4 lg:px-3 xl:px-3.5 2xl:px-4 rounded-md font-medium shadow bg-cyan-600 text-white hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs"
                    onClick={() => setShowNotesPopup(true)}
                  >
                    Notes
                  </Button>
                </div>
              </div>
              {showMoreCustomerFields && (
                <div className="grid grid-cols-6 gap-2 sm:gap-2 md:gap-2 lg:gap-1.5 xl:gap-2 2xl:gap-2 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-1.5 animate-fade-in">
                  <div>
                    <label className="block text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-2">Email</label>
                    <input
                      className="w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                      value={customer.Email}
                      onChange={e => setCustomer(f => ({ ...f, Email: e.target.value }))}
                      placeholder="Enter email"
                      type="email"
                      readOnly={isUnknown}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-2">Referred by</label>
                    <input
                      className="w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                      value={customer.ReferredBy}
                      onChange={e => setCustomer(f => ({ ...f, ReferredBy: e.target.value }))}
                      placeholder="Enter referred by"
                      type="text"
                      readOnly={isUnknown}
                    />
                  </div>
                  <div className="col-span-4">
                    <label className="block text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-2">Address</label>
                    <input
                      className="w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                      value={customer.Address}
                      onChange={e => setCustomer(f => ({ ...f, Address: e.target.value }))}
                      placeholder="Enter address"
                      type="text"
                      readOnly={isUnknown}
                    />
                  </div>
                </div>
              )}
            </div>
            {/* Product Section - compact for desktop, max 2 rows visible */}
            <div className="p-2 sm:p-3 md:p-3 lg:p-2 xl:p-2.5 2xl:p-3 pt-0 border-b bg-gray-50 flex flex-col min-h-0">
              {/* Product Fields Row 1 - visible fields, equal width, max 2 rows */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 md:gap-4 lg:gap-1.5 xl:gap-2 2xl:gap-2 items-start mb-1 sm:mb-3 md:mb-4 lg:mb-1 xl:mb-2 2xl:mb-2">
                {/* Product Barcode */}
                <div className="min-w-0 flex-1 lg:min-w-0">
                  <label className="block text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-2">Product Barcode</label>
                  <div className="relative">
                    <input
                      className="w-full h-6 sm:h-7 md:h-7 lg:h-6 xl:h-6 2xl:h-7 px-1 sm:px-1.5 md:px-1.5 lg:px-1 xl:px-1 2xl:px-1.5 py-0.5 border border-gray-300 rounded-md text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition pr-6 sm:pr-8 md:pr-8 lg:pr-6 xl:pr-6 2xl:pr-8"
                      value={barcodeSearchTerm}
                      onChange={e => {
                        const value = e.target.value;
                        setBarcodeSearchTerm(value);
                        const parsed = parseWeightBarcode(value);
                        setWeightFromBarcode(parsed?.weightKg ?? null);
                        setShowBarcodeDropdown(true);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const value = (e.currentTarget.value || '').trim();
                          const parsed = parseWeightBarcode(value);
                          setDebouncedBarcodeSearch(parsed?.baseBarcode ?? value);
                          setWeightFromBarcode(parsed?.weightKg ?? null);
                          setShowBarcodeDropdown(true);
                        }
                      }}
                      onFocus={() => setShowBarcodeDropdown(true)}
                      placeholder="Search barcode..."
                      type="text"
                    />

                    {showBarcodeDropdown && barcodeSearchTerm.length > 0 && (
                      <div className="absolute left-0 top-full mt-1 z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {barcodeSearchTerm !== debouncedBarcodeSearch ? (
                          <div className="p-2 text-xs text-gray-500">Typing...</div>
                        ) : isBarcodeLoading ? (
                          <div className="p-2 text-xs text-gray-500">Loading...</div>
                        ) : barcodeProducts.length > 0 ? (
                          barcodeProducts.map((product: any) => {
                            const available = Number(product.Available) || 0;
                            const isTraceable = product.isTraceable || false;
                            const isOutOfStock = available <= 0 && isTraceable;

                            return (
                              <div
                                key={`barcode-${product.Barcode}`}
                                className={`p-2 text-xs border-b last:border-b-0 transition flex items-center gap-2 ${isOutOfStock
                                  ? 'bg-gray-100 cursor-not-allowed opacity-60'
                                  : 'hover:bg-blue-100 cursor-pointer'
                                  }`}
                                onClick={() => {
                                  if (!isOutOfStock) {
                                    handleBarcodeProductSelect(product);
                                  }
                                }}
                              >
                                <img
                                  src={product.imgstr || '/placeholder.svg'}
                                  alt={product.Title}
                                  className="w-8 h-8 object-contain rounded border"
                                  onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
                                />
                                <div>
                                  <div className="font-medium">{product.Barcode}</div>
                                  <div className="text-gray-600">{product.Title}</div>
                                  {isOutOfStock && (
                                    <div className="text-red-500 font-semibold text-xs">Out of Stock</div>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="p-2 text-xs text-gray-500">No products found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Serial field - only shown when Serial Text setting has a value */}
                {showSerialField && (
                <div className="min-w-0 flex-1 lg:min-w-0">
                  <label className="block text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-2">{serialText}</label>
                  <input
                    className="w-full h-6 sm:h-7 md:h-7 lg:h-6 xl:h-6 2xl:h-7 px-1 sm:px-1.5 md:px-1.5 lg:px-1 xl:px-1 2xl:px-1.5 py-0.5 border border-gray-300 rounded-md text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                    value={serialNumberSearchTerm}
                    onChange={e => setSerialNumberSearchTerm(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && serialNumberSearchTerm.trim()) {
                        e.preventDefault();
                        handleSerialNumberSearch(serialNumberSearchTerm.trim());
                      }
                    }}
                    placeholder={`Search ${serialText.toLowerCase()}...`}
                    type="text"
                  />
                </div>
                )}

                {/* Product Name */}
                <div className="min-w-0 flex-1 lg:min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2 md:gap-2 lg:gap-1.5 xl:gap-1.5 2xl:gap-2 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-2">
                    <label className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700">Product Name</label>
                    <span
                      className="text-cyan-700 underline cursor-pointer text-xs hover:text-cyan-800"
                      onClick={() => setShowProductEntryModal(true)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          setShowProductEntryModal(true);
                        }
                      }}
                    >
                      New
                    </span>
                    <label className="flex items-center text-xs whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="mr-1"
                        checked={showQtyPopup}
                        onChange={e => setQtyPopupOverride(e.target.checked)}
                      />
                      Pop up for Qty
                    </label>
                  </div>
                  <div className="flex flex-row items-center gap-2">
                    <div className="relative flex-1">
                      <input
                        className="w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                        value={productNameSearchTerm}
                        onChange={e => { setProductNameSearchTerm(e.target.value); setShowProductNameDropdown(true); }}
                        onFocus={() => setShowProductNameDropdown(true)}
                        onKeyDown={e => {
                          const len = productNameProducts?.length || 0;
                          if (!showProductNameDropdown || len === 0) return;
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            setProductDropdownActiveIndex(prev => {
                              const next = prev + 1;
                              return next >= len ? 0 : next;
                            });
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setProductDropdownActiveIndex(prev => {
                              const next = prev - 1;
                              return next < 0 ? len - 1 : next;
                            });
                          } else if (e.key === 'Enter') {
                            e.preventDefault();
                            const idx = productDropdownActiveIndex >= 0 ? productDropdownActiveIndex : 0;
                            const selected = productNameProducts[idx];
                            if (selected) {
                              const available = Number(selected.Available) || 0;
                              const isTraceable = selected.isTraceable || false;
                              const isOutOfStock = available <= 0 && isTraceable;
                              if (!isOutOfStock) {
                                handleNameProductSelect(selected);
                              }
                            }
                          }
                        }}
                        placeholder="Search product name..."
                        type="text"
                      />
                      {showProductNameDropdown && productNameSearchTerm.length > 0 && (
                        <div ref={productNameDropdownRef} className="absolute left-0 top-full mt-1 z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {productNameSearchTerm !== debouncedProductNameSearch ? (
                            <div className="p-2 text-xs text-gray-500">Typing...</div>
                          ) : isProductNameLoading ? (
                            <div className="p-2 text-xs text-gray-500">Loading...</div>
                          ) : productNameProducts.length > 0 ? (
                            productNameProducts.map((product: any, index: number) => {
                              const available = Number(product.Available) || 0;
                              const isTraceable = product.isTraceable || false;
                              const isOutOfStock = available <= 0 && isTraceable;

                              return (
                                <div
                                  key={`name-${product.Barcode}`}
                                  className={`dropdown-item p-2 text-xs border-b last:border-b-0 transition flex items-center gap-2 ${index === productDropdownActiveIndex ? 'active' : ''} ${isOutOfStock
                                    ? 'bg-gray-100 cursor-not-allowed opacity-60'
                                    : 'hover:bg-blue-100 cursor-pointer'
                                    }`}
                                  onClick={() => {
                                    if (!isOutOfStock) {
                                      handleNameProductSelect(product);
                                    }
                                  }}
                                >
                                  <img
                                    src={product.imgstr || '/placeholder.svg'}
                                    alt={product.Title}
                                    className="w-8 h-8 object-contain rounded border"
                                    onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
                                  />
                                  <div>
                                    <div className="font-medium">{product.Title}</div>
                                    <div className="text-gray-600">{product.Barcode}</div>
                                    {isOutOfStock && (
                                      <div className="text-red-500 font-semibold text-xs">Out of Stock</div>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <div className="p-2 text-xs text-gray-500">No products found</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Rep Name */}
                <div className="hidden lg:block min-w-0 flex-1 lg:min-w-0 relative rep-name-dropdown">
                  <div className="flex items-center justify-between mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1.5 2xl:mb-2">
                    <label className="block text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700">Rep Name</label>
                    <button type="button" className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs text-cyan-700 hover:underline" onClick={() => setShowRepManager(true)}>New</button>
                  </div>
                  <input
                    className="w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                    value={repNameSearchTerm}
                    onChange={e => {
                      setRepNameSearchTerm(e.target.value);
                      setRepName(e.target.value);
                      setShowRepNameDropdown(true);
                    }}
                    onFocus={() => setShowRepNameDropdown(true)}
                    placeholder="Enter rep name"
                    type="text"
                  />
                  {showRepNameDropdown && (
                    <div className="absolute z-50 w-64 max-h-48 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg mt-1">
                      {isRepresentativesLoading ? (
                        <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
                      ) : representativesError ? (
                        <div className="px-3 py-2 text-sm text-red-500">Error loading representatives</div>
                      ) : filteredRepresentatives.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">No representatives found</div>
                      ) : (
                        filteredRepresentatives.map((rep, index) => (
                          <div
                            key={index}
                            className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setRepName(rep.Representative);
                              setRepNameSearchTerm(rep.Representative);
                              setShowRepNameDropdown(false);
                            }}
                          >
                            {rep.Representative}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  <RepresentativeManager
                    open={showRepManager}
                    onOpenChange={setShowRepManager}
                    onAnyChange={() => {
                      try {
                        if (queryClient) {
                          queryClient.invalidateQueries({ queryKey: ['representatives'] });
                        }
                      } catch { }
                    }}
                  />
                </div>
                {/* Location */}
                <div className="min-w-0 flex-1 lg:min-w-0">
                  <label className="block text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-2">Location</label>
                  <select
                    className="w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400 appearance-none transition"
                    value={customer.Location}
                    onChange={e => {
                      const newLoc = e.target.value;
                      if (products.length > 0 && newLoc !== customer.Location) {
                        setPrevLocation(customer.Location); // capture current location so Cancel restores it
                        setPendingLocation(newLoc);
                        setShowLocationConfirm(true);
                      } else {
                        setCustomer(f => ({ ...f, Location: newLoc }));
                        setPrevLocation(newLoc);
                      }
                    }}
                  >
                    {locationsError && <option value="">Error loading locations</option>}
                    {locations && locations.map((loc: any) => (
                      <option key={loc.ID} value={loc.ID}>{loc.Name}</option>
                    ))}
                  </select>
                </div>

                {/* Select Company */}
                <div className="min-w-0 flex-1 lg:min-w-0">
                  <label className="block text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-2">Select Company</label>
                  <select
                    className="w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400 appearance-none transition"
                    value={customer.SelectCompany}
                    onChange={e => {
                      const newCompany = e.target.value;
                      if (products.length > 0 && newCompany !== customer.SelectCompany) {
                        setPendingCompany(newCompany);
                        setShowCompanyConfirm(true);
                      } else {
                        const newState = getStateFromCompany(newCompany);
                        setCustomer(f => ({
                          ...f,
                          SelectCompany: newCompany,
                          State: newState // Automatically set state from selected company
                        }));
                        setPrevCompany(newCompany);
                      }
                    }}
                  >
                    {companiesError && <option value="">Error loading companies</option>}
                    {companies && companies.map((company: any) => (
                      <option key={company.Id} value={company.Id}>{company.Name}</option>
                    ))}
                  </select>
                </div>

                {/* Partners - Conditionally shown based on settings */}
                {salesInvoiceSettings.invoicePartnerRequired && (
                  <div className="flex flex-col gap-1 sm:gap-2 md:gap-2 lg:gap-1 xl:gap-1.5 2xl:gap-2 min-w-0 flex-1 lg:min-w-0">
                    <label className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700">Partners</label>
                    <select
                      className="w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400 appearance-none transition"
                      value={customer.Partner}
                      onChange={e => {
                        const newPartner = e.target.value;
                        if (products.length > 0 && newPartner !== customer.Partner) {
                          setPendingPartner(newPartner);
                          setShowPartnerConfirm(true);
                        } else {
                          setCustomer(f => ({ ...f, Partner: newPartner }));
                          setSelectedPartner(newPartner);
                          setPrevPartner(newPartner);
                        }
                      }}
                    >
                      {partnersError && <option value="">Error loading partners</option>}
                      {partners && partners.map((partner: any) => (
                        <option key={partner.Id} value={partner.Id}>{partner.PartnerName}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              {/* Product More - collapsible section for Search In Table, eComm #, Quote No, KM, VEHICLE NO */}
              <div className="mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1 2xl:mb-1.5">
                <button type="button" className="text-blue-600 underline text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs" onClick={() => setShowMoreProductFields(v => !v)}>{showMoreProductFields ? 'Hide' : 'More'} ▼</button>
              </div>
              {showMoreProductFields && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 lg:gap-1.5 xl:gap-2 2xl:gap-2 mb-1 sm:mb-3 md:mb-4 lg:mb-1 xl:mb-2 2xl:mb-2 animate-fade-in">
                  {/* Search In Table */}
                  <div className="flex flex-col gap-1 lg:gap-1 min-w-0 flex-1">
                    <label className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700">Search In Table</label>
                    <div className="relative w-full">
                      <svg className="w-4 h-4 text-gray-500 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        className="w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 pl-6 sm:pl-7 md:pl-7 lg:pl-6 xl:pl-6.5 2xl:pl-7 pr-1.5 sm:pr-2 md:pr-2 lg:pr-1.5 xl:pr-1.5 2xl:pr-2 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                        value={productTableSearch}
                        onChange={e => setProductTableSearch(e.target.value)}
                        placeholder="Search in table..."
                        type="text"
                      />
                    </div>
                  </div>
                  {/* eComm # */}
                  <div className="flex flex-col gap-1 lg:gap-1 min-w-0 flex-1">
                    <label className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700">eComm #</label>
                    <input
                      type="text"
                      className="w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                      value={customer.eCommOrderID}
                      onChange={e => {
                        const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                        setCustomer(f => ({ ...f, eCommOrderID: value }));
                      }}
                      placeholder="Enter eComm #"
                      maxLength={50}
                    />
                  </div>
                  {/* Quote No */}
                  <div className="flex flex-col gap-1 lg:gap-1 min-w-0 flex-1">
                    <label className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700">Quote No</label>
                    <input
                      className="w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                      value={invoiceForm.QuoteNo}
                      onChange={e => setInvoiceForm(f => ({ ...f, QuoteNo: e.target.value }))}
                      placeholder="Quote no"
                      type="text"
                    />
                  </div>
                  {/* Tag 1 (KM) */}
                  {salesInvoiceSettings.tag1Name && (
                    <div className="flex flex-col gap-1 lg:gap-1 min-w-0 flex-1">
                      <label className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700">{salesInvoiceSettings.tag1Name}</label>
                      <input
                        className="w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                        value={invoiceForm.Mode}
                        onChange={e => setInvoiceForm(f => ({ ...f, Mode: e.target.value }))}
                        placeholder={salesInvoiceSettings.tag1Name}
                        type="text"
                      />
                    </div>
                  )}
                  {/* Tag 2 (VEHICLE NO) */}
                  {salesInvoiceSettings.tag2Name && (
                    <div className="flex flex-col gap-1 lg:gap-1 min-w-0 flex-1">
                      <label className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700">{salesInvoiceSettings.tag2Name}</label>
                      <input
                        className="w-full h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 py-0.5 sm:py-1 md:py-1 lg:py-0.5 xl:py-0.5 2xl:py-1 border border-gray-300 rounded-md text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                        value={invoiceForm.VehicleNo}
                        onChange={e => setInvoiceForm(f => ({ ...f, VehicleNo: e.target.value }))}
                        placeholder={salesInvoiceSettings.tag2Name}
                        type="text"
                      />
                    </div>
                  )}
                </div>
              )}
              {productAddMessage && (
                <div className={`text-xs mt-1 ml-1 ${productAddMessageType === 'success' ? 'text-green-600' : 'text-red-600'}`}>{productAddMessage}</div>
              )}
              <div className="flex-1 min-h-0">
                <div className={cn(
                  "overflow-x-auto overflow-y-auto border rounded shadow bg-white max-h-[calc(100vh-320px)]",
                  filteredProducts.length === 0
                    ? "min-h-[100px] sm:min-h-[120px] lg:min-h-[120px]"
                    : "min-h-[320px] sm:min-h-[360px] md:min-h-[380px] lg:min-h-[360px] xl:min-h-[380px] 2xl:min-h-[420px]"
                )}>
                  <table className="min-w-full border text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs rounded-lg overflow-hidden">
                    <thead className="bg-blue-100 text-blue-900 font-bold">
                      <tr>
                        <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">S.No</th>
                        {salesInvoiceSettings.imageInInvoice && <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">Image</th>}
                        <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">P.Name</th>
                        {/* <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">Extra1</th> */}
                        {/* <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">Extra2</th> */}
                        <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">Barcode</th>
                        {!isNonGSTCompany && <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">HSN</th>}
                        {showSerialField && <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">{serialText}</th>}
                        <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">MRP</th>
                        {/* <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">Unit Price</th> */}
                        <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">Qty</th>
                        <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">Dis%</th>
                        <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">Dis</th>
                        {!isNonGSTCompany && <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">{isIGST ? 'IGST' : 'GST'}</th>}
                        {!isNonGSTCompany && <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">Taxable Value</th>}
                        {!isNonGSTCompany && <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">Cess</th>}
                        {/* Cost Price Column - Conditionally shown based on settings */}
                        {salesInvoiceSettings.showCostPrice && <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">Cost Price</th>}
                        <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">Total</th>
                        <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">Del. Qty</th>
                        <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">Offer Price</th>
                        <th className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.length === 0 ? (
                        <tr>
                          <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2 text-center bg-white" colSpan={(isNonGSTCompany ? (salesInvoiceSettings.showCostPrice ? 14 : 13) : (salesInvoiceSettings.showCostPrice ? 18 : 17)) - (salesInvoiceSettings.imageInInvoice ? 0 : 1) - (showSerialField ? 0 : 1)}>No items</td>
                        </tr>
                      ) : (
                        <>
                          {filteredProducts.map((prod, idx) => {
                          const mrp = parseFloat(prod.MRP) || 0;
                          const offer = parseFloat(prod.OfferPrice) || 0;
                          const qty = parseFloat(prod.Qty) || 1;
                          const discount = mrp ? (mrp - offer) : 0;
                          const discountPer = mrp ? (100 * (mrp - offer) / mrp) : 0;
                          const isSerialProduct = !!(prod?.SerialDetails?.length) || prod?.hasSerial === 1 || prod?.HasSerialFlag === 1 || !!prod?.SerialNumber;
                          return (
                            <tr
                              key={prod.Barcode || idx}
                              className={idx % 2 === 0 ? 'bg-white' : 'bg-blue-50'}
                            >
                              <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2 text-center">{idx + 1}</td>
                              {salesInvoiceSettings.imageInInvoice && (
                                <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">
                                  <img
                                    src={prod.imgstr || '/placeholder.svg'}
                                    alt={prod.Title}
                                    className="w-12 h-12 object-contain rounded border"
                                    onError={e => { e.currentTarget.src = '/placeholder.svg'; }}
                                  />
                                </td>
                              )}
                              <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">{prod.Title}</td>
                              {/* <td className="border px-3 py-2">
                          <input
                            type="text"
                            className="w-18 sm:w-20 md:w-20 lg:w-18 xl:w-19 2xl:w-20 border rounded px-0.5 sm:px-1 md:px-1 lg:px-0.5 xl:px-0.5 2xl:px-1 py-0.5 text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs"
                            value={prod.Extra1 ?? ''}
                            onChange={e => handleProductFieldChange(idx, 'Extra1', e.target.value)}
                          />
                        </td> */}
                              {/* <td className="border px-3 py-2">
                          <input
                            type="text"
                            className="w-18 sm:w-20 md:w-20 lg:w-18 xl:w-19 2xl:w-20 border rounded px-0.5 sm:px-1 md:px-1 lg:px-0.5 xl:px-0.5 2xl:px-1 py-0.5 text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs"
                            value={prod.Extra2 ?? ''}
                            onChange={e => handleProductFieldChange(idx, 'Extra2', e.target.value)}
                          />
                        </td> */}
                              <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">{prod.Barcode}</td>
                              {!isNonGSTCompany && <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">{prod.HSN}</td>}
                              {showSerialField && (
                                <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">
                                  {prod.SerialNumber || prod.SerialDetails?.[0]?.SerialNumber || '-'}
                                </td>
                              )}
                              <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="no-spinner w-18 sm:w-20 md:w-20 lg:w-18 xl:w-19 2xl:w-20 border rounded px-0.5 sm:px-1 md:px-1 lg:px-0.5 xl:px-0.5 2xl:px-1 py-0.5 text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs"
                                  onWheel={e => e.currentTarget.blur()}
                                  value={rawInputValues[`MRP-${idx}`] !== undefined ? rawInputValues[`MRP-${idx}`] : prod.MRP}
                                  onChange={e => {
                                    const value = e.target.value;
                                    setRawInputValues(prev => ({ ...prev, [`MRP-${idx}`]: value }));
                                  }}
                                  onFocus={e => {
                                    e.target.select();
                                    if (rawInputValues[`MRP-${idx}`] === undefined) {
                                      setRawInputValues(prev => ({ ...prev, [`MRP-${idx}`]: prod.MRP }));
                                    }
                                  }}
                                  onBlur={() => {
                                    const rawKey = `MRP-${idx}`;
                                    const value = rawInputValues[rawKey] !== undefined ? rawInputValues[rawKey] : prod.MRP;
                                    handleProductFieldChange(idx, 'MRP', value);
                                    setRawInputValues(prev => {
                                      const newVals = { ...prev };
                                      delete newVals[rawKey];
                                      return newVals;
                                    });
                                  }}
                                  onKeyDown={e => handleFieldKeyDown(e, idx, 'MRP')}
                                  data-field="MRP"
                                  data-index={idx}
                                  readOnly={!canEditPrice}
                                />
                              </td>
                              {/* <td className="border px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="w-18 sm:w-20 md:w-20 lg:w-18 xl:w-19 2xl:w-20 border rounded px-0.5 sm:px-1 md:px-1 lg:px-0.5 xl:px-0.5 2xl:px-1 py-0.5 text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs"
                            value={rawInputValues[`UnitPrice-${idx}`] !== undefined ? rawInputValues[`UnitPrice-${idx}`] : (prod.UnitPrice || '')}
                            onChange={e => {
                              const value = e.target.value;
                              setRawInputValues(prev => ({ ...prev, [`UnitPrice-${idx}`]: value }));
                            }}
                            onFocus={e => {
                              e.target.select();
                              if (rawInputValues[`UnitPrice-${idx}`] === undefined) {
                                setRawInputValues(prev => ({ ...prev, [`UnitPrice-${idx}`]: prod.UnitPrice || '' }));
                              }
                            }}
                            onBlur={() => {
                              const rawKey = `UnitPrice-${idx}`;
                              const value = rawInputValues[rawKey] !== undefined ? rawInputValues[rawKey] : (prod.UnitPrice || '');
                              const originalUnitPrice = parseFloat(prod.OriginalUnitPrice || '0') || 0;
                              const enteredUnitPrice = parseFloat(value) || 0;
                              
                              // Check if entered value is less than original (only validate if original is greater than 0)
                              if (enteredUnitPrice > 0 && originalUnitPrice > 0 && enteredUnitPrice < originalUnitPrice) {
                                setUnitPriceValidationData({
                                  index: idx,
                                  enteredValue: value,
                                  originalValue: prod.OriginalUnitPrice || '0.00'
                                });
                                setShowUnitPriceValidation(true);
                              } else {
                                // Update directly if no validation needed
                                handleProductFieldChange(idx, 'UnitPrice', value);
                                setRawInputValues(prev => {
                                  const newVals = { ...prev };
                                  delete newVals[rawKey];
                                  return newVals;
                                });
                              }
                            }}
                            onKeyDown={e => handleFieldKeyDown(e, idx, 'UnitPrice')}
                            data-field="UnitPrice"
                            data-index={idx}
                          />
                        </td> */}
                              <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">
                                <input
                                  type="number"
                                  min="1"
                                  className={`no-spinner w-14 sm:w-16 md:w-16 lg:w-14 xl:w-15 2xl:w-16 border rounded px-0.5 sm:px-1 md:px-1 lg:px-0.5 xl:px-0.5 2xl:px-1 py-0.5 text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs ${isSerialProduct ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                  inputMode="numeric"
                                  value={rawInputValues[`Qty-${idx}`] !== undefined ? rawInputValues[`Qty-${idx}`] : (prod.Qty ?? 1)}
                                  onChange={e => {
                                    const value = e.target.value;
                                    setRawInputValues(prev => ({ ...prev, [`Qty-${idx}`]: value }));
                                  }}
                                  onFocus={e => {
                                    e.target.select();
                                    if (rawInputValues[`Qty-${idx}`] === undefined) {
                                      setRawInputValues(prev => ({ ...prev, [`Qty-${idx}`]: String(prod.Qty ?? 1) }));
                                    }
                                  }}
                                  onBlur={() => {
                                    const rawKey = `Qty-${idx}`;
                                    const value = rawInputValues[rawKey] !== undefined ? rawInputValues[rawKey] : String(prod.Qty ?? 1);
                                    handleProductFieldChange(idx, 'Qty', value);
                                    setRawInputValues(prev => {
                                      const newVals = { ...prev };
                                      delete newVals[rawKey];
                                      return newVals;
                                    });
                                  }}
                                  onKeyDown={e => {
                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                      e.preventDefault();
                                      return;
                                    }
                                    handleFieldKeyDown(e, idx, 'Qty');
                                  }}
                                  onWheel={e => e.preventDefault()}
                                  data-field="Qty"
                                  data-index={idx}
                                  readOnly={isSerialProduct}
                                />
                              </td>
                              <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="no-spinner w-14 sm:w-16 md:w-16 lg:w-14 xl:w-15 2xl:w-16 border rounded px-0.5 sm:px-1 md:px-1 lg:px-0.5 xl:px-0.5 2xl:px-1 py-0.5 text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs"
                                  onWheel={e => e.currentTarget.blur()}
                                  value={rawInputValues[`DiscountPer-${idx}`] !== undefined ? rawInputValues[`DiscountPer-${idx}`] : (prod.DiscountPer ?? discountPer.toFixed(2))}
                                  onChange={e => {
                                    const value = e.target.value;
                                    // Store raw input value for natural typing (no recalculation)
                                    setRawInputValues(prev => ({ ...prev, [`DiscountPer-${idx}`]: value }));
                                  }}
                                  onFocus={e => {
                                    e.target.select();
                                    // Initialize raw value if needed
                                    if (rawInputValues[`DiscountPer-${idx}`] === undefined) {
                                      setRawInputValues(prev => ({ ...prev, [`DiscountPer-${idx}`]: prod.DiscountPer ?? discountPer.toFixed(2) }));
                                    }
                                  }}
                                  onBlur={() => {
                                    // Trigger recalculation on blur
                                    const rawKey = `DiscountPer-${idx}`;
                                    const value = rawInputValues[rawKey] !== undefined ? rawInputValues[rawKey] : (prod.DiscountPer ?? discountPer.toFixed(2));
                                    handleProductFieldChange(idx, 'DiscountPer', value);
                                    // Clear raw input value on blur so formatted value shows
                                    setRawInputValues(prev => {
                                      const newVals = { ...prev };
                                      delete newVals[rawKey];
                                      return newVals;
                                    });
                                  }}
                                  onKeyDown={e => handleFieldKeyDown(e, idx, 'DiscountPer')}
                                  data-field="DiscountPer"
                                  data-index={idx}
                                  readOnly={!canEditDiscount}
                                />
                              </td>
                              <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="no-spinner w-14 sm:w-16 md:w-16 lg:w-14 xl:w-15 2xl:w-16 border rounded px-0.5 sm:px-1 md:px-1 lg:px-0.5 xl:px-0.5 2xl:px-1 py-0.5 text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs"
                                  onWheel={e => e.currentTarget.blur()}
                                  value={rawInputValues[`Discount-${idx}`] !== undefined ? rawInputValues[`Discount-${idx}`] : (prod.Discount ?? discount.toFixed(2))}
                                  readOnly={!canEditDiscount}
                                  onChange={e => {
                                    const value = e.target.value;
                                    // Store raw input value for natural typing (no recalculation)
                                    setRawInputValues(prev => ({ ...prev, [`Discount-${idx}`]: value }));
                                  }}
                                  onFocus={e => {
                                    e.target.select();
                                    // Initialize raw value if needed
                                    if (rawInputValues[`Discount-${idx}`] === undefined) {
                                      setRawInputValues(prev => ({ ...prev, [`Discount-${idx}`]: prod.Discount ?? discount.toFixed(2) }));
                                    }
                                  }}
                                  onBlur={() => {
                                    // Trigger recalculation on blur
                                    const rawKey = `Discount-${idx}`;
                                    const value = rawInputValues[rawKey] !== undefined ? rawInputValues[rawKey] : (prod.Discount ?? discount.toFixed(2));
                                    handleProductFieldChange(idx, 'Discount', value);
                                    // Clear raw input value on blur so formatted value shows
                                    setRawInputValues(prev => {
                                      const newVals = { ...prev };
                                      delete newVals[rawKey];
                                      return newVals;
                                    });
                                  }}
                                  onKeyDown={e => handleFieldKeyDown(e, idx, 'Discount')}
                                  data-field="Discount"
                                  data-index={idx}
                                />
                              </td>
                              {!isNonGSTCompany && <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">{
                                (() => {
                                  // Always recalculate to ensure fresh display
                                  if (prod.GSTAmount !== undefined && prod.GSTAmount !== null) {
                                    return prod.GSTAmount;
                                  }
                                  // Fallback calculation
                                  const offerPrice = parseFloat(prod.OfferPrice) || 0;
                                  const qty = parseFloat(prod.Qty || prod.Quantity || 1);
                                  const gstPercent = parseFloat(prod.GST) || 0;
                                  const cessPercent = parseFloat(prod.CessPer) || 0;
                                  const taxableValue = (offerPrice * qty) / (1 + (gstPercent + cessPercent) / 100);
                                  const gstAmount = taxableValue * gstPercent / 100;
                                  return gstAmount.toFixed(2);
                                })()
                              }</td>}
                              {!isNonGSTCompany && <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="no-spinner w-18 sm:w-20 md:w-20 lg:w-18 xl:w-19 2xl:w-20 border rounded px-0.5 sm:px-1 md:px-1 lg:px-0.5 xl:px-0.5 2xl:px-1 py-0.5 text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs"
                                  onWheel={e => e.currentTarget.blur()}
                                  value={rawInputValues[`TaxableValue-${idx}`] !== undefined ? rawInputValues[`TaxableValue-${idx}`] : (prod.TaxableValue || (() => {
                                    const offerPrice = parseFloat(prod.OfferPrice) || 0;
                                    const qty = parseFloat(prod.Qty || prod.Quantity || 1);
                                    const gstPercent = parseFloat(prod.GST) || 0;
                                    const cessPercent = parseFloat(prod.CessPer) || 0;
                                    const taxableValue = (offerPrice * qty) / (1 + (gstPercent + cessPercent) / 100);
                                    return taxableValue.toFixed(2);
                                  })())}
                                  onChange={e => {
                                    const value = e.target.value;
                                    setRawInputValues(prev => ({ ...prev, [`TaxableValue-${idx}`]: value }));
                                  }}
                                  onFocus={e => {
                                    e.target.select();
                                    if (rawInputValues[`TaxableValue-${idx}`] === undefined) {
                                      const offerPrice = parseFloat(prod.OfferPrice) || 0;
                                      const qty = parseFloat(prod.Qty || prod.Quantity || 1);
                                      const gstPercent = parseFloat(prod.GST) || 0;
                                      const cessPercent = parseFloat(prod.CessPer) || 0;
                                      const taxableValue = (offerPrice * qty) / (1 + (gstPercent + cessPercent) / 100);
                                      setRawInputValues(prev => ({ ...prev, [`TaxableValue-${idx}`]: prod.TaxableValue || taxableValue.toFixed(2) }));
                                    }
                                  }}
                                  onBlur={() => {
                                    const rawKey = `TaxableValue-${idx}`;
                                    const value = rawInputValues[rawKey] !== undefined ? rawInputValues[rawKey] : (prod.TaxableValue || '0.00');
                                    handleProductFieldChange(idx, 'TaxableValue', value);
                                    setRawInputValues(prev => {
                                      const newVals = { ...prev };
                                      delete newVals[rawKey];
                                      return newVals;
                                    });
                                  }}
                                  onKeyDown={e => handleFieldKeyDown(e, idx, 'TaxableValue')}
                                  data-field="TaxableValue"
                                  data-index={idx}
                                />
                              </td>}
                              {!isNonGSTCompany && <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">{
                                prod.CessAmount || (() => {
                                  // Cess Amount = Taxable Value * CessPercent / 100
                                  const offerPrice = parseFloat(prod.OfferPrice) || 0;
                                  const qty = parseFloat(prod.Qty || prod.Quantity || 1);
                                  const gstPercent = parseFloat(prod.GST) || 0;
                                  const cessPercent = parseFloat(prod.CessPer) || 0;
                                  const taxableValue = (offerPrice * qty) / (1 + (gstPercent + cessPercent) / 100);
                                  const cessAmount = taxableValue * cessPercent / 100;
                                  return cessAmount.toFixed(2);
                                })()
                              }</td>}
                              {/* Cost Price Cell - Conditionally shown based on settings */}
                              {salesInvoiceSettings.showCostPrice && <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="no-spinner w-18 sm:w-20 md:w-20 lg:w-18 xl:w-19 2xl:w-20 border rounded px-0.5 sm:px-1 md:px-1 lg:px-0.5 xl:px-0.5 2xl:px-1 py-0.5 text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs bg-gray-100"
                                  onWheel={e => e.currentTarget.blur()}
                                  value={prod.CostPrice || '0.00'}
                                  readOnly
                                  data-field="CostPrice"
                                  data-index={idx}
                                />
                              </td>}
                              <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">
                                {prod.Total || (() => {
                                  // Total = Taxable Value + GST Amount + Cess Amount
                                  const offerPrice = parseFloat(prod.OfferPrice) || 0;
                                  const qty = parseFloat(prod.Qty || prod.Quantity || 1);
                                  const gstPercent = parseFloat(prod.GST) || 0;
                                  const cessPercent = parseFloat(prod.CessPer) || 0;
                                  const taxableValue = (offerPrice * qty) / (1 + (gstPercent + cessPercent) / 100);
                                  const gstAmount = taxableValue * gstPercent / 100;
                                  const cessAmount = taxableValue * cessPercent / 100;
                                  const total = taxableValue + gstAmount + cessAmount;
                                  return total.toFixed(2);
                                })()}
                              </td>
                              <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">
                                <input
                                  type="number"
                                  min="0"
                                  className="no-spinner w-14 sm:w-16 md:w-16 lg:w-14 xl:w-15 2xl:w-16 border rounded px-0.5 sm:px-1 md:px-1 lg:px-0.5 xl:px-0.5 2xl:px-1 py-0.5 text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs"
                                  onWheel={e => e.currentTarget.blur()}
                                  value={!invoiceForm.Delivered ? 0 : (rawInputValues[`DelQty-${idx}`] !== undefined ? rawInputValues[`DelQty-${idx}`] : (prod.DelQty ?? 1))}
                                  onChange={e => {
                                    const value = e.target.value;
                                    if (parseFloat(value) < 0) return;
                                    setRawInputValues(prev => ({ ...prev, [`DelQty-${idx}`]: value }));
                                  }}
                                  onFocus={e => {
                                    e.target.select();
                                    if (rawInputValues[`DelQty-${idx}`] === undefined) {
                                      setRawInputValues(prev => ({ ...prev, [`DelQty-${idx}`]: String(prod.DelQty ?? 1) }));
                                    }
                                  }}
                                  onBlur={() => {
                                    const rawKey = `DelQty-${idx}`;
                                    const value = rawInputValues[rawKey] !== undefined ? rawInputValues[rawKey] : String(prod.DelQty ?? 1);
                                    handleDelQtyChange(idx, value);
                                    setRawInputValues(prev => {
                                      const newVals = { ...prev };
                                      delete newVals[rawKey];
                                      return newVals;
                                    });
                                  }}
                                  onKeyDown={e => handleFieldKeyDown(e, idx, 'DelQty')}
                                  data-field="DelQty"
                                  data-index={idx}
                                  disabled={!invoiceForm.Delivered}
                                />
                              </td>
                              <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="no-spinner w-18 sm:w-20 md:w-20 lg:w-18 xl:w-19 2xl:w-20 border rounded px-0.5 sm:px-1 md:px-1 lg:px-0.5 xl:px-0.5 2xl:px-1 py-0.5 text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs"
                                  onWheel={e => e.currentTarget.blur()}
                                  value={rawInputValues[`OfferPrice-${idx}`] !== undefined ? rawInputValues[`OfferPrice-${idx}`] : (prod.OfferPrice !== undefined && prod.OfferPrice !== null ? prod.OfferPrice : '')}
                                  onChange={e => {
                                    const value = e.target.value;
                                    setRawInputValues(prev => ({ ...prev, [`OfferPrice-${idx}`]: value }));
                                  }}
                                  onFocus={e => {
                                    e.target.select();
                                    if (rawInputValues[`OfferPrice-${idx}`] === undefined) {
                                      setRawInputValues(prev => ({ ...prev, [`OfferPrice-${idx}`]: (prod.OfferPrice !== undefined && prod.OfferPrice !== null ? prod.OfferPrice : '') }));
                                    }
                                  }}
                                  onBlur={() => {
                                    const rawKey = `OfferPrice-${idx}`;
                                    const value = rawInputValues[rawKey] !== undefined ? rawInputValues[rawKey] : (prod.OfferPrice !== undefined && prod.OfferPrice !== null ? prod.OfferPrice : '');
                                    handleProductFieldChange(idx, 'OfferPrice', value);
                                    setRawInputValues(prev => {
                                      const newVals = { ...prev };
                                      delete newVals[rawKey];
                                      return newVals;
                                    });
                                  }}
                                  onKeyDown={e => handleFieldKeyDown(e, idx, 'OfferPrice')}
                                  data-field="OfferPrice"
                                  data-index={idx}
                                  readOnly={!canEditPrice}
                                />
                              </td>
                              <td className="border px-1.5 sm:px-3 md:px-3 lg:px-1.5 xl:px-2 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2">
                                <Button
                                  className="h-6 sm:h-8 md:h-8 lg:h-6 xl:h-7 2xl:h-8 px-3 sm:px-4 md:px-4 lg:px-3 xl:px-3.5 2xl:px-4 rounded-md font-medium shadow bg-red-500 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 transition text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs"
                                  onClick={() => handleRemoveProduct(idx)}
                                >
                                  ❌
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                          {filteredProducts.length === 1 && (
                            <tr>
                              <td
                                colSpan={(isNonGSTCompany ? (salesInvoiceSettings.showCostPrice ? 14 : 13) : (salesInvoiceSettings.showCostPrice ? 18 : 17)) - (salesInvoiceSettings.imageInInvoice ? 0 : 1) - (showSerialField ? 0 : 1)}
                                className="border-t-2 border-gray-400 p-0 h-px leading-none"
                              />
                            </tr>
                          )}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="mt-1 sm:mt-2 md:mt-2 lg:mt-1 xl:mt-1.5 2xl:mt-2 flex items-center gap-1.5 sm:gap-2 md:gap-2 lg:gap-1.5 xl:gap-1.5 2xl:gap-2">
                  <div className="ml-auto text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm font-semibold">Total: ₹{Math.round(invoiceTotal)}</div>
                </div>
              </div>
            </div>
            {/* Payment Section */}
            <div className="p-2 sm:p-3 md:p-3 lg:p-2 xl:p-2.5 2xl:p-3 border-b bg-gray-50 flex flex-row items-center gap-2 sm:gap-4 md:gap-4 lg:gap-2 xl:gap-2.5 2xl:gap-4">
              {/* Invoice Details fields in a single row */}
              <div className="flex flex-row items-center gap-2 sm:gap-4 md:gap-4 lg:gap-2 xl:gap-2.5 2xl:gap-4 flex-wrap">
                <label className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-semibold">Invoice Date</label>
                <Input type="date" className="w-28 sm:w-36 md:w-36 lg:w-28 xl:w-32 2xl:w-36 h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm focus:ring-2 focus:ring-cyan-400" value={form.InvoiceDate} onChange={e => setForm(f => ({ ...f, InvoiceDate: e.target.value }))} />
                <label className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-semibold">Delivered</label>
                <input type="checkbox" className="mr-1 sm:mr-2 md:mr-2 lg:mr-1 xl:mr-1.5 2xl:mr-2 w-3 sm:w-4 md:w-4 lg:w-3 xl:w-3.5 2xl:w-4 h-3 sm:h-4 md:h-4 lg:h-3 xl:h-3.5 2xl:h-4" checked={invoiceForm.Delivered} onChange={e => setInvoiceForm(f => ({ ...f, Delivered: e.target.checked }))} />
                {balanceDue !== 0 && (
                  <>
                    <label className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-semibold">Pay before</label>
                    <Input type="date" className="w-28 sm:w-36 md:w-36 lg:w-28 xl:w-32 2xl:w-36 h-7 sm:h-8 md:h-8 lg:h-7 xl:h-7 2xl:h-8 text-xs sm:text-sm md:text-sm lg:text-xs xl:text-xs 2xl:text-sm focus:ring-2 focus:ring-cyan-400" value={form.PayBefore || getPayBeforeDateFromInvoiceDaysSetting(getSetting('Invoice No of Day for Due'))} onChange={e => setForm(f => ({ ...f, PayBefore: e.target.value }))} />
                  </>
                )}
                {/* FIFO Checkbox - Always visible */}
                <>
                  <label className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-semibold">Add in FIFO</label>
                  <input type="checkbox" className="mr-1 sm:mr-2 md:mr-2 lg:mr-1 xl:mr-1.5 2xl:mr-2 w-3 sm:w-4 md:w-4 lg:w-3 xl:w-3.5 2xl:w-4 h-3 sm:h-4 md:h-4 lg:h-3 xl:h-3.5 2xl:h-4" checked={invoiceForm.AddInFIFO} onChange={e => setInvoiceForm(f => ({ ...f, AddInFIFO: e.target.checked }))} />
                </>
              </div>
              {showPaymentModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50 p-2 sm:p-4">
                  <div className="bg-white rounded-xl shadow-2xl w-[90vw] max-w-[100vh] max-h-[70vh] flex flex-col min-h-0">
                    {/* Sticky header: close button always visible */}
                    <div className="flex-shrink-0 flex items-center justify-between gap-2 px-4 pt-3 pb-1 sm:px-5 sm:pt-4 sm:pb-2">
                      <h2 className="font-bold text-lg sm:text-xl text-gray-800">Payment</h2>
                      <button
                        className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-red-500 hover:bg-gray-100 text-xl font-bold focus:outline-none focus:ring-2 focus:ring-cyan-400"
                        aria-label="Close"
                        onClick={() => setShowPaymentModal(false)}
                      >×</button>
                    </div>
                    <hr className="flex-shrink-0 border-gray-200" />
                    {/* Scrollable body: payment fields */}
                    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 sm:px-5 sm:py-4 space-y-3">
                    {/* Split Payment Checkbox - Always visible; default state from settings, user can toggle */}
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="split-payment-checkbox"
                        className="mr-2"
                        checked={payments.splitPaymentEnabled}
                        onChange={e => setPayments(p => ({ ...p, splitPaymentEnabled: e.target.checked }))}
                      />
                      <label htmlFor="split-payment-checkbox" className="text-sm font-semibold">Split Payment</label>
                    </div>

                    {/* Always show only one payment method at a time */}
                    <div className="space-y-3">
                      {/* Payment Method Selection */}
                      <div>
                        <label className="block text-sm font-semibold mb-1">Select Payment Method</label>
                        <select
                          className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                          value={selectedPaymentMethod}
                          onChange={e => {
                            const value = e.target.value;
                            setSelectedPaymentMethod(value);

                            // Fetch customer points when Points payment method is selected
                            if (value === 'Points') {
                              fetchPointsWhenSelected();
                            }

                            if (!payments.splitPaymentEnabled && !editingInvoiceId) {
                              // Reset the three specific payment methods (Coupon, Credit Note, Points) when switching between them
                              if (value !== 'Coupon') {
                                setPayments(p => ({ ...p, couponAmount: '0', couponNo: '', couponAmountLocked: false }));
                                setCouponError('');
                              }
                              if (value !== 'Credit Note') {
                                setPayments(p => ({ ...p, creditNoteAmount: '0', creditNoteNo: '' }));
                                setCreditNoteError('');
                              }
                              if (value !== 'Points') {
                                setPayments(p => ({ ...p, pointsRedeem: '0' }));
                                setPointsError('');
                              }
                            }
                          }}
                        >
                          <option value="Cash">Cash</option>
                          <option value="Card">Card</option>
                          <option value="Wallets">Wallet</option>
                          <option value="Coupon">Coupon</option>
                          <option value="Cheque">Cheque</option>
                          <option value="Points">Points</option>
                          <option value="Credit Note">Credit Note</option>
                        </select>
                      </div>

                      {/* Payment Method Fields */}
                      {payments.splitPaymentEnabled ? (
                        // All fields visible and editable
                        <div className="space-y-4">
                          {selectedPaymentMethod === 'Cash' && (
                            <div className="flex flex-col gap-2">
                              <label className="text-sm font-medium">Cash Amount</label>
                              <input
                                type="number"
                                inputMode="decimal"
                                className="no-spinner w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.cashPayment}
                                onChange={e => setPayments(p => ({ ...p, cashPayment: e.target.value }))}
                                onFocus={e => e.target.select()}
                                onWheel={e => e.currentTarget.blur()}
                                placeholder="Enter amount"
                                disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Cash'}
                              />
                            </div>
                          )}
                          {selectedPaymentMethod === 'Card' && (
                            <div className="flex flex-col gap-2">
                              <label className="text-sm font-medium">Card Amount</label>
                              <input
                                type="number"
                                inputMode="decimal"
                                className="no-spinner w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.cardPayment}
                                onChange={e => setPayments(p => ({ ...p, cardPayment: e.target.value }))}
                                onFocus={e => e.target.select()}
                                onWheel={e => e.currentTarget.blur()}
                                placeholder="Enter amount"
                                disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Card'}
                              />
                            </div>
                          )}
                          {selectedPaymentMethod === 'Wallets' && (
                            <div className="flex flex-col gap-2">
                              <label className="text-sm font-medium">Wallet Provider</label>
                              <select
                                className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.walletProvider}
                                onChange={e => setPayments(p => ({ ...p, walletProvider: e.target.value }))}
                                disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Wallets'}
                              >
                                <option value="GooglePay">GooglePay</option>
                                <option value="PhonePe">PhonePe</option>
                                <option value="AmazonPay">AmazonPay</option>
                                <option value="Paytm">Paytm</option>
                              </select>
                              <label className="text-sm font-medium">Wallet Txn/Ref No.</label>
                              <input
                                type="text"
                                className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.walletTxNo}
                                onChange={e => setPayments(p => ({ ...p, walletTxNo: e.target.value }))}
                                placeholder="Remarks/Ref."
                                disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Wallets'}
                              />
                              <label className="text-sm font-medium">Wallet Amount</label>
                              <input
                                type="number"
                                inputMode="decimal"
                                className="no-spinner w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.walletAmount}
                                onChange={e => setPayments(p => ({ ...p, walletAmount: e.target.value }))}
                                onWheel={e => e.currentTarget.blur()}
                                placeholder="Enter amount"
                                disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Wallets'}
                              />
                            </div>
                          )}
                          {selectedPaymentMethod === 'Cheque' && (
                            <div className="flex flex-col gap-2">
                              <label className="text-sm font-medium">Cheque Amount</label>
                              <input
                                type="number"
                                inputMode="decimal"
                                className="no-spinner w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.chequeAmount}
                                onChange={e => setPayments(p => ({ ...p, chequeAmount: e.target.value }))}
                                onWheel={e => e.currentTarget.blur()}
                                placeholder="Enter amount"
                                disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Cheque'}
                              />
                              <label className="text-sm font-medium">Cheque No.</label>
                              <input
                                type="text"
                                className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.chequeNo}
                                onChange={e => setPayments(p => ({ ...p, chequeNo: e.target.value }))}
                                placeholder="Cheque No."
                                disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Cheque'}
                              />
                            </div>
                          )}
                          {selectedPaymentMethod === 'Coupon' && (
                            <div className="flex flex-col gap-2">
                              <label className="text-sm font-medium">Coupon Code</label>
                              <div className="flex gap-2">
                                <div className="relative flex-1">
                                  <input
                                    type="text"
                                    className="w-full h-10 px-3 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                    value={payments.couponNo}
                                    onChange={e => {
                                      setPayments(p => ({ ...p, couponNo: e.target.value, couponAmount: '0', couponAmountLocked: false }));
                                      setCouponError(''); // Clear error when user types
                                    }}
                                    placeholder="Coupon Code"
                                    disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Coupon'}
                                  />
                                  {payments.couponNo && (
                                    <button
                                      type="button"
                                      aria-label="Clear coupon code"
                                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                      onClick={() => {
                                        setPayments(p => ({ ...p, couponNo: '', couponAmount: '0', couponAmountLocked: false }));
                                        setCouponError('');
                                      }}
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  className="px-2 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                                  onClick={handleCouponValidation}
                                  disabled={isValidatingCoupon || !payments.couponNo}
                                >
                                  {isValidatingCoupon ? 'Validating...' : 'Validate'}
                                </button>
                              </div>
                              {couponError && (
                                <div className="text-red-600 text-sm">{couponError}</div>
                              )}
                              <label className="text-sm font-medium">Coupon Amount</label>
                              <input
                                type="number"
                                inputMode="decimal"
                                className="no-spinner w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.couponAmount || '0'}
                                onChange={e => {
                                  setPayments(p => ({ ...p, couponAmount: p.couponAmount || '0' }));
                                }}
                                onWheel={e => e.currentTarget.blur()}
                                placeholder="0"
                                disabled={true}
                                readOnly={true}
                              />
                            </div>
                          )}
                          {selectedPaymentMethod === 'Credit Note' && (
                            <div className="flex flex-col gap-2">
                              <label className="text-sm font-medium">Credit Note No.</label>
                              <div className="flex gap-2">
                                <div className="relative flex-1">
                                  <input
                                    type="text"
                                    className="w-full h-10 px-3 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                    value={payments.creditNoteNo}
                                    onChange={e => {
                                      setPayments(p => ({ ...p, creditNoteNo: e.target.value }));
                                      setCreditNoteError('');
                                    }}
                                    placeholder="Credit Note No."
                                    disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Credit Note'}
                                  />
                                  {payments.creditNoteNo && (
                                    <button
                                      type="button"
                                      aria-label="Clear credit note number"
                                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                      onClick={() => {
                                        setPayments(p => ({ ...p, creditNoteNo: '', creditNoteAmount: '0' }));
                                        setCreditNoteError('');
                                      }}
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  className="px-2 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                                  onClick={handleCreditNoteValidation}
                                  disabled={isValidatingCreditNote || !payments.creditNoteNo}
                                >
                                  {isValidatingCreditNote ? 'Validating...' : 'Validate'}
                                </button>
                              </div>
                              {creditNoteError && (
                                <div className="text-red-600 text-sm whitespace-pre-line">{creditNoteError}</div>
                              )}
                              <label className="text-sm font-medium">Credit Note Amount</label>
                              <input
                                type="number"
                                inputMode="decimal"
                                className="no-spinner w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.creditNoteAmount || '0'}
                                onChange={e => setPayments(p => ({ ...p, creditNoteAmount: p.creditNoteAmount || '0' }))}
                                onWheel={e => e.currentTarget.blur()}
                                placeholder="0"
                                disabled={true}
                                readOnly={true}
                              />
                            </div>
                          )}
                          {selectedPaymentMethod === 'Points' && (
                            <div className="flex flex-col gap-2">
                              <label className="text-sm font-medium">Points to Redeem</label>
                              <input
                                type="number"
                                inputMode="numeric"
                                className={`no-spinner w-full h-10 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition ${eligiblePoints <= 0 || pointsRedeemRupees > eligiblePoints || (!payments.splitPaymentEnabled && eligiblePoints < invoiceTotal)
                                  ? 'border-red-500 bg-red-50'
                                  : 'border-gray-300'
                                  }`}
                                value={payments.pointsRedeem}
                                onWheel={e => e.currentTarget.blur()}
                                onChange={e => {
                                  const value = e.target.value;
                                  setPayments(p => ({ ...p, pointsRedeem: value }));

                                  // Clear error when user starts typing or when value is 0/empty
                                  if (pointsError || !value || parseFloat(value) === 0) {
                                    setPointsError('');
                                  }

                                  // Validate points when split payment is unchecked
                                  handlePointsValidation(value);
                                }}
                                placeholder="Enter points"
                                disabled={Boolean(editingInvoiceId) || (!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Points')}
                                readOnly={Boolean(editingInvoiceId) || eligiblePoints <= 0 || (!payments.splitPaymentEnabled && eligiblePoints < invoiceTotal)}
                              />
                              {eligiblePoints <= 0 && (
                                <div className="text-red-600 text-sm">No eligible points available</div>
                              )}
                              {!editingInvoiceId && pointsRedeemRupees > eligiblePoints && eligiblePoints > 0 && (
                                <div className="text-red-600 text-sm">Points to redeem cannot exceed eligible points</div>
                              )}
                              {!payments.splitPaymentEnabled && eligiblePoints < invoiceTotal && eligiblePoints > 0 && (
                                <div className="text-red-600 text-sm">Eligible points are not enough for full payment</div>
                              )}
                              {pointsError && (
                                <div className="text-red-600 text-sm">{pointsError}</div>
                              )}
                              <div className="grid grid-cols-2 gap-3">
                                <div className="flex flex-col gap-1">
                                  <label className="text-sm font-medium">Eligible Points</label>
                                  <input
                                    type="text"
                                    className="no-spinner w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition bg-gray-50"
                                    value={formattedEligiblePoints}
                                    readOnly
                                  />
                                </div>
                                <div className="flex flex-col gap-1">
                                  <label className="text-sm font-medium">Remaining Points</label>
                                  <input
                                    type="text"
                                    className="no-spinner w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition bg-gray-50"
                                    value={formattedRemainingPoints}
                                    readOnly
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        // Only selected method visible
                        <div className="space-y-4">
                          {selectedPaymentMethod === 'Cash' && (
                            <div className="flex flex-col gap-2">
                              <label className="text-sm font-medium">Cash Amount</label>
                              <input
                                type="number"
                                inputMode="decimal"
                                className="no-spinner w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.cashPayment}
                                onChange={e => setPayments(p => ({ ...p, cashPayment: e.target.value }))}
                                onFocus={e => e.target.select()}
                                onWheel={e => e.currentTarget.blur()}
                                placeholder="Enter amount"
                                disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Cash'}
                              />
                            </div>
                          )}
                          {selectedPaymentMethod === 'Card' && (
                            <div className="flex flex-col gap-2">
                              <label className="text-sm font-medium">Card Amount</label>
                              <input
                                type="number"
                                inputMode="decimal"
                                className="no-spinner w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.cardPayment}
                                onChange={e => setPayments(p => ({ ...p, cardPayment: e.target.value }))}
                                onFocus={e => e.target.select()}
                                onWheel={e => e.currentTarget.blur()}
                                placeholder="Enter amount"
                                disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Card'}
                              />
                            </div>
                          )}
                          {selectedPaymentMethod === 'Wallets' && (
                            <div className="flex flex-col gap-2">
                              <label className="text-sm font-medium">Wallet Provider</label>
                              <select
                                className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.walletProvider}
                                onChange={e => setPayments(p => ({ ...p, walletProvider: e.target.value }))}
                                disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Wallets'}
                              >
                                <option value="GooglePay">GooglePay</option>
                                <option value="PhonePe">PhonePe</option>
                                <option value="AmazonPay">AmazonPay</option>
                                <option value="Paytm">Paytm</option>
                              </select>
                              <label className="text-sm font-medium">Wallet Txn/Ref No.</label>
                              <input
                                type="text"
                                className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.walletTxNo}
                                onChange={e => setPayments(p => ({ ...p, walletTxNo: e.target.value }))}
                                placeholder="Remarks/Ref."
                                disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Wallets'}
                              />
                              <label className="text-sm font-medium">Wallet Amount</label>
                              <input
                                type="number"
                                inputMode="decimal"
                                className="no-spinner w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.walletAmount}
                                onChange={e => setPayments(p => ({ ...p, walletAmount: e.target.value }))}
                                onWheel={e => e.currentTarget.blur()}
                                placeholder="Enter amount"
                                disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Wallets'}
                              />
                            </div>
                          )}
                          {selectedPaymentMethod === 'Cheque' && (
                            <div className="flex flex-col gap-2">
                              <label className="text-sm font-medium">Cheque Amount</label>
                              <input
                                type="number"
                                inputMode="decimal"
                                className="no-spinner w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.chequeAmount}
                                onChange={e => setPayments(p => ({ ...p, chequeAmount: e.target.value }))}
                                onWheel={e => e.currentTarget.blur()}
                                placeholder="Enter amount"
                                disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Cheque'}
                              />
                              <label className="text-sm font-medium">Cheque No.</label>
                              <input
                                type="text"
                                className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.chequeNo}
                                onChange={e => setPayments(p => ({ ...p, chequeNo: e.target.value }))}
                                placeholder="Cheque No."
                                disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Cheque'}
                              />
                            </div>
                          )}
                          {selectedPaymentMethod === 'Coupon' && (
                            <div className="flex flex-col gap-2">
                              <label className="text-sm font-medium">Coupon Code</label>
                              <div className="flex gap-2">
                                <div className="relative flex-1">
                                  <input
                                    type="text"
                                    className="w-full h-10 px-3 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                    value={payments.couponNo}
                                    onChange={e => {
                                      setPayments(p => ({ ...p, couponNo: e.target.value, couponAmount: '0', couponAmountLocked: false }));
                                      setCouponError(''); // Clear error when user types
                                    }}
                                    placeholder="Coupon Code"
                                    disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Coupon'}
                                  />
                                  {payments.couponNo && (
                                    <button
                                      type="button"
                                      aria-label="Clear coupon code"
                                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                      onClick={() => {
                                        setPayments(p => ({ ...p, couponNo: '', couponAmount: '0', couponAmountLocked: false }));
                                        setCouponError('');
                                      }}
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  className="px-2 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                                  onClick={handleCouponValidation}
                                  disabled={isValidatingCoupon || !payments.couponNo}
                                >
                                  {isValidatingCoupon ? 'Validating...' : 'Validate'}
                                </button>
                              </div>
                              {couponError && (
                                <div className="text-red-600 text-sm">{couponError}</div>
                              )}
                              <label className="text-sm font-medium">Coupon Amount</label>
                              <input
                                type="number"
                                inputMode="decimal"
                                className="no-spinner w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.couponAmount || '0'}
                                onChange={e => {
                                  setPayments(p => ({ ...p, couponAmount: p.couponAmount || '0' }));
                                }}
                                onWheel={e => e.currentTarget.blur()}
                                placeholder="0"
                                disabled={true}
                                readOnly={true}
                              />
                            </div>
                          )}
                          {selectedPaymentMethod === 'Credit Note' && (
                            <div className="flex flex-col gap-2">
                              <label className="text-sm font-medium">Credit Note No.</label>
                              <div className="flex gap-2">
                                <div className="relative flex-1">
                                  <input
                                    type="text"
                                    className="w-full h-10 px-3 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                    value={payments.creditNoteNo}
                                    onChange={e => {
                                      setPayments(p => ({ ...p, creditNoteNo: e.target.value }));
                                      setCreditNoteError('');
                                    }}
                                    placeholder="Credit Note No."
                                    disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Credit Note'}
                                  />
                                  {payments.creditNoteNo && (
                                    <button
                                      type="button"
                                      aria-label="Clear credit note number"
                                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                      onClick={() => {
                                        setPayments(p => ({ ...p, creditNoteNo: '', creditNoteAmount: '0' }));
                                        setCreditNoteError('');
                                      }}
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  className="px-2 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                                  onClick={handleCreditNoteValidation}
                                  disabled={isValidatingCreditNote || !payments.creditNoteNo}
                                >
                                  {isValidatingCreditNote ? 'Validating...' : 'Validate'}
                                </button>
                              </div>
                              {creditNoteError && (
                                <div className="text-red-600 text-sm whitespace-pre-line">{creditNoteError}</div>
                              )}
                              <label className="text-sm font-medium">Credit Note Amount</label>
                              <input
                                type="number"
                                inputMode="decimal"
                                className="no-spinner w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                                value={payments.creditNoteAmount || '0'}
                                onChange={e => setPayments(p => ({ ...p, creditNoteAmount: p.creditNoteAmount || '0' }))}
                                onWheel={e => e.currentTarget.blur()}
                                placeholder="0"
                                disabled={true}
                                readOnly={true}
                              />
                            </div>
                          )}
                          {selectedPaymentMethod === 'Points' && (
                            <div className="flex flex-col gap-2">
                              <label className="text-sm font-medium">Points to Redeem</label>
                              <input
                                type="number"
                                inputMode="numeric"
                                className={`no-spinner w-full h-10 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition ${eligiblePoints <= 0 || pointsRedeemRupees > eligiblePoints || (!payments.splitPaymentEnabled && eligiblePoints < invoiceTotal)
                                  ? 'border-red-500 bg-red-50'
                                  : 'border-gray-300'
                                  }`}
                                value={payments.pointsRedeem}
                                onWheel={e => e.currentTarget.blur()}
                                onChange={e => {
                                  const value = e.target.value;
                                  setPayments(p => ({ ...p, pointsRedeem: value }));

                                  // Clear error when user starts typing or when value is 0/empty
                                  if (pointsError || !value || parseFloat(value) === 0) {
                                    setPointsError('');
                                  }

                                  // Validate points when split payment is unchecked
                                  handlePointsValidation(value);
                                }}
                                placeholder="Enter points"
                                disabled={!payments.splitPaymentEnabled && selectedPaymentMethod !== 'Points'}
                                readOnly={eligiblePoints <= 0 || (!payments.splitPaymentEnabled && eligiblePoints < invoiceTotal)}
                              />
                              {eligiblePoints <= 0 && (
                                <div className="text-red-600 text-sm">No eligible points available</div>
                              )}
                              {pointsRedeemRupees > eligiblePoints && eligiblePoints > 0 && (
                                <div className="text-red-600 text-sm">Points to redeem cannot exceed eligible points</div>
                              )}
                              {!payments.splitPaymentEnabled && eligiblePoints < invoiceTotal && eligiblePoints > 0 && (
                                <div className="text-red-600 text-sm">Eligible points are not enough for full payment</div>
                              )}
                              {pointsError && (
                                <div className="text-red-600 text-sm">{pointsError}</div>
                              )}
                              <div className="grid grid-cols-2 gap-3">
                                <div className="flex flex-col gap-1">
                                  <label className="text-sm font-medium">Eligible Points</label>
                                  <input
                                    type="text"
                                    className="no-spinner w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition bg-gray-50"
                                    value={formattedEligiblePoints}
                                    readOnly
                                  />
                                </div>
                                <div className="flex flex-col gap-1">
                                  <label className="text-sm font-medium">Remaining Points</label>
                                  <input
                                    type="text"
                                    className="no-spinner w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition bg-gray-50"
                                    value={formattedRemainingPoints}
                                    readOnly
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Payment Summary */}
                    <div className="bg-gray-50 rounded-lg p-3 mt-2 flex flex-col gap-1.5">
                      <div className="flex justify-between text-sm">
                        <span>Total:</span>
                        <span className="font-semibold text-gray-800">₹{roundedInvoiceTotal}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Paid:</span>
                        <span className="font-semibold text-green-600">₹{totalPaid.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Balance:</span>
                        <span className="font-semibold text-red-600">₹{balanceDue.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Payment Methods Display - Only show in edit mode */}
                    {isEditMode && editingInvoiceId && (
                      <div className="bg-blue-50 rounded-lg p-4 mt-2 border border-blue-200">
                        <div className="text-sm font-semibold text-blue-900 mb-2">Payment Methods Used:</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {roundPayment(payments.cashPayment) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Cash:</span>
                              <span className="font-semibold text-green-600">₹{roundPayment(payments.cashPayment).toFixed(2)}</span>
                            </div>
                          )}
                          {roundPayment(payments.cardPayment) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Card:</span>
                              <span className="font-semibold text-green-600">₹{roundPayment(payments.cardPayment).toFixed(2)}</span>
                            </div>
                          )}
                          {roundPayment(payments.walletAmount) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Wallet:</span>
                              <span className="font-semibold text-green-600">₹{roundPayment(payments.walletAmount).toFixed(2)}</span>
                            </div>
                          )}
                          {roundPayment(payments.chequeAmount) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Cheque:</span>
                              <span className="font-semibold text-green-600">₹{roundPayment(payments.chequeAmount).toFixed(2)}</span>
                            </div>
                          )}
                          {roundPayment(payments.couponAmount) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Coupon:</span>
                              <span className="font-semibold text-green-600">₹{roundPayment(payments.couponAmount).toFixed(2)}</span>
                            </div>
                          )}
                          {roundPayment(payments.creditNoteAmount) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Credit Note:</span>
                              <span className="font-semibold text-green-600">₹{roundPayment(payments.creditNoteAmount).toFixed(2)}</span>
                            </div>
                          )}
                          {roundPayment(payments.pointsRedeem) > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Points:</span>
                              <span className="font-semibold text-green-600">{Math.round(roundPayment(payments.pointsRedeem))} pts</span>
                            </div>
                          )}
                        </div>

                      </div>
                    )}

                    {/* Pay Before Date Field - Only show when there's a balance (date from "Invoice No of Day for Due" setting) */}
                    {balanceDue > 0 && (
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-semibold">Pay Before</label>
                        <input
                          type="date"
                          className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                          value={form.PayBefore || getPayBeforeDateFromInvoiceDaysSetting(getSetting('Invoice No of Day for Due'))}
                          onChange={e => setForm(f => ({ ...f, PayBefore: e.target.value }))}
                          onFocus={e => e.target.select()}
                        />
                      </div>
                    )}

                    </div>
                    {/* Sticky footer: actions always visible */}
                    <div className="flex-shrink-0 px-4 py-3 sm:px-5 sm:py-4 border-t border-gray-100 bg-gray-50/80 rounded-b-xl">
                      <div className="flex justify-end gap-2">
                        <button
                          className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold text-sm"
                          onClick={() => setShowPaymentModal(false)}
                        >Cancel</button>
                        <button
                          className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-sm"
                          onClick={handlePaymentConfirm}
                        >Confirm Payment</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Summary Section — single container with vertical dividers */}
            <div className="flex-1 w-full min-h-0 px-2 sm:px-3 md:px-3 lg:px-2 xl:px-2.5 2xl:px-3">
              <div className="flex flex-row w-full items-stretch rounded-xl shadow-lg overflow-hidden border border-gray-200 bg-white h-full min-h-[140px]">
                {/* Section 1 (30%) — Summary (yellow) */}
                <div className="w-[30%] flex flex-col justify-center p-2 sm:p-4 md:p-4 lg:p-2 xl:p-3 2xl:p-4 bg-yellow-200 border-r-2 border-gray-300">
                  <div className="flex flex-row gap-4 sm:gap-8 md:gap-8 lg:gap-4 xl:gap-6 2xl:gap-8">
                    <div className="flex flex-col gap-1 sm:gap-2 md:gap-2 lg:gap-1 xl:gap-1.5 2xl:gap-2 flex-1">
                      <div className="flex flex-row justify-between items-center text-[10px] sm:text-[13px] md:text-[13px] lg:text-[10px] xl:text-[11px] 2xl:text-[13px] font-semibold text-yellow-900">
                        <span>GST</span>
                        <span className="font-bold">{summaryGstDisplay.toFixed(2)}</span>
                      </div>
                      <div className="flex flex-row justify-between items-center text-[10px] sm:text-[13px] md:text-[13px] lg:text-[10px] xl:text-[11px] 2xl:text-[13px] font-semibold text-yellow-900">
                        <span>CESS</span>
                        <span className="font-bold">{summaryCessDisplay.toFixed(2)}</span>
                      </div>
                      <div className="flex flex-row justify-between items-center text-[10px] sm:text-[13px] md:text-[13px] lg:text-[10px] xl:text-[11px] 2xl:text-[13px] font-semibold text-yellow-900">
                        <span>Subtotal</span>
                        <span className="font-bold">{summary.subtotal.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 sm:gap-2 md:gap-2 lg:gap-1 xl:gap-1.5 2xl:gap-2 flex-1">
                      <div className="flex flex-row justify-between items-center text-[10px] sm:text-[13px] md:text-[13px] lg:text-[10px] xl:text-[11px] 2xl:text-[13px] font-semibold text-yellow-900">
                        <span>Discount</span>
                        <span className="font-bold">{summary.discount.toFixed(2)}</span>
                      </div>
                      <div className="flex flex-row justify-between items-center text-[10px] sm:text-[13px] md:text-[13px] lg:text-[10px] xl:text-[11px] 2xl:text-[13px] font-semibold text-yellow-900">
                        <span>Qty</span>
                        <span className="font-bold">{summary.qty.toFixed(2)}</span>
                      </div>
                      <div className="flex flex-row justify-between items-center text-[10px] sm:text-[13px] md:text-[13px] lg:text-[10px] xl:text-[11px] 2xl:text-[13px] font-semibold text-yellow-900">
                        <span>Items</span>
                        <span className="font-bold">{summary.items}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2 (20%) — Discount / Adjust */}
                <div className="w-[20%] flex flex-col justify-center p-2 sm:p-4 md:p-4 lg:p-2 xl:p-3 2xl:p-4 bg-white border-r-2 border-gray-300 gap-1.5 sm:gap-2 md:gap-2 lg:gap-1.5 xl:gap-1.5 2xl:gap-2">
                  <div className="flex items-center gap-1.5 sm:gap-2 md:gap-2 lg:gap-1.5 xl:gap-1.5 2xl:gap-2">
                    <label className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700 w-5 sm:w-6 md:w-6 lg:w-5 xl:w-5 2xl:w-6">%</label>
                    <input
                      className="w-10 sm:w-12 md:w-12 lg:w-10 xl:w-11 2xl:w-12 h-6 sm:h-8 md:h-8 lg:h-6 xl:h-7 2xl:h-8 text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs border border-gray-300 rounded-md px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                      placeholder=""
                      type="text"
                      value={discountPercent}
                      onChange={e => handlePercentChange(e.target.value)}
                      disabled={isDiscountActive}
                    />
                    <label className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700">Discount</label>
                    <input
                      className="w-16 sm:w-20 md:w-20 lg:w-16 xl:w-18 2xl:w-20 h-6 sm:h-8 md:h-8 lg:h-6 xl:h-7 2xl:h-8 text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs border border-gray-300 rounded-md px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                      placeholder=""
                      type="text"
                      value={discountAmount}
                      onChange={e => handleAmountChange(e.target.value)}
                      disabled={isDiscountActive}
                    />
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2 md:gap-2 lg:gap-1.5 xl:gap-1.5 2xl:gap-2">
                    <label className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-medium text-gray-700 w-10 sm:w-12 md:w-12 lg:w-10 xl:w-11 2xl:w-12">Total</label>
                    <input
                      className="flex-1 h-6 sm:h-8 md:h-8 lg:h-6 xl:h-7 2xl:h-8 text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs border border-gray-300 rounded-md px-1.5 sm:px-2 md:px-2 lg:px-1.5 xl:px-1.5 2xl:px-2 bg-gray-50 focus:outline-none"
                      placeholder=""
                      type="text"
                      readOnly
                      value={roundedInvoiceTotal.toFixed(2)}
                    />
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2 md:gap-2 lg:gap-1.5 xl:gap-1.5 2xl:gap-2 mt-0.5 sm:mt-1 md:mt-1 lg:mt-0.5 xl:mt-0.5 2xl:mt-1">
                    <button
                      className="flex-1 h-6 sm:h-8 md:h-8 lg:h-6 xl:h-7 2xl:h-8 px-2 sm:px-3 md:px-3 lg:px-2 xl:px-2.5 2xl:px-3 rounded-md font-medium shadow bg-cyan-600 text-white hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs"
                      type="button"
                      onClick={handleAdjust}
                      disabled={isDiscountActive}
                    >
                      Adjust
                    </button>
                    <button
                      className="flex-1 h-6 sm:h-8 md:h-8 lg:h-6 xl:h-7 2xl:h-8 px-2 sm:px-3 md:px-3 lg:px-2 xl:px-2.5 2xl:px-3 rounded-md font-medium shadow bg-gray-200 text-cyan-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs"
                      type="button"
                      onClick={handleCancel}
                      disabled={!isDiscountActive}
                    >
                      Cancel
                    </button>
                  </div>
                </div>

                {/* Section 3 (35%) — Payment Summary + Payment Button */}
                <div className="w-[35%] flex flex-col items-center justify-center p-3 sm:p-4 md:p-4 lg:p-3 xl:p-3.5 2xl:p-4 bg-cyan-50/50 border-r-2 border-gray-300 gap-3">
                  <div className="flex flex-row items-start justify-between gap-3 sm:gap-6 md:gap-6 lg:gap-3 xl:gap-4 2xl:gap-6 w-full max-w-md">
                    <div className="flex flex-col items-center justify-start flex-1">
                      <label className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-semibold text-gray-700 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1.5 2xl:mb-2">Total</label>
                      <input
                        type="number"
                        className="w-20 sm:w-24 md:w-24 lg:w-20 xl:w-22 2xl:w-24 px-2 sm:px-3 md:px-3 lg:px-2 xl:px-2.5 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-cyan-400 text-xs sm:text-base md:text-base lg:text-xs xl:text-sm 2xl:text-base font-semibold bg-gray-50 text-center"
                        value={roundedInvoiceTotal.toFixed(2)}
                        readOnly
                      />
                    </div>
                    <div className="flex flex-col items-center justify-start flex-1">
                      <label className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-semibold text-gray-700 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1.5 2xl:mb-2">Payment</label>
                      <input
                        type="number"
                        className="w-20 sm:w-24 md:w-24 lg:w-20 xl:w-22 2xl:w-24 px-2 sm:px-3 md:px-3 lg:px-2 xl:px-2.5 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2 rounded-md border-2 border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-xs sm:text-base md:text-base lg:text-xs xl:text-sm 2xl:text-base font-semibold bg-gray-50 text-center cursor-not-allowed"
                        value={totalPaid.toFixed(2)}
                        readOnly
                        title="Click 'Create' or 'Create and Print' button to enter payment"
                      />
                    </div>
                    <div className="flex flex-col items-center justify-start flex-1">
                      <label className="text-[10px] sm:text-xs md:text-xs lg:text-[10px] xl:text-[10px] 2xl:text-xs font-semibold text-gray-700 mb-1 sm:mb-2 md:mb-2 lg:mb-1 xl:mb-1.5 2xl:mb-2">Balance</label>
                      <input
                        type="number"
                        className="w-20 sm:w-24 md:w-24 lg:w-20 xl:w-22 2xl:w-24 px-2 sm:px-3 md:px-3 lg:px-2 xl:px-2.5 2xl:px-3 py-1 sm:py-2 md:py-2 lg:py-1 xl:py-1.5 2xl:py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-cyan-400 text-xs sm:text-base md:text-base lg:text-xs xl:text-sm 2xl:text-base font-semibold bg-gray-50 text-center"
                        value={balanceDue.toFixed(2)}
                        readOnly
                      />
                    </div>
                  </div>
                  <button
                    className="self-center w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-2.5 rounded-lg shadow"
                    onClick={() => {
                      setShowPaymentModal(true);
                      setHasOpenedPaymentModal(true);
                    }}
                    disabled={invoiceTotal === 0}
                    title={!hasOpenedPaymentModal && invoiceTotal > 0 ? 'Please add payment details to enable Create Invoice' : undefined}
                  >
                    {!hasOpenedPaymentModal && invoiceTotal > 0 ? 'Payment — Add details to continue' : 'Payment'}
                  </button>
                </div>

                {/* Section 4 (25%) — Action Buttons */}
                <div className="w-[25%] flex flex-col justify-center p-3 sm:p-4 md:p-4 lg:p-3 xl:p-3.5 2xl:p-4 bg-gray-50/80 gap-3">
                  <Button
                    className={`w-full bg-blue-600 text-white rounded-lg py-2.5 h-11 sm:h-12 text-sm sm:text-base font-semibold shadow-md transition ${!hasOpenedPaymentModal || balanceDue < 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`}
                    onClick={handleCreateAndPrint}
                    disabled={!hasOpenedPaymentModal || balanceDue < 0}
                    title={!hasOpenedPaymentModal && invoiceTotal > 0 ? 'Please add payment details first' : undefined}
                  >
                    {isEditMode ? 'Update and Print' : 'Create and Print'}
                  </Button>
                  <div className="flex gap-3">
                    <Button
                      className={`flex-1 bg-green-600 text-white rounded-lg py-2.5 h-11 sm:h-12 text-sm sm:text-base font-semibold shadow-md transition ${!hasOpenedPaymentModal || balanceDue < 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-700'}`}
                      onClick={handleCreateInvoice}
                      disabled={!hasOpenedPaymentModal || balanceDue < 0}
                      title={!hasOpenedPaymentModal && invoiceTotal > 0 ? 'Please add payment details first' : undefined}
                    >
                      {isEditMode ? 'Update' : 'Create'}
                    </Button>
                    {!isEditMode && (
                      <Button
                        className="flex-1 bg-gray-600 text-white rounded-lg py-2.5 h-11 sm:h-12 text-sm sm:text-base font-semibold shadow-md hover:bg-gray-700 transition"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('Report button clicked, isEditMode:', isEditMode);
                          console.log('Navigating to /reports/invoice-report');
                          try {
                            navigate('/reports/invoice-report');
                          } catch (error) {
                            console.error('Navigation error:', error);
                          }
                        }}
                        style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                      >
                        Report
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {showLocationConfirm && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50 animate-fade-in">
                <div className="bg-white rounded-2xl shadow-2xl p-8 w-[420px] max-w-full flex flex-col items-center gap-6 relative">
                  <button
                    className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-red-200 text-gray-500 hover:text-red-600 text-xl font-bold focus:outline-none focus:ring-2 focus:ring-red-400 transition"
                    aria-label="Close"
                    onClick={() => {
                      setShowLocationConfirm(false);
                      setPendingLocation(null);
                      setCustomer(f => ({ ...f, Location: prevLocation }));
                    }}
                  >×</button>
                  <div className="font-semibold text-lg text-center text-gray-800">All selected items for sale are for a different location.<br />Do you want to delete all items and add fresh items to the sale list?</div>
                  <div className="flex flex-row gap-8 mt-2">
                    <button
                      className="h-10 px-8 rounded-lg font-semibold shadow bg-gray-200 text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition text-base"
                      onClick={() => {
                        setShowLocationConfirm(false);
                        setPendingLocation(null);
                        setCustomer(f => ({ ...f, Location: prevLocation }));
                      }}
                    >Cancel</button>
                    <button
                      className="h-10 px-8 rounded-lg font-semibold shadow bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-600 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition text-base"
                      onClick={() => {
                        setShowLocationConfirm(false);
                        setProducts([]);
                        setCustomer(f => ({ ...f, Location: pendingLocation || '' }));
                        setPrevLocation(pendingLocation || '');
                        setPendingLocation(null);
                      }}
                    >OK</button>
                  </div>
                </div>
              </div>
            )}
            {showCompanyConfirm && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50 animate-fade-in">
                <div className="bg-white rounded-2xl shadow-2xl p-8 w-[420px] max-w-full flex flex-col items-center gap-6 relative">
                  <button
                    className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-red-200 text-gray-500 hover:text-red-600 text-xl font-bold focus:outline-none focus:ring-2 focus:ring-red-400 transition"
                    aria-label="Close"
                    onClick={() => {
                      setShowCompanyConfirm(false);
                      setPendingCompany(null);
                      const prevState = getStateFromCompany(prevCompany);
                      setForm(f => ({
                        ...f,
                        SelectCompany: prevCompany,
                        State: prevState // Restore state from previous company
                      }));
                    }}
                  >×</button>
                  <img src="/company-logo.svg" alt="Company Change" className="w-20 h-20 rounded-full shadow mb-2 border border-gray-200 bg-white object-contain" />
                  <div className="font-semibold text-lg text-center text-gray-800">Changing the company will delete all line items from the sale list.<br />Do you want to continue?</div>
                  <div className="flex flex-row gap-8 mt-2">
                    <button
                      className="h-10 px-8 rounded-lg font-semibold shadow bg-gray-200 text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition text-base"
                      onClick={() => {
                        setShowCompanyConfirm(false);
                        setPendingCompany(null);
                        const prevState = getStateFromCompany(prevCompany);
                        setForm(f => ({
                          ...f,
                          SelectCompany: prevCompany,
                          State: prevState // Restore state from previous company
                        }));
                      }}
                    >Cancel</button>
                    <button
                      className="h-10 px-8 rounded-lg font-semibold shadow text-white transition text-base"
                      style={{ backgroundColor: '#004526' }}
                      onClick={() => {
                        setShowCompanyConfirm(false);
                        setProducts([]);
                        // Clear payment values when line items are removed so Payment/Balance show 0
                        setPayments(prev => ({
                          ...prev,
                          cashPayment: '',
                          cardPayment: '',
                          walletAmount: '',
                          chequeAmount: '',
                          bankTransAmount: '',
                          couponAmount: '0',
                          couponNo: '',
                          couponAmountLocked: false,
                          creditNoteAmount: '0',
                          creditNoteNo: '',
                          pointsRedeem: '',
                        }));
                        const newState = getStateFromCompany(pendingCompany || '');
                        setForm(f => ({
                          ...f,
                          SelectCompany: pendingCompany || '',
                          State: newState // Set state from the new company
                        }));
                        setPrevCompany(pendingCompany || '');
                        setPendingCompany(null);
                      }}
                    >OK</button>
                  </div>
                </div>
              </div>
            )}
            {showQtyModal && qtyModalProduct && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50 animate-fade-in">
                <div className="bg-white rounded-2xl shadow-2xl p-8 w-[350px] max-w-full flex flex-col items-center gap-4 relative transition-all duration-200">
                  <button
                    className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-red-200 text-gray-500 hover:text-red-600 text-xl font-bold focus:outline-none focus:ring-2 focus:ring-red-400 transition"
                    aria-label="Close"
                    onClick={() => setShowQtyModal(false)}
                  >×</button>
                  <div className="font-extrabold text-2xl text-center tracking-wide text-gray-800 mb-1">{qtyModalProduct.Title}</div>
                  <div className="text-center text-sm mb-1 text-amber-600 font-semibold">
                    {qtyModalProduct.isTraceable ? 'Trackable product' : 'Non-trackable product'}
                  </div>
                  {qtyModalProduct.isTraceable && (
                    <div className="text-center text-base mb-2 text-gray-700 font-medium">Max Qty Available : <span className="font-bold text-cyan-700">{Number(qtyModalProduct.Available).toFixed(3)}</span> {qtyModalProduct.UQCName || ''}</div>
                  )}
                  <input
                    type="number"
                    min="1"
                    max={qtyModalProduct.Available}
                    className="text-4xl font-extrabold text-center text-white bg-blue-500 rounded-lg w-36 h-16 mb-2 focus:outline-none focus:ring-4 focus:ring-cyan-400 shadow-inner transition-all duration-150"
                    value={qtyModalQty}
                    onChange={e => {
                      setQtyModalQty(e.target.value);
                      setQtyModalError('');
                    }}
                    autoFocus
                  />
                  <div className="flex flex-row gap-6 mb-2 w-full justify-center">
                    <div className="flex flex-col items-center">
                      <span className="text-base mb-1 text-gray-700 font-semibold">Price incl GST</span>
                      <input
                        className="w-24 text-center border border-gray-300 rounded bg-gray-100 font-semibold text-gray-700"
                        value={qtyModalMRP}
                        onChange={e => {
                          const newMRP = e.target.value;
                          setQtyModalMRP(newMRP);
                          const gstPercent = parseFloat(qtyModalProduct.GST) || 0;
                          const cessPercent = parseFloat(qtyModalProduct.CessPer) || 0;
                          const totalTax = gstPercent + cessPercent;
                          const mrp = parseFloat(newMRP) || 0;
                          const unitPrice = totalTax > 0 ? mrp / (1 + totalTax / 100) : mrp;
                          setQtyModalUnitPrice(unitPrice.toFixed(2));
                        }}
                      />
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-base mb-1 text-gray-700 font-semibold">Price excl GST</span>
                      <input
                        className="w-24 text-center border border-gray-300 rounded bg-gray-100 font-semibold text-gray-700"
                        value={qtyModalUnitPrice}
                        onChange={e => {
                          const newUnitPrice = e.target.value;
                          setQtyModalUnitPrice(newUnitPrice);
                          const gstPercent = parseFloat(qtyModalProduct.GST) || 0;
                          const cessPercent = parseFloat(qtyModalProduct.CessPer) || 0;
                          const totalTax = gstPercent + cessPercent;
                          const unitPrice = parseFloat(newUnitPrice) || 0;
                          const mrp = unitPrice * (1 + totalTax / 100);
                          setQtyModalMRP(mrp.toFixed(2));
                        }}
                      />
                    </div>
                  </div>
                  <button
                    className="h-12 w-40 mt-2 rounded-lg font-semibold shadow bg-cyan-200 text-black hover:bg-cyan-300 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition text-lg"
                    onClick={() => {
                      const enteredQty = Number(qtyModalQty);
                      const maxQty = Number(qtyModalProduct.Available);
                      const isTraceable = qtyModalProduct.isTraceable || false;

                      // Only check quantity validation for traceable products
                      if (isTraceable && enteredQty > maxQty) {
                        setQtyModalError('You cannot sell more than the available quantity');
                        return;
                      }

                      setShowQtyModal(false);
                      addProductToTable({
                        ...qtyModalProduct,
                        Qty: enteredQty,
                        DelQty: enteredQty,
                        MRP: qtyModalMRP,
                        // Keep CostPrice from product (API costprice); do not overwrite with unit price
                        _fromPopup: true,
                      });
                      setProductAddMessage('Product added.');
                      setProductAddMessageType('success');
                      setQtyModalError('');
                    }}
                  >Add</button>
                  {qtyModalError && (
                    <div className="text-red-600 text-sm font-semibold mt-1">{qtyModalError}</div>
                  )}
                </div>
              </div>
            )}
            {showPartnerConfirm && (
              <div style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 1000,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Segoe UI, Arial, sans-serif'
              }}>
                <div style={{
                  background: 'white',
                  borderRadius: 12,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                  minWidth: 380,
                  maxWidth: '90vw',
                  minHeight: 160,
                  padding: 0,
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  overflow: 'hidden',
                  border: '1.5px solid #e0e0e0'
                }}>
                  {/* Title Bar */}
                  <div style={{
                    width: '100%',
                    background: 'linear-gradient(90deg, #e0f7fa 0%, #b2ebf2 100%)',
                    padding: '16px 0 10px 0',
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: 20,
                    letterSpacing: 0.5,
                    color: '#007c91',
                    position: 'relative',
                    borderBottom: '1px solid #cfd8dc'
                  }}>
                    Confirmation
                    <button
                      style={{
                        position: 'absolute',
                        right: 16,
                        top: 10,
                        background: '#e57373',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        width: 28,
                        height: 28,
                        fontWeight: 'bold',
                        fontSize: 18,
                        cursor: 'pointer',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
                      }}
                      aria-label="Close"
                      onClick={() => {
                        setShowPartnerConfirm(false);
                        setPendingPartner(null);
                        setSelectedPartner(prevPartner);
                        setCustomer(f => ({ ...f, Partner: prevPartner }));
                      }}
                    >×</button>
                  </div>
                  {/* Message */}
                  <div style={{
                    fontWeight: 500,
                    fontSize: 18,
                    margin: '32px 24px 0 24px',
                    textAlign: 'center',
                    color: '#263238',
                    lineHeight: 1.4
                  }}>
                    Are you sure you want to change the rates?<br />
                    <span style={{ color: '#d84315', fontWeight: 600 }}>Existing items will be removed.</span>
                  </div>
                  {/* Buttons */}
                  <div style={{ display: 'flex', flexDirection: 'row', gap: 32, margin: '36px 0 24px 0', justifyContent: 'center' }}>
                    <button
                      style={{
                        background: 'linear-gradient(90deg, #b2ebf2 0%, #4dd0e1 100%)',
                        color: '#01579b',
                        border: 'none',
                        borderRadius: 8,
                        width: 120,
                        height: 48,
                        fontWeight: 600,
                        fontSize: 18,
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                        transition: 'background 0.2s, color 0.2s',
                        outline: 'none',
                      }}
                      onMouseOver={e => e.currentTarget.style.background = '#e0f7fa'}
                      onMouseOut={e => e.currentTarget.style.background = 'linear-gradient(90deg, #b2ebf2 0%, #4dd0e1 100%)'}
                      onClick={() => {
                        setShowPartnerConfirm(false);
                        setPendingPartner(null);
                        setSelectedPartner(prevPartner);
                        setCustomer(f => ({ ...f, Partner: prevPartner }));
                      }}
                    >Close</button>
                    <button
                      style={{
                        background: 'linear-gradient(90deg, #b2ebf2 0%, #4dd0e1 100%)',
                        color: '#01579b',
                        border: 'none',
                        borderRadius: 8,
                        width: 120,
                        height: 48,
                        fontWeight: 600,
                        fontSize: 18,
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                        transition: 'background 0.2s, color 0.2s',
                        outline: 'none',
                      }}
                      onMouseOver={e => e.currentTarget.style.background = '#e0f7fa'}
                      onMouseOut={e => e.currentTarget.style.background = 'linear-gradient(90deg, #b2ebf2 0%, #4dd0e1 100%)'}
                      onClick={() => {
                        setShowPartnerConfirm(false);
                        setProducts([]);
                        setSelectedPartner(pendingPartner || '');
                        setCustomer(f => ({ ...f, Partner: pendingPartner || '' }));
                        setPrevPartner(pendingPartner || '');
                        setPendingPartner(null);
                      }}
                    >OK</button>
                  </div>
                </div>
              </div>
            )}
            {/* Ship To Different Address Popup */}
            {showShipToPopup && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
                <div className="bg-white rounded-xl shadow-lg p-6 min-w-[340px] max-w-[95vw] w-full sm:w-[420px] flex flex-col gap-4 relative">
                  <div className="mb-2 font-bold text-lg text-center">Shipping Address</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                      <input className={`w-full h-8 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400 transition ${isUnknown ? 'bg-gray-100' : 'bg-white'}`} value={shipToForm.Name} onChange={e => setShipToForm(f => ({ ...f, Name: e.target.value }))} readOnly={isUnknown} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Company</label>
                      <input className={`w-full h-8 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400 transition ${isUnknown ? 'bg-gray-100' : 'bg-white'}`} value={shipToForm.Company} onChange={e => setShipToForm(f => ({ ...f, Company: e.target.value }))} readOnly={isUnknown} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                      <input className={`w-full h-8 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400 transition ${isUnknown ? 'bg-gray-100' : 'bg-white'}`} value={shipToForm.Phone} onChange={e => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                        setShipToForm(f => ({ ...f, Phone: value }));
                      }} placeholder="Phone" maxLength={10} readOnly={isUnknown} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                      <input className={`w-full h-8 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400 transition ${isUnknown ? 'bg-gray-100' : 'bg-white'}`} value={shipToForm.Email} onChange={e => setShipToForm(f => ({ ...f, Email: e.target.value }))} readOnly={isUnknown} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">GST Number</label>
                      <input
                        className={cn('w-full h-8 px-2 py-1 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400 transition', isUnknown ? 'bg-gray-100 border-gray-300' : 'bg-white', shipToGstError ? 'border-red-500' : 'border-gray-300')}
                        value={shipToForm.GST}
                        onChange={e => {
                          const { value, hadInvalidInput } = sanitizeGstInput(e.target.value);
                          setShipToForm(f => ({ ...f, GST: value }));
                          setShipToGstError(hadInvalidInput);
                        }}
                        onBlur={() => setShipToGstError(false)}
                        maxLength={15}
                        readOnly={isUnknown}
                      />
                      {shipToGstError && <p className="text-red-500 text-xs mt-0.5">{GST_VALIDATION_MESSAGE}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                      <input className={`w-full h-8 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400 transition ${isUnknown ? 'bg-gray-100' : 'bg-white'}`} value={shipToForm.Address} onChange={e => setShipToForm(f => ({ ...f, Address: e.target.value }))} readOnly={isUnknown} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">State</label>
                      <select className={`w-full h-8 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-400 transition ${isUnknown ? 'bg-gray-100' : 'bg-white'}`} value={shipToForm.State} onChange={e => setShipToForm(f => ({ ...f, State: e.target.value }))} disabled={isUnknown}>
                        <option value="">Select</option>
                        {states && states.map((state: any) => (
                          <option key={state.Code} value={state.State}>{state.State}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex flex-row gap-4 justify-end mt-4">
                    <button className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-6 rounded-lg shadow focus:outline-none" onClick={() => { setShowShipToPopup(false); setShipToSame(true); }}>Close</button>
                    <button className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-2 px-6 rounded-lg shadow focus:outline-none" onClick={() => {
                      setShowShipToPopup(false);
                      setShipToSame(false);
                      setForm(f => ({ ...f, ...shipToForm }));
                    }}>Update</button>
                  </div>
                </div>
              </div>
            )}
            {showNotesPopup && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
                <div className="bg-white rounded-2xl shadow-2xl p-6 w-[350px] max-w-full flex flex-col items-center gap-4 relative animate-fade-in">
                  <button
                    className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-xl font-bold focus:outline-none"
                    aria-label="Close"
                    onClick={() => { setShowNotesPopup(false); setNotesText(''); }}
                  >×</button>
                  <div className="font-bold text-lg text-center mb-2">Notes</div>
                  <textarea
                    className="w-full h-48 p-2 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-cyan-400 text-base bg-gray-50"
                    value={notesText}
                    onChange={e => setNotesText(e.target.value)}
                    placeholder="Enter your notes here..."
                    autoFocus
                  />
                  <div className="flex flex-row gap-4 w-full justify-between mt-2">
                    <button
                      className="bg-cyan-200 hover:bg-cyan-300 text-black font-semibold py-2 px-8 rounded-lg shadow focus:outline-none text-base"
                      onClick={() => { setShowNotesPopup(false); setNotes(notesText); }}
                    >Save</button>
                    <button
                      className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-8 rounded-lg shadow focus:outline-none text-base"
                      onClick={() => { setShowNotesPopup(false); setNotesText(''); }}
                    >Close</button>
                  </div>
                </div>
              </div>
            )}
            {showStateChangeConfirm && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50 animate-fade-in">
                <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md flex flex-col gap-6 relative">
                  <button
                    className="absolute top-3 right-3 text-gray-400 hover:text-red-500 text-2xl font-bold focus:outline-none"
                    aria-label="Close"
                    onClick={() => {
                      setShowStateChangeConfirm(false);
                      setPendingState(null);
                    }}
                  >×</button>
                  <div className="font-bold text-xl text-center mb-2 text-gray-800">All the line items will be deleted due to change in state</div>
                  <div className="flex justify-center gap-8 mt-4">
                    <button
                      className="px-8 py-3 rounded-lg bg-cyan-200 hover:bg-cyan-300 text-gray-800 font-semibold text-lg shadow"
                      onClick={() => {
                        setShowStateChangeConfirm(false);
                        setPendingState(null);
                      }}
                    >Close</button>
                    <button
                      className="px-8 py-3 rounded-lg bg-cyan-400 hover:bg-cyan-500 text-white font-bold text-lg shadow"
                      onClick={() => {
                        setShowStateChangeConfirm(false);
                        setProducts([]);
                        setCustomer(f => ({ ...f, State: pendingState || '' }));
                        setPrevCustomerState(pendingState || '');
                        setPendingState(null);
                      }}
                    >OK</button>
                  </div>
                </div>
              </div>
            )}

            {/* Print Options Modal for SalesInvoice - Only on Android */}
            {Capacitor.isNativePlatform() && (
              <PrintOptionsModal
                open={showPrintOptions}
                onClose={() => setShowPrintOptions(false)}
                onSelect={handlePrintOptionSelect}
                onDownload={downloadPdf}
                showPrintFormatSelection={true} // Enable print format selection for SalesInvoice flows on Android
              />
            )}

            {/* Web Print Options Modal - Simple version without format selection */}
            {!Capacitor.isNativePlatform() && showPrintOptions && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40" style={{ zIndex: 9999 }}>
                <div className="bg-white rounded-lg p-6 min-w-[300px]">
                  <h2 className="text-lg font-semibold mb-4">Select Print Option</h2>
                  <div className="mb-4">
                    <label className="block mb-1 font-medium">Printer Type</label>
                    <select value={printMainOption} onChange={e => setPrintMainOption(e.target.value)} className="w-full border rounded p-2">
                      <option value="Kohinoor">Kohinoor</option>
                      <option value="Multiple Page Modem">Multiple Page Modem</option>
                    </select>
                  </div>
                  {printMainOption === 'Kohinoor' && (
                    <div className="mb-4">
                      <label className="block mb-1 font-medium">Paper Width</label>
                      <select value={kohinoorSubOption} onChange={e => setKohinoorSubOption(e.target.value)} className="w-full border rounded p-2">
                        <option value="2mm">2 inch</option>
                        <option value="3mm">3 inch</option>
                      </select>
                    </div>
                  )}
                  {printMainOption === 'Multiple Page Modem' && (
                    <div className="mb-4 text-gray-500">(Placeholder for Multiple Page Modem layout)</div>
                  )}
                  {enableDesktopDirectPrint && (
                    <div className="mb-4">
                      <label className="block mb-1 font-medium">Printer Driver</label>
                      {isPrinterDriverLoading ? (
                        <div className="text-sm text-gray-500">Detecting default printer...</div>
                      ) : (
                        <input
                          type="text"
                          className="w-full border rounded p-2"
                          value={selectedPrinterDriver}
                          onChange={e => setSelectedPrinterDriver(e.target.value)}
                          placeholder="Enter printer driver name"
                          disabled={Boolean(printerDriverError && !defaultPrinterDriver)}
                        />
                      )}
                      {defaultPrinterDriver && (
                        <p className="text-xs text-gray-500 mt-1">Default detected: {defaultPrinterDriver}</p>
                      )}
                      {printerDriverError && (
                        <p className="text-xs text-red-600 mt-1">{printerDriverError}</p>
                      )}
                    </div>
                  )}
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setShowPrintOptions(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={downloadPdf}
                      className="px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded hover:bg-gray-50"
                    >
                      Download
                    </button>
                    <button
                      onClick={printPdf}
                      className={webPrintButtonClass}
                      disabled={webPrintDisabled}
                    >
                      {webPrintButtonLabel}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Product Entry Form Modal */}
            <Dialog open={showProductEntryModal} onOpenChange={setShowProductEntryModal}>
              <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Product</DialogTitle>
                  <DialogDescription>
                    Fill in the details to add a new product to your inventory.
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <ProductEntryForm
                    token={localStorage.getItem('token') || ''}
                    showFooter={false}
                    onSuccess={(productData) => {
                      setShowProductEntryModal(false);
                      // Show success message
                      setProductAddMessage('Product added successfully!');
                      setProductAddMessageType('success');

                      // Add the newly created product to the invoice
                      if (productData) {
                        addProductToTable(productData);
                      }

                      // Refresh products list
                      if (queryClient) {
                        queryClient.invalidateQueries({ queryKey: ['products-search'] });
                      }
                    }}
                    onCancel={() => setShowProductEntryModal(false)}
                    ref={productFormRef}
                  />
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowProductEntryModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    form="product-form"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => productFormRef.current?.submit()}
                  >
                    Add Product
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Serial Selection Popup */}
            {serialPopupProduct && (
              <SerialSelectionPopup
                isOpen={showSerialSelectionPopup}
                onClose={() => {
                  setShowSerialSelectionPopup(false);
                  setSerialPopupProduct(null);
                }}
                onAddToList={handleSerialSelection}
                serialDetails={serialPopupProduct.SerialDetails || []}
                productName={serialPopupProduct.Title || ''}
                barcode={serialPopupProduct.Barcode || ''}
                excludedSerialNumbers={addedSerialNumbers}
              />
            )}

            {/* Cost Price Validation Dialog */}
            <Dialog open={showCostPriceValidation} onOpenChange={setShowCostPriceValidation}>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Cost Price Validation</DialogTitle>
                  <DialogDescription>
                    {costPriceValidationData && (
                      <>
                        The cost price you entered (<strong>₹{parseFloat(costPriceValidationData.enteredValue).toFixed(2)}</strong>) is less than the original cost price (<strong>₹{parseFloat(costPriceValidationData.originalValue).toFixed(2)}</strong>).
                      </>
                    )}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2">
                  <Button
                    variant="outline"
                    onClick={handleCostPriceCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCostPriceConfirm}
                  >
                    Confirm
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Unit Price Validation Dialog */}
            <Dialog open={showUnitPriceValidation} onOpenChange={setShowUnitPriceValidation}>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Unit Price Validation</DialogTitle>
                  <DialogDescription>
                    {unitPriceValidationData && (
                      <>
                        You entered Unit Price (<strong>₹{parseFloat(unitPriceValidationData.enteredValue).toFixed(2)}</strong>) is less than the original unit price (<strong>₹{parseFloat(unitPriceValidationData.originalValue).toFixed(2)}</strong>).
                      </>
                    )}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2">
                  <Button
                    variant="outline"
                    onClick={handleUnitPriceCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleUnitPriceConfirm}
                  >
                    Confirm
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

          </main>
        </div>
      )}
    </>
  );
}

export default SalesInvoice; 
